/**
 * The per-member journey view behind `/consumers/:id`.
 *
 * Most of what these tabs read does not exist for most members, so every panel
 * has to say which of the two it is looking at: a member with nothing
 * recorded, or a read that failed. An absence gets a `Callout` naming exactly
 * what is missing; a failure gets the shared `FailureText` and a retry.
 * Nothing here invents a placeholder row, and no empty state quotes a
 * fleet-wide count - those rot the moment the product is used.
 *
 * Empty states describe only what the corresponding read returned. They do
 * not infer why a member has no rows or invent evidence that is not present.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  archiveMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdArchivePost,
  bookmarkMemberOutfitApiV1InternalUsersIdOutfitsBookmarkPost,
  completeMemberOnboardingApiV1InternalUsersIdOnboardingCompletePost,
  completeMediaUploadApiV1MediaUploadsIdCompletePost,
  createMediaUploadApiV1MediaUploadsPost,
  createMemberGarmentApiV1InternalWardrobeMembersMemberIdGarmentsPost,
  createMemberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersPost,
  createWardrobeBulkImportApiV1InternalWardrobeBulkImportsPost,
  createMemberOutfitApiV1InternalUsersIdOutfitsPost,
  editMemberGarmentTagsApiV1InternalUsersIdWardrobeGarmentsGarmentIdTagsPost,
  editMemberOutfitTagsApiV1InternalUsersIdOutfitsOutfitIdTagsPatch,
  enhanceMemberAvatarApiV1InternalUsersIdAvatarEnhancePost,
  memberChatApiV1InternalUsersIdChatGet,
  memberDailyLooksApiV1InternalUsersIdDailyLooksGet,
  memberGarmentPairingsApiV1InternalUsersIdWardrobeGarmentsGarmentIdPairingsGet,
  memberOnboardingApiV1InternalUsersIdOnboardingGet,
  memberSearchesApiV1InternalUsersIdSearchesGet,
  memberOutfitApiV1InternalUsersIdOutfitsOutfitIdGet,
  memberOutfitsApiV1InternalUsersIdOutfitsGet,
  memberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersFolderIdGet,
  memberWardrobeFoldersApiV1InternalUsersIdWardrobeFoldersGet,
  failedGarmentsApiV1InternalGarmentsFailedGet,
  getWardrobeBulkImportApiV1InternalWardrobeBulkImportsImportIdGet,
  listWardrobeBulkImportsApiV1InternalWardrobeBulkImportsGet,
  memberWardrobeApiV1InternalUsersIdWardrobeGet,
  memberWardrobeSummaryApiV1InternalUsersIdWardrobeSummaryGet,
  mutateMemberWardrobeFolderOutfitsApiV1InternalUsersIdWardrobeFoldersFolderIdOutfitsPost,
  type AdminGarmentPairing,
  type AdminGarmentPairingPage,
  type AdminMemberGarment,
  type AdminMemberGarmentPage,
  type AdminMemberChatPage,
  type AdminMemberChatTurn,
  type AdminChatReferencedGarment,
  type AdminChatToolCall,
  type AdminMemberDailyLook,
  type AdminMemberDailyLookGarment,
  type AdminMemberDailyLookPage,
  type AdminMemberWardrobeFolderDetail,
  type AdminMemberWardrobeFolderPage,
  type AdminFailedGarment,
  type AdminFailedGarmentPage,
  type AdminMemberOnboarding,
  type ConsumerDetail,
  type AdminMemberSearchPage,
  type AdminMemberOnboardingMedia,
  type AdminMemberOutfit,
  type AdminMemberOutfitPage,
  type AdminMemberWardrobeSummary,
  type AdminOnBehalfAvatarOperation,
  type AdminOnBehalfAvatarResult,
  type AdminOnBehalfActionResult,
  type AdminOnBehalfReprocessResult,
  type AdminOnBehalfBodyShape,
  type AdminOnBehalfJobAccepted,
  type AdminWardrobeFacet,
  type OperatorGarmentQueued,
  type WardrobeBulkImportCreated,
  type WardrobeBulkImportStatus,
  type WardrobeBulkImportSummary,
  recomputeMemberBodyShapeApiV1InternalUsersIdBodyShapeRecomputePost,
  removeMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdDelete,
  reprocessMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdReprocessPost,
  requeueMemberGarmentIngestionApiV1InternalUsersIdWardrobeGarmentsGarmentIdRequeueIngestionPost,
  markMemberOutfitWornApiV1InternalUsersIdOutfitsWearPost,
  promoteMemberOutfitApiV1InternalUsersIdOutfitsOutfitIdPromotePost,
  putMemberWardrobeFolderGarmentApiV1InternalUsersIdWardrobeFoldersFolderIdGarmentsGarmentIdPut,
  regenerateMemberOutfitApiV1InternalUsersIdOutfitsRegeneratePost,
  renameMemberOutfitApiV1InternalUsersIdOutfitsOutfitIdTitlePatch,
  setMemberOutfitGarmentsApiV1InternalUsersIdOutfitsOutfitIdGarmentsPost,
  deleteMemberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersFolderIdDelete,
  removeMemberWardrobeFolderGarmentApiV1InternalUsersIdWardrobeFoldersFolderIdGarmentsGarmentIdDelete,
  restoreMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdRestorePost,
  resetMemberOnboardingApiV1InternalUsersIdOnboardingResetPost,
  updateMemberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersFolderIdPatch,
  undoMemberAvatarImprovementApiV1InternalUsersIdAvatarUndoPost,
  uploadMemberTwinPhoto,
  type AdminOnBehalfBulkResult,
  type AdminOnBehalfIngestionRequeued,
  type AdminOnBehalfOnboardingResult,
  bulkMemberGarmentsApiV1InternalUsersIdWardrobeGarmentsBulkPost,
  type WardrobeFolderGarmentItem,
  type WardrobeFolderOutfit,
  type WardrobeFolderSummary,
} from '@closet-theory/api';
import { Check, Image01, Pencil01, XClose } from '@untitledui/icons';
import { Badge } from '@ui/components/base/badges/badges';
import { Button } from '@ui/components/base/buttons/button';
import { LoadingIndicator } from '@ui/components/application/loading-indicator/loading-indicator';
import { Tabs } from '@ui/components/application/tabs/tabs';
import { Callout, FailureText, Section, StatRow, StatTile } from '@ui/console/primitives';
import { IdentifierValue, Quantity, formatCount, formatPercent } from '@ui/console/measures';
import { CopyableId, EntityRef, IdentifierCopy, looksLikeUuid, refHref } from './references';
import { Term } from '@ui/console/glossary';
import { ActionDialog } from '@ui/console/PageShell';
import { RecordTable } from './record-table';
import { OutfitSpread, type SpreadGarment } from './outfit-spread';
import {
  ManifestField,
  MemberFailure,
  MemberSelectionOutcome,
  CategoryFromFolderNamesField,
  SubfolderFoldersField,
  buildSelection,
  rejectionDetail,
} from './drive-import-selection';
import { SelectField, TextAreaField, TextField } from '@ui/console/forms';
import { cx } from '@ui/utils/cx';
import { configureAdminApi, handleUnauthorized } from './auth';
import { GarmentImage, SignatureExpiryBoundary } from './garment-image';
import { OnboardingAnswersPanel } from './onboarding-answers';
import { Actions, DetailList, Filters, Page, Pager, Status } from './components';
import { errorDetail } from './operation-policy';
import { MakeGarmentSet, MemberGarmentSets } from './member-garment-sets';
import { SearchHistoryFilters, SearchHistoryList } from './search-history-list';
import { MemberWorkflowRunsPanel } from './workflow-jobs';
import {
  BulkOutfitDeletion,
  DeleteOutfitAction,
  outfitDeletionOperations,
} from './outfit-deletion';
import {
  BulkGarmentDeletion,
  DeleteGarmentAction,
  garmentDeletionOperations,
} from './garment-deletion';

/**
 * The onboarding destination keeps the stable `onboarding` id, while its label
 * names the member-owned progress the route reads.
 */
export const INSIGHT_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'wardrobe', label: 'Wardrobe' },
  { id: 'drive-import', label: 'Drive import' },
  { id: 'outfits', label: 'Outfits' },
  { id: 'folders', label: 'Folders' },
  { id: 'chat', label: 'Chat' },
  { id: 'searches', label: 'Searches' },
  { id: 'daily-looks', label: 'Daily looks' },
  { id: 'workflow-runs', label: 'Workflow runs' },
] as const;

export type InsightTabId = (typeof INSIGHT_TABS)[number]['id'];

type WorkerJobState = {
  job_id: string;
  status: string;
  error_code: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
};

export type GarmentWithProducingJob = AdminMemberGarment & {
  producing_job_id?: string | null;
};

type OutfitWithRenderJobs = AdminMemberOutfit & {
  render_job?: WorkerJobState | null;
  title_job?: WorkerJobState | null;
  regeneration_job?: WorkerJobState | null;
  producing_job_id?: string | null;
  render_image_url?: string | null;
};

type DailyLookWithRenderJob = AdminMemberDailyLook & {
  producing_job_id?: string | null;
  render_job?: WorkerJobState | null;
};

const ACTIVE_JOB_STATUSES = new Set(['queued', 'running', 'retry_wait']);

function activeJob(job: WorkerJobState | null | undefined) {
  return job && ACTIVE_JOB_STATUSES.has(job.status) ? job : undefined;
}

function outfitRenderJobs(outfit: OutfitWithRenderJobs) {
  return [outfit.render_job, outfit.regeneration_job].filter((job): job is WorkerJobState =>
    Boolean(job),
  );
}

function activeOutfitJob(outfit: OutfitWithRenderJobs) {
  const regeneration = activeJob(outfit.regeneration_job);
  if (regeneration) return { job: regeneration, regenerating: true };
  const render = activeJob(outfit.render_job);
  return render ? { job: render, regenerating: false } : undefined;
}

function latestOutfitJob(outfit: OutfitWithRenderJobs) {
  return outfitRenderJobs(outfit).sort((left, right) =>
    (right.created_at ?? '').localeCompare(left.created_at ?? ''),
  )[0];
}

function outfitJobSignature(outfit: OutfitWithRenderJobs) {
  return outfitRenderJobs(outfit)
    .map((job) => `${job.job_id}:${job.status}`)
    .join('|');
}

function useRenderAcceptance(outfit: OutfitWithRenderJobs | undefined) {
  const [pending, setPending] = useState(false);
  const acceptedJobSignature = useRef('');
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clear = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = undefined;
    setPending(false);
  }, []);
  const accept = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current);
    acceptedJobSignature.current = outfit ? outfitJobSignature(outfit) : '';
    setPending(true);
    timeout.current = setTimeout(() => {
      timeout.current = undefined;
      setPending(false);
    }, 60_000);
  }, [outfit]);

  useEffect(() => {
    if (!outfit || !pending) return;
    const signature = outfitJobSignature(outfit);
    if (outfitRenderJobs(outfit).length && signature !== acceptedJobSignature.current) clear();
  }, [clear, outfit, pending]);
  useEffect(
    () => () => {
      if (timeout.current) clearTimeout(timeout.current);
    },
    [],
  );

  return { pending, accept };
}

function WorkflowJobButton({ jobId, source = false }: { jobId: string; source?: boolean }) {
  return (
    <Button size="sm" color="secondary" href={`/workflow-jobs/${jobId}`}>
      {source ? 'View source workflow' : 'View workflow job'}
    </Button>
  );
}

function ActiveJobState({
  job,
  regenerating = false,
}: {
  job: WorkerJobState;
  regenerating?: boolean;
}) {
  const label = regenerating
    ? 'Regenerating…'
    : job.status === 'queued'
      ? 'Queued'
      : job.status === 'retry_wait'
        ? 'Retrying soon'
        : 'Rendering…';
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-live="polite">
      <Status value={job.status} />
      <span className="text-xs text-tertiary">{label}</span>
      <WorkflowJobButton jobId={job.job_id} />
    </div>
  );
}

/**
 * Tab ids people have bookmarked, and the tab that answers what they were
 * after. `recommendations` read `recommendation_runs`, written by the
 * abandoned half of two parallel three-a-day implementations; the batches a
 * member actually receives are the ones this tab shows now.
 */
const RENAMED_TABS: Record<string, InsightTabId> = {
  recommendations: 'daily-looks',
  stylist: 'chat',
};

export function resolveInsightTab(value: string | null): InsightTabId {
  const found = INSIGHT_TABS.find((tab) => tab.id === value)?.id;
  if (found) return found;
  return (value === null ? undefined : RENAMED_TABS[value]) ?? 'overview';
}

const PAGE = 25;
// Smart search has no entry point in the member app, so admin history is Wardrobe-only.
const SEARCH_SURFACE = 'wardrobe' as const;

export function when(value: string | null | undefined, fallback = 'Not recorded') {
  return value ? new Date(value).toLocaleString() : fallback;
}

export function words(value: string) {
  return value.replace(/[_-]+/g, ' ');
}

/**
 * One attribute value, as prose.
 *
 * The facet tallies come back as the raw text of the stored attribute, and the
 * ingestion pipeline writes the multi-valued ones (season, occasion) as JSON
 * arrays. Postgres' `->>` hands those back as a JSON literal, so an untouched
 * season bucket reads `["Spring", "Summer"]` on screen. Print the members
 * instead - and only when the text really parses as an array of scalars, so a
 * value that merely starts with a bracket survives intact.
 */
export function facetValue(value: string) {
  const text = value.trim();
  if (text.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (Array.isArray(parsed)) {
        const members = parsed
          .filter((item) => item !== null && typeof item !== 'object')
          .map((item) => words(String(item)).trim())
          .filter(Boolean);
        if (members.length) return members.join(', ');
      }
    } catch {
      // Not JSON after all; the raw text is the best reading of it.
    }
  }
  return words(text);
}

/* ------------------------------------------------------------------ loading */

export type Insight<T> =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'failed'; detail: string }
  | { status: 'ready'; data: T; receivedAt: number };

export type Read<T> = { data?: T | undefined; error?: unknown; response: { status: number } };

/** One cadence for every live read in the member console. */
export const LIVE_REFRESH_INTERVAL_MS = 12_000;

function interactivePanelOpen(): boolean {
  return Boolean(
    document.querySelector(
      '[role="dialog"], [data-live-refresh-pause="true"], [aria-haspopup="listbox"][aria-expanded="true"]',
    ),
  );
}

/**
 * Refreshes a mounted read without replacing its current data with a loader or
 * a transient polling failure. Hidden tabs do no work and catch up as soon as
 * they become visible. Dialogs and pickers pause every read on the page so a
 * response cannot move the record underneath an operator's in-progress edit.
 * Failed reads back off and eventually stop until the operator or tab resumes them.
 */
export function useLiveRefresh(refresh: () => void | Promise<void>, paused = false) {
  const refreshing = useRef(false);
  const failures = useRef(0);
  const skippedTicks = useRef(0);
  const generation = useRef(0);
  const refreshRef = useRef(refresh);
  const reset = useCallback(() => {
    failures.current = 0;
    skippedTicks.current = 0;
    generation.current += 1;
  }, []);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);
  useEffect(() => {
    const run = () => {
      if (
        paused ||
        refreshing.current ||
        document.visibilityState === 'hidden' ||
        interactivePanelOpen()
      )
        return;
      if (failures.current >= 5) return;
      if (skippedTicks.current > 0) {
        skippedTicks.current -= 1;
        return;
      }
      refreshing.current = true;
      const runGeneration = generation.current;
      void Promise.resolve()
        .then(() => refreshRef.current())
        .then(() => {
          if (generation.current !== runGeneration) return;
          failures.current = 0;
          skippedTicks.current = 0;
        })
        .catch(() => {
          if (generation.current !== runGeneration) return;
          failures.current += 1;
          skippedTicks.current = Math.min(2 ** failures.current, 8) - 1;
        })
        .finally(() => {
          refreshing.current = false;
        });
    };
    const visible = () => {
      if (document.visibilityState === 'visible') {
        reset();
        run();
      }
    };
    const interval = window.setInterval(run, LIVE_REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [paused, reset]);
  return reset;
}

/** A server code when there is one, otherwise prose - `FailureText` reads both. */
function failureDetail(error: unknown): string {
  if (typeof error === 'string' && error.trim()) return error;
  if (error && typeof error === 'object') {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'request_failed';
}

/**
 * A 403 is kept apart from both an empty result and a failure: an operator
 * whose role cannot read a section must be told that, not shown a bare table.
 */
export function useInsight<T>(
  load: () => Promise<Read<T>>,
  dependencies: readonly unknown[],
  // `live: false` opts a read out of background refresh. Lists stay live;
  // dashboards and reports that aggregate don't poll, since a chart changing
  // under someone reading it is noise and each tick costs a heavy query.
  options?: { pauseRefresh?: boolean; live?: boolean },
) {
  const [state, setState] = useState<Insight<T>>({ status: 'loading' });
  const read = useCallback(async (options?: { preserve?: boolean }) => {
    if (!options?.preserve) setState({ status: 'loading' });
    try {
      configureAdminApi();
      const result = await load();
      handleUnauthorized(result.response?.status);
      if (result.response?.status === 403) {
        if (options?.preserve) throw new Error('forbidden');
        setState({ status: 'forbidden' });
        return;
      }
      if (result.error || !result.data) {
        if (options?.preserve) throw new Error(failureDetail(result.error));
        setState({ status: 'failed', detail: failureDetail(result.error) });
        return;
      }
      // The read is also when its signed URLs were minted, so panels that
      // render imagery can tell how old those signatures are.
      setState({ status: 'ready', data: result.data, receivedAt: Date.now() });
    } catch (cause) {
      if (options?.preserve) throw cause;
      setState({ status: 'failed', detail: failureDetail(cause) });
    }
  }, dependencies);
  useEffect(() => void read(), [read]);
  const resetLiveRefresh = useLiveRefresh(
    () => read({ preserve: true }),
    Boolean(options?.pauseRefresh) || options?.live === false,
  );
  const refresh = useCallback(
    (options?: { preserve?: boolean }) => {
      resetLiveRefresh();
      return read(options);
    },
    [read, resetLiveRefresh],
  );
  return { state, refresh };
}

export function InsightState<T>({
  source,
  subject,
  children,
}: {
  source: { state: Insight<T>; refresh: () => void };
  /** What this read is of, used verbatim in the failure and permission wording. */
  subject: string;
  /** `receivedAt` is when the payload arrived, which dates any signed URL in it. */
  children: (data: T, receivedAt: number) => ReactNode;
}) {
  const { state } = source;
  if (state.status === 'loading')
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-tertiary" aria-live="polite">
        <LoadingIndicator size="sm" type="dot-circle" />
        Loading…
      </div>
    );
  if (state.status === 'forbidden')
    return (
      <Callout tone="warning" title={`Your role cannot read ${subject}.`}>
        This is a permission boundary, not an empty record — nothing can be concluded about this
        member from this panel. Ask an admin to widen your access.
      </Callout>
    );
  if (state.status === 'failed')
    return (
      <Callout
        tone="error"
        title={`${subject.charAt(0).toUpperCase()}${subject.slice(1)} could not be loaded.`}
        actions={
          <Button size="sm" color="secondary" onClick={source.refresh}>
            Try again
          </Button>
        }
      >
        <FailureText raw={state.detail} />
      </Callout>
    );
  return <>{children(state.data, state.receivedAt)}</>;
}

/** An absence that is genuinely expected, worded against the thing that is missing. */
export function Absent({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Callout tone="neutral" title={title}>
      {children}
    </Callout>
  );
}

/* ---------------------------------------------------------------- onboarding */

function OnboardingSourceNote() {
  // This replaces the retired empty-state copy “No onboarding session has been booked for this member.”
  return (
    <Callout tone="info" title="This panel shows the member’s in-app onboarding progress.">
      It reads the member-owned state the app saves as they move through onboarding. Missing fields
      mean that part of the flow has not been recorded yet; they do not indicate a failed read.
    </Callout>
  );
}

type AvatarEnhancementOptions = {
  expression: 'neutral' | 'soft_smile' | 'smile';
  makeup: 'none' | 'subtle';
  lighting: 'as_is' | 'even';
};

const isExpression = (value: unknown): value is AvatarEnhancementOptions['expression'] =>
  value === 'neutral' || value === 'soft_smile' || value === 'smile';

const isMakeup = (value: unknown): value is AvatarEnhancementOptions['makeup'] =>
  value === 'none' || value === 'subtle';

const isLighting = (value: unknown): value is AvatarEnhancementOptions['lighting'] =>
  value === 'as_is' || value === 'even';

type OnboardingMedia = {
  step: string;
  kind: string;
  url: string | null;
  enhancement_options: AvatarEnhancementOptions | null;
};

/** Narrow the generated media schema's open object before using its signed URL. */
function onboardingMedia(item: AdminMemberOnboardingMedia): OnboardingMedia | null {
  if (typeof item.step !== 'string' || typeof item.kind !== 'string') return null;
  if (item.url !== null && item.url !== undefined && typeof item.url !== 'string') return null;
  const raw = item.enhancement_options;
  const options = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : null;
  const enhancementOptions =
    options &&
    isExpression(options.expression) &&
    isMakeup(options.makeup) &&
    isLighting(options.lighting)
      ? {
          expression: options.expression,
          makeup: options.makeup,
          lighting: options.lighting,
        }
      : null;
  return {
    step: item.step,
    kind: item.kind,
    url: item.url ?? null,
    enhancement_options: enhancementOptions,
  };
}

function OnboardingEvidenceImage({ media, alt }: { media?: OnboardingMedia; alt: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [media?.url]);

  const frame =
    'relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border border-secondary bg-secondary';
  if (!media?.url || failed)
    return (
      <div
        className={frame}
        data-image-state="absent"
        role="img"
        aria-label={`${alt} — no image was captured for this step`}
      >
        <div className="flex flex-col items-center gap-1 px-2 py-3 text-center">
          <Image01 className="size-5 text-fg-quaternary" />
          <span className="text-xs font-medium text-tertiary">
            No image was captured for this step.
          </span>
          <span className="text-xs text-quaternary">No onboarding media was returned.</span>
        </div>
      </div>
    );

  return (
    <div onErrorCapture={() => setFailed(true)}>
      <GarmentImage url={media.url} alt={alt} />
    </div>
  );
}

function EvidenceBox({
  title,
  media,
  items,
  headline = false,
  hasMediaSlot = true,
  children,
}: {
  title: string;
  media?: OnboardingMedia;
  items: Array<[ReactNode, ReactNode]>;
  headline?: boolean;
  hasMediaSlot?: boolean;
  children?: ReactNode;
}) {
  return (
    <article
      className={cx(
        'grid min-w-0 gap-4 rounded-xl border border-secondary bg-primary p-4',
        headline
          ? 'sm:col-span-2 sm:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] sm:items-start'
          : 'grid-cols-1',
      )}
    >
      {hasMediaSlot ? <OnboardingEvidenceImage media={media} alt={title} /> : null}
      <div className="flex min-w-0 flex-col gap-3">
        <div>
          {headline ? (
            <p className="text-xs font-medium uppercase tracking-wide text-tertiary">Photo step</p>
          ) : null}
          <h3
            className={
              headline ? 'text-lg font-semibold text-primary' : 'text-sm font-semibold text-primary'
            }
          >
            {title}
          </h3>
        </div>
        <DetailList items={items} />
        {children}
      </div>
    </article>
  );
}

function OnboardingEvidence({ insight }: { insight: AdminMemberOnboarding }) {
  const artifacts = insight.artifacts;
  const media = (artifacts?.media ?? []).flatMap((item) => {
    const parsed = onboardingMedia(item);
    return parsed ? [parsed] : [];
  });
  const hasEvidence = Boolean(
    media.some((item) => item.url) || (insight.style_deck_runs?.length ?? 0) > 0,
  );

  if (!hasEvidence)
    return (
      <Section title="Onboarding evidence">
        <Absent title="No onboarding evidence was returned for this member.">
          There are no captured images or onboarding artefact details to show.
        </Absent>
      </Section>
    );

  const forStep = (...steps: string[]) => media.find((item) => steps.includes(item.step));
  const recordedCurrentTwin = media.find((item) => item.kind === 'current_twin');
  const currentTwin = recordedCurrentTwin ?? forStep('photo');
  const styleDeckRun = insight.style_deck_runs?.at(-1);
  return (
    <Section
      title="Onboarding evidence"
      description="The member’s current twin and the evidence returned for each captured onboarding step."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <EvidenceBox
          headline
          title="Current twin"
          media={currentTwin}
          items={
            recordedCurrentTwin
              ? [
                  [
                    'Enhanced with',
                    recordedCurrentTwin.enhancement_options
                      ? enhancementOptionsLabel(recordedCurrentTwin.enhancement_options)
                      : 'Not recorded',
                  ],
                ]
              : []
          }
        />
        {/* Colour analysis is read from the answers route, under "What the member told us". */}
        <EvidenceBox
          title="Style deck"
          hasMediaSlot={false}
          items={
            styleDeckRun
              ? [
                  ['Run status', <Status value={styleDeckRun.status} />],
                  ['Run revision', styleDeckRun.revision],
                ]
              : []
          }
        >
          {styleDeckRun ? <StyleDeckResponses responses={styleDeckRun.responses ?? []} /> : null}
        </EvidenceBox>
      </div>
    </Section>
  );
}

function StyleDeckResponses({
  responses,
}: {
  responses: NonNullable<AdminMemberOnboarding['style_deck_runs']>[number]['responses'];
}) {
  const recorded = responses ?? [];
  const counts = recorded.reduce<Map<string, number>>((summary, response) => {
    summary.set(response.response, (summary.get(response.response) ?? 0) + 1);
    return summary;
  }, new Map());

  if (!recorded.length)
    return <span className="text-sm text-tertiary">No responses recorded.</span>;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5" aria-label="Response counts">
        {[...counts].map(([response, count]) => (
          <span
            key={response}
            className="rounded-full border border-secondary bg-secondary px-2 py-1 text-xs text-secondary"
          >
            {response}: {formatCount(count)}
          </span>
        ))}
      </div>
      <ul className="flex flex-col divide-y divide-secondary rounded-lg border border-secondary">
        {recorded.map((response) => (
          <li
            key={`${response.card_id}-${response.responded_at}`}
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs"
          >
            <span className="font-medium text-secondary">{response.card_id}</span>
            <span className="text-tertiary">
              {response.response} · {when(response.responded_at)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* --------------------------------------------------- twin and body shape */

type AvatarAction = 'enhance' | 'undo' | 'recompute';

const DEFAULT_AVATAR_ENHANCEMENT_OPTIONS: AvatarEnhancementOptions = {
  expression: 'soft_smile',
  makeup: 'subtle',
  lighting: 'as_is',
};

const AVATAR_ACTIONS: readonly AvatarAction[] = ['enhance', 'undo', 'recompute'];

const AVATAR_REASON: Record<AvatarAction, string> = {
  enhance: 'Enhanced the member’s twin from the admin console.',
  undo: 'Undid the member’s last twin improvement from the admin console.',
  recompute: 'Recomputed the member’s body shape from the admin console.',
};

type AvatarOutcome =
  | { kind: 'queued'; data: AdminOnBehalfAvatarOperation; options: AvatarEnhancementOptions }
  | { kind: 'replacement'; data: AdminOnBehalfJobAccepted }
  | { kind: 'avatar'; data: AdminOnBehalfAvatarResult }
  | { kind: 'body-shape'; data: AdminOnBehalfBodyShape };

/**
 * The writes against a member's twin and body shape.
 *
 * They live inside the onboarding panel because they operate on the same
 * member, although they are independent of the member's onboarding progress.
 *
 * The idempotency key for the paid enhancement is minted when the choice step
 * is confirmed. A repeated transport attempt carries that request identity;
 * a new confirm press is a new intent and gets a fresh key.
 */
function AvatarOperatorActions({ memberId }: { memberId: string }) {
  const [photo, setPhoto] = useState<File>();
  const [reason, setReason] = useState('');
  const [confirmingReplacement, setConfirmingReplacement] = useState(false);
  const [confirmingEnhancement, setConfirmingEnhancement] = useState(false);
  const [enhancementOptions, setEnhancementOptions] = useState<AvatarEnhancementOptions>(
    DEFAULT_AVATAR_ENHANCEMENT_OPTIONS,
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [outcome, setOutcome] = useState<AvatarOutcome>();

  /** One completion path, so no branch below can forget the 403 or the reset. */
  const finish = <T,>(
    result: { data?: T | undefined; error?: unknown; response: { status: number } },
    outcomeOf: (data: T) => AvatarOutcome,
    specific: Record<number, string> = {},
  ) => {
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot take that action on a member’s twin or body shape.');
      return;
    }
    const known = specific[result.response.status];
    if (known) {
      setFailure(known);
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That action could not be completed.'));
      return;
    }
    setOutcome(outcomeOf(result.data));
  };

  const run = async (action: AvatarAction, options?: AvatarEnhancementOptions) => {
    if (action === 'enhance') {
      if (!options) return;
      setBusy(true);
      setFailure(undefined);
      configureAdminApi();
      const path = { id: memberId };
      const reason = `${AVATAR_REASON.enhance.slice(0, -1)} (expression: ${words(options.expression)}, makeup: ${words(options.makeup)}, lighting: ${words(options.lighting)}).`;
      const result = await enhanceMemberAvatarApiV1InternalUsersIdAvatarEnhancePost({
        path,
        headers: { 'Idempotency-Key': `avatar-enhance-${crypto.randomUUID()}` },
        body: { reason, ...options },
      });
      finish(result, (data) => ({ kind: 'queued', data, options }));
      return;
    }
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const path = { id: memberId };
    if (action === 'recompute') {
      const result = await recomputeMemberBodyShapeApiV1InternalUsersIdBodyShapeRecomputePost({
        path,
        body: { reason: AVATAR_REASON.recompute },
      });
      finish(result, (data) => ({ kind: 'body-shape', data }), {
        409: 'This member has no usable measurements, so the classifier was refused rather than allowed to record a shape nobody measured. Capture a body scan first.',
      });
      return;
    }
    if (action === 'undo') {
      const result = await undoMemberAvatarImprovementApiV1InternalUsersIdAvatarUndoPost({
        path,
        body: { reason: AVATAR_REASON.undo },
      });
      finish(result, (data) => ({ kind: 'avatar', data }), {
        404: 'This member has no earlier published twin to go back to.',
      });
      return;
    }
  };

  const replaceTwin = async () => {
    const trimmedReason = reason.trim();
    if (!photo || trimmedReason.length < 3) return;
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await uploadMemberTwinPhoto({
      path: { id: memberId },
      headers: { 'Idempotency-Key': `admin-twin-replace:${crypto.randomUUID()}` },
      body: {
        photo: photo as unknown as string,
        reason: trimmedReason,
        mode: 'replace',
      },
    });
    setConfirmingReplacement(false);
    finish(result, (data) => ({ kind: 'replacement', data }));
  };

  const replacementReady = Boolean(photo && reason.trim().length >= 3);

  const start = (action: AvatarAction) => {
    setFailure(undefined);
    setOutcome(undefined);
    if (action === 'enhance') {
      setEnhancementOptions(DEFAULT_AVATAR_ENHANCEMENT_OPTIONS);
      setConfirmingEnhancement(true);
      return;
    }
    void run(action);
  };

  return (
    <Section
      title="Twin and body shape"
      description="Member-scoped controls for the published twin and stored body shape. Every action is recorded against your operator account with this member named as the subject."
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        {outcome ? <AvatarOutcomeNote outcome={outcome} /> : null}
        <TextField
          label="Replacement twin photo"
          type="file"
          accept="image/*"
          required
          disabled={confirmingReplacement || busy}
          onChange={(event) => {
            setPhoto(event.target.files?.[0]);
            setConfirmingReplacement(false);
          }}
          hint="Choose the photo that should replace this member’s current twin after processing."
        />
        <TextAreaField
          label="Reason for replacing the twin"
          required
          minLength={3}
          maxLength={1000}
          disabled={confirmingReplacement || busy}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            setConfirmingReplacement(false);
          }}
          hint="Required. This explanation is recorded with the operator action."
        />
        {confirmingReplacement ? (
          <ConfirmStep
            title="Replace this member’s twin?"
            body="This photo will be queued for processing in replace mode. If processing succeeds, it will replace the likeness the member currently sees; the change is not completed by this acceptance response and is hard to reverse."
            confirmLabel="Confirm replace twin"
            busy={busy}
            onCancel={() => setConfirmingReplacement(false)}
            onConfirm={() => void replaceTwin()}
          />
        ) : (
          <Actions>
            <Button
              size="sm"
              color="primary-destructive"
              isDisabled={!replacementReady || busy}
              onClick={() => {
                setFailure(undefined);
                setOutcome(undefined);
                setConfirmingReplacement(true);
              }}
            >
              Replace twin
            </Button>
          </Actions>
        )}
        {confirmingEnhancement ? (
          <ConfirmStep
            title="Enhance this member’s twin?"
            body={
              <div className="flex flex-col gap-4">
                <SelectField
                  label="Expression"
                  value={enhancementOptions.expression}
                  onChange={(event) => {
                    const expression = event.currentTarget
                      .value as AvatarEnhancementOptions['expression'];
                    setEnhancementOptions((current) => ({
                      ...current,
                      expression,
                    }));
                  }}
                  options={[
                    { value: 'neutral', label: 'Neutral' },
                    { value: 'soft_smile', label: 'Soft smile (default)' },
                    { value: 'smile', label: 'Smile' },
                  ]}
                  hint="Soft smile is the closed-lip smile every twin gets today. Neutral suits members who asked for no smile; Smile shows teeth."
                />
                <SelectField
                  label="Makeup"
                  value={enhancementOptions.makeup}
                  onChange={(event) => {
                    const makeup = event.currentTarget.value as AvatarEnhancementOptions['makeup'];
                    setEnhancementOptions((current) => ({
                      ...current,
                      makeup,
                    }));
                  }}
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'subtle', label: 'Subtle (default)' },
                  ]}
                  hint="Subtle evens skin and adds light definition, as today. None keeps the member's face exactly as photographed."
                />
                <SelectField
                  label="Lighting"
                  value={enhancementOptions.lighting}
                  onChange={(event) => {
                    const lighting = event.currentTarget
                      .value as AvatarEnhancementOptions['lighting'];
                    setEnhancementOptions((current) => ({
                      ...current,
                      lighting,
                    }));
                  }}
                  options={[
                    { value: 'as_is', label: 'As is (default)' },
                    { value: 'even', label: 'Even out' },
                  ]}
                  hint="Even out corrects exposure, white balance and uneven studio light, for dim, yellow or patchy photos. Skin tone is always kept."
                />
                <span>
                  This is a paid enhancement. Leaving every option at its default repeats today's
                  enhancement; any other choice is charged as a new enhancement.
                </span>
              </div>
            }
            confirmLabel="Confirm enhance"
            cancelLabel="Cancel"
            busy={busy}
            onCancel={() => setConfirmingEnhancement(false)}
            onConfirm={() => {
              const options = enhancementOptions;
              setConfirmingEnhancement(false);
              void run('enhance', options);
            }}
          />
        ) : (
          <Actions>
            {AVATAR_ACTIONS.map((action) => (
              <Button
                key={action}
                size="sm"
                color="secondary"
                isDisabled={busy}
                onClick={() => start(action)}
              >
                {action === 'enhance'
                  ? 'Enhance twin…'
                  : action === 'undo'
                    ? 'Undo last improvement'
                    : 'Recompute body shape'}
              </Button>
            ))}
          </Actions>
        )}
      </div>
    </Section>
  );
}

/**
 * What the write left behind, in plain words rather than as two raw values.
 *
 * A body-shape recompute that changed nothing and one that silently
 * reclassified the member are the same 200, so `changed` leads the sentence
 * instead of leaving the before and after side by side to be diffed by eye.
 */
function AvatarOutcomeNote({ outcome }: { outcome: AvatarOutcome }) {
  if (outcome.kind === 'replacement')
    return (
      <Callout tone="success" title="The twin replacement was accepted and queued.">
        The new likeness is being processed and has not replaced the member’s twin yet.
        {outcome.data.reference ? (
          <IdentifierCopy label="Queued job reference" value={outcome.data.reference} />
        ) : (
          <span className="block pt-1 text-sm">Queue status: {words(outcome.data.status)}</span>
        )}
      </Callout>
    );
  if (outcome.kind === 'queued')
    return (
      <Callout
        tone={outcome.data.replayed ? 'warning' : 'success'}
        title={
          outcome.data.replayed
            ? 'This enhancement was already running, so nothing new was bought.'
            : 'A twin enhancement was queued.'
        }
      >
        <span className="block pb-1">
          Chosen options: {enhancementOptionsLabel(outcome.options)}.
        </span>
        <IdentifierCopy label="Operation reference" value={outcome.data.operation_id} />
      </Callout>
    );
  if (outcome.kind === 'avatar')
    return (
      <Callout tone="success" title={`The member’s twin was ${words(outcome.data.action)}.`}>
        <div className="flex flex-col gap-2">
          <IdentifierValue
            label="Twin now"
            value={outcome.data.url ?? 'No published twin — the member has none.'}
          />
          <IdentifierValue label="Twin before" value={outcome.data.previous_url ?? 'None'} />
        </div>
      </Callout>
    );
  return (
    <Callout
      tone={outcome.data.changed ? 'warning' : 'success'}
      title={
        outcome.data.changed
          ? `Recomputed, and the shape changed: ${words(outcome.data.previous_body_shape ?? 'none recorded')} is now ${words(outcome.data.body_shape)}.`
          : `Recomputed, same answer: still ${words(outcome.data.body_shape)}.`
      }
    >
      {outcome.data.changed
        ? 'The member’s recommendations are fitted to this shape, so what they are shown will move with it.'
        : 'The classifier agreed with the shape already on record. Nothing downstream changes.'}
    </Callout>
  );
}

function enhancementOptionsLabel(options: AvatarEnhancementOptions) {
  const expression = {
    neutral: 'Neutral',
    soft_smile: 'Soft smile',
    smile: 'Smile',
  }[options.expression];
  const makeup = { none: 'No makeup', subtle: 'Subtle makeup' }[options.makeup];
  const lighting = { as_is: 'Lighting as is', even: 'Even lighting' }[options.lighting];
  return `${expression} · ${makeup} · ${lighting}`;
}

/** JSON values from member-owned progress, made readable without assuming a shape. */
function recordedValue(value: unknown): string {
  if (typeof value === 'string') return words(value);
  if (value === null) return 'null';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === undefined) return 'Not recorded';
  return JSON.stringify(value) ?? 'Not recorded';
}

const ONBOARDING_ACTION_REASON = {
  complete: 'Completed onboarding from the admin console.',
  reset: 'Reset onboarding state from the admin console.',
} as const;

function OnboardingOperatorActions({
  memberId,
  revision,
  onDone,
}: {
  memberId: string;
  revision: number | null | undefined;
  onDone: () => void;
}) {
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [result, setResult] = useState<AdminOnBehalfOnboardingResult>();

  const run = async (action: 'complete' | 'reset') => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const options = {
      path: { id: memberId },
      body: {
        reason: ONBOARDING_ACTION_REASON[action],
        expected_revision: revision ?? null,
      },
    };
    const response =
      action === 'complete'
        ? await completeMemberOnboardingApiV1InternalUsersIdOnboardingCompletePost(options)
        : await resetMemberOnboardingApiV1InternalUsersIdOnboardingResetPost(options);
    handleUnauthorized(response.response.status);
    setBusy(false);
    if (response.response.status === 403) {
      setFailure('Your role cannot change a member’s onboarding state.');
      return;
    }
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'That onboarding action could not be completed.'));
      return;
    }
    setResult(response.data);
    setResetOpen(false);
    onDone();
  };

  return (
    <Section
      title="Onboarding actions"
      description="Complete or reset this member’s onboarding state. These actions are recorded against your operator account. Resetting does not delete colour analysis, avatar, body shape, or other member artefacts."
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        {result ? (
          <Callout tone="success" title="The server saved the onboarding state change.">
            <div className="flex flex-col gap-2">
              <IdentifierValue label="Checkpoint" value={result.checkpoint} />
              <IdentifierValue label="Revision" value={String(result.revision)} />
            </div>
          </Callout>
        ) : null}
        <Actions>
          <Button
            size="sm"
            color="secondary"
            isDisabled={busy}
            onClick={() => void run('complete')}
          >
            Complete onboarding
          </Button>
          <Button
            size="sm"
            color="secondary-destructive"
            isDisabled={busy}
            onClick={() => {
              setFailure(undefined);
              setResetOpen(true);
            }}
          >
            Reset onboarding
          </Button>
        </Actions>
        {resetOpen ? (
          <ActionDialog
            title="Reset this member’s onboarding state?"
            description="This changes only onboarding progress. The member’s colour analysis, avatar, body shape, wardrobe, and other artefacts remain in place."
            onClose={() => setResetOpen(false)}
          >
            <ConfirmStep
              title="Confirm onboarding reset."
              body="The member will start onboarding again from its reset state. Their existing artefacts are not deleted."
              confirmLabel="Confirm reset onboarding"
              busy={busy}
              onCancel={() => setResetOpen(false)}
              onConfirm={() => void run('reset')}
            />
          </ActionDialog>
        ) : null}
      </div>
    </Section>
  );
}

/** The current twin from the onboarding read, for comparing with the photo it was made from. */
function currentTwinOf(insight: AdminMemberOnboarding): OnboardingMedia | undefined {
  const media = (insight.artifacts?.media ?? []).flatMap((item) => {
    const parsed = onboardingMedia(item);
    return parsed ? [parsed] : [];
  });
  return (
    media.find((item) => item.kind === 'current_twin') ??
    media.find((item) => item.step === 'photo')
  );
}

function OnboardingPanel({ id, member }: { id: string; member?: ConsumerDetail | undefined }) {
  const source = useInsight<AdminMemberOnboarding>(
    () => memberOnboardingApiV1InternalUsersIdOnboardingGet({ path: { id } }),
    [id],
  );
  return (
    <InsightState source={source} subject="this member's onboarding progress">
      {(insight, receivedAt) => {
        const completedSteps = insight.completed_steps ?? [];
        const checkpoint = insight.checkpoint ? Object.entries(insight.checkpoint) : [];
        const styleDeckRuns = insight.style_deck_runs ?? [];
        const step =
          typeof insight.step === 'string' ? words(insight.step) : (insight.step ?? 'Not recorded');
        return (
          <div className="flex flex-col gap-6">
            <OnboardingSourceNote />
            <OnboardingEvidence insight={insight} />
            <OnboardingAnswersPanel
              id={id}
              member={member}
              currentTwin={{
                url: currentTwinOf(insight)?.url ?? null,
                signedAt: receivedAt,
                onRefresh: () => source.refresh({ preserve: true }),
              }}
            />
            <Section title="Progress">
              <DetailList
                items={[
                  ['Status', <Status value={insight.completed ? 'completed' : 'in progress'} />],
                  ['Current step', step],
                  ['Revision', insight.revision ?? 'Not recorded'],
                  ['Completed', when(insight.completed_at, 'Not completed')],
                  [
                    'Scan deferred',
                    insight.scan_deferred == null
                      ? 'Not recorded'
                      : insight.scan_deferred
                        ? 'Yes'
                        : 'No',
                  ],
                  ['Scan deferred at', when(insight.scan_deferred_at, 'Not recorded')],
                ]}
              />
            </Section>

            <OnboardingOperatorActions
              memberId={id}
              revision={insight.revision}
              onDone={() => void source.refresh()}
            />

            <Section
              title="Completed steps"
              description="The progress markers the app has recorded for this member."
            >
              {completedSteps.length ? (
                <ul className="flex flex-col divide-y divide-secondary rounded-xl border border-secondary bg-primary">
                  {completedSteps.map((completedStep, index) => (
                    <li key={index} className="px-4 py-3 text-sm font-medium text-secondary">
                      {recordedValue(completedStep)}
                    </li>
                  ))}
                </ul>
              ) : (
                <Absent title="No completed steps are recorded.">
                  Partial onboarding progress is normal; the app has not recorded a completed-step
                  marker for this member yet.
                </Absent>
              )}
            </Section>

            <Section
              title="Checkpoint"
              description="The member-owned values the app will use when onboarding resumes."
            >
              {checkpoint.length ? (
                <DetailList
                  items={checkpoint.map(([key, value]) => [words(key), recordedValue(value)])}
                />
              ) : (
                <Absent title="No checkpoint is recorded.">
                  This is an ordinary partial-progress state: the app has no saved checkpoint
                  contents for this member yet.
                </Absent>
              )}
            </Section>

            {styleDeckRuns.length ? (
              <Section
                title="Style deck runs"
                description="Style deck activity associated with this member's onboarding."
              >
                <div className="flex flex-col gap-4">
                  {styleDeckRuns.map((run) => (
                    <div key={run.id} className="rounded-xl border border-secondary bg-primary p-4">
                      <DetailList
                        items={[
                          ['Status', <Status value={run.status} />],
                          ['Deck version', run.deck_version],
                          ['Revision', run.revision],
                          ['Created', when(run.created_at)],
                          ['Completed', when(run.completed_at, 'Not completed')],
                          ['Responses', String(run.responses?.length ?? 0)],
                        ]}
                      />
                      <div className="mt-3">
                        <StyleDeckResponses responses={run.responses ?? []} />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            ) : null}

            <AvatarOperatorActions memberId={id} />
          </div>
        );
      }}
    </InsightState>
  );
}

/* ------------------------------------------------------------------ wardrobe */

/**
 * Turning the composition charts into filters.
 *
 * `GET /users/{id}/wardrobe` returns each garment's attribute document, so
 * every facet the server tallies can be matched here. Each reader below picks
 * values with the same key precedence as the server's facet SQL
 * (`_wardrobe_facet_tallies`): an array-valued key contributes every element,
 * so a garment tagged for two seasons matches both season buckets. Values are
 * trimmed and lower-cased as the server buckets them; a garment with no value
 * is what the "unknown" bucket counts.
 */
function facetText(value: unknown) {
  if (typeof value === 'string') return value.trim().toLowerCase() || null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

/**
 * The values a facet reads off a garment, mirroring the SQL: an array under
 * `arrayKey` wins, otherwise the first scalar key that is present - present
 * even when blank, because the SQL tests for the key and not its value.
 */
function attributeFacetValues(
  garment: AdminMemberGarment,
  arrayKey: string,
  scalarKeys: readonly string[],
): string[] {
  const attributes = garment.attributes ?? {};
  const listed = attributes[arrayKey];
  let raw: unknown[] = [];
  if (Array.isArray(listed)) raw = listed;
  else {
    const key = scalarKeys.find((candidate) => candidate in attributes);
    if (key !== undefined) raw = [attributes[key]];
  }
  return raw.map(facetText).filter((value): value is string => value !== null);
}

const FACET_FIELDS: Record<string, (garment: AdminMemberGarment) => string[]> = {
  colour: (garment) =>
    attributeFacetValues(garment, 'colour', ['primary_colour', 'colour_primary_name', 'colour']),
  season: (garment) => attributeFacetValues(garment, 'season', ['season']),
  // Edits and the pipeline write the `occasion_tags` list; older garments carry
  // `occasions` or a single `occasion`. The server's tallies read them in that order.
  occasion: (garment) => {
    for (const key of ['occasion_tags', 'occasions']) {
      const values = attributeFacetValues(garment, key, []);
      if (values.length) return values;
    }
    return attributeFacetValues(garment, 'occasion', ['occasion']);
  },
  // `COALESCE(attributes->>'category', garments.category)`: the attribute
  // document's category wins over the column when it holds a value.
  category: (garment) => {
    const own = garment.attributes?.category;
    const value = facetText(own === null || own === undefined ? garment.category : own);
    return value ? [value] : [];
  },
  // Not one of the server's facets: the "Never worn" headline figure, which is
  // a count of a field this read does carry, and so is filterable on the same
  // terms as a chart bucket.
  worn: (garment) => [(garment.wear_count ?? 0) > 0 ? 'worn' : 'never'],
};

/** Facets whose values are a closed set rather than whatever the data holds. */
const FACET_VALUES: Record<string, readonly string[]> = { worn: ['never', 'worn'] };

/** Mirrors the server's `FACET_LABELS`, so a chip can name a facet on its own. */
const FACET_LABELS: Record<string, string> = {
  colour: 'Colour',
  category: 'Category',
  occasion: 'Occasion',
  season: 'Season',
  worn: 'Wear',
};

/** The server's two synthetic buckets: the null tally and the tail past top-N. */
const UNKNOWN_BUCKET = 'unknown';
const TAIL_BUCKET = 'other';

/** Where a filtered view keeps its selection, one parameter per facet. */
const FACET_PARAM = 'facet.';

/** The API's own ceiling on one page, and so on how far a filter can see. */
const FILTER_SCAN = 200;

export type FacetFilters = Record<string, string>;

export function facetFilterable(key: string) {
  return key in FACET_FIELDS;
}

/** `other` is the remainder of the top-N list rather than a value anything has. */
function bucketSelectable(key: string, value: string) {
  if (!facetFilterable(key) || value === TAIL_BUCKET || !value.trim()) return false;
  const closed = FACET_VALUES[key];
  return !closed || closed.includes(value);
}

function facetLabel(key: string) {
  return FACET_LABELS[key] ?? words(key);
}

function bucketLabel(value: string) {
  return value === UNKNOWN_BUCKET ? 'No value recorded' : facetValue(value);
}

/** How an active filter names itself on its chip. */
function filterLabel(key: string, value: string) {
  if (key === 'worn') return value === 'never' ? 'Never worn' : 'Worn at least once';
  return bucketLabel(value);
}

/**
 * The selection carried by the URL, reduced to the filters that can actually be
 * applied. A hand-typed or stale parameter for a facet this read cannot match
 * is dropped here rather than shown as an active filter that narrows nothing.
 */
export function readFacetFilters(params: URLSearchParams): FacetFilters {
  const filters: FacetFilters = {};
  for (const [param, value] of params) {
    if (!param.startsWith(FACET_PARAM)) continue;
    const key = param.slice(FACET_PARAM.length);
    if (bucketSelectable(key, value)) filters[key] = value;
  }
  return filters;
}

function garmentMatches(garment: AdminMemberGarment, filters: FacetFilters) {
  return Object.entries(filters).every(([key, value]) => {
    const field = FACET_FIELDS[key];
    // A filter that cannot be evaluated hides rather than silently passes: an
    // over-narrow grid is visible, an ignored filter is not.
    if (!field) return false;
    const actual = field(garment);
    return value === UNKNOWN_BUCKET ? actual.length === 0 : actual.includes(value);
  });
}

function FacetBreakdown({
  facet,
  population,
  selected,
  onSelect,
}: {
  facet: AdminWardrobeFacet;
  population: number;
  /** The bucket currently filtering the grid, if this facet is the one doing it. */
  selected: string | undefined;
  onSelect: (value: string | null) => void;
}) {
  const buckets = facet.buckets ?? [];
  const counted = facet.known + facet.unknown;
  // Present only once the server unnests array facets. When it is there,
  // `known` is a tag count and may exceed it.
  const carrying = facet.garments;
  const overlaps = carrying !== undefined && facet.known > carrying;
  const row = 'grid grid-cols-[minmax(4rem,7rem)_minmax(0,1fr)_auto] items-center gap-3';
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-primary">{facet.label}</h3>
        <span
          className="text-xs text-tertiary"
          title={
            overlaps
              ? `${carrying} active garment(s) carry this, with ${facet.known} tag(s) between them - a garment can be tagged more than once, so the bars sum above 100%. Archived garments are not in this breakdown, which is why these totals are smaller than the ${population} garment(s) on record.`
              : `Counted over the ${counted} active garment(s) in this wardrobe. Archived garments are not in this breakdown, which is why these totals are smaller than the ${population} garment(s) on record.`
          }
        >
          {overlaps
            ? `${carrying} garment(s) tagged · ${facet.known} tag(s) · ${facet.unknown} unknown`
            : `${facet.known} known · ${facet.unknown} unknown, of ${counted} active garment(s)`}
        </span>
      </div>
      {buckets.length ? (
        <ul className="flex flex-col gap-2">
          {buckets.map((bucket) => {
            const label = bucketLabel(bucket.value);
            const figures = (
              <>
                <span className="truncate text-xs text-secondary capitalize">{label}</span>
                <span className="block h-2 overflow-hidden rounded-full bg-secondary">
                  <span
                    className="block h-full min-w-px rounded-full bg-brand-solid"
                    style={{
                      width: `${Math.min(100, Math.max(0, bucket.share * 100)).toFixed(1)}%`,
                    }}
                  />
                </span>
                <Quantity
                  className="text-xs text-tertiary"
                  value={formatCount(bucket.count)}
                  raw={`${bucket.count} garments, ${formatPercent(bucket.share)} of those recorded`}
                />
              </>
            );
            const selectable = bucketSelectable(facet.key, bucket.value);
            const active = selected === bucket.value;
            return (
              <li key={bucket.value} className="min-w-0">
                {selectable ? (
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => onSelect(active ? null : bucket.value)}
                    title={
                      active
                        ? `Stop filtering the garments below by ${facet.label.toLowerCase()} “${label}”.`
                        : `Show only the garments whose ${facet.label.toLowerCase()} is “${label}” — ${bucket.count} of the ${counted} active garment(s) counted here.`
                    }
                    className={cx(
                      row,
                      'w-full cursor-pointer rounded-md px-1.5 py-1 text-left outline-brand hover:bg-secondary focus-visible:outline-2',
                      active && 'bg-secondary ring-1 ring-brand',
                    )}
                  >
                    {figures}
                  </button>
                ) : (
                  <span className={cx(row, 'px-1.5 py-1')}>{figures}</span>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-tertiary">
          No active garment carries a value for this attribute. Every attribute in the garment
          document is nullable and not every one of them is written by the ingestion pipeline, so
          this is a gap in the data rather than in the read.
        </p>
      )}
      {buckets.length && !facetFilterable(facet.key) ? (
        <p className="text-xs text-quaternary">These buckets cannot narrow the garments below.</p>
      ) : null}
    </div>
  );
}

function WardrobeSummary({
  id,
  filters,
  onSelectFacet,
}: {
  id: string;
  filters: FacetFilters;
  onSelectFacet: (key: string, value: string | null) => void;
}) {
  const source = useInsight<AdminMemberWardrobeSummary>(
    () => memberWardrobeSummaryApiV1InternalUsersIdWardrobeSummaryGet({ path: { id } }),
    [id],
  );
  return (
    <InsightState source={source} subject="this member's wardrobe summary">
      {(summary) => {
        if (!summary.total_garments)
          return (
            <Absent title="This member has no digitised garments.">
              Nothing has been ingested into this member&apos;s wardrobe, so there is no composition
              to break down.
            </Absent>
          );
        const facets = summary.facets ?? [];
        const onRecord = summary.total_garments ?? 0;
        return (
          <div className="flex flex-col gap-4">
            <StatRow>
              <StatTile
                label="Active garments"
                tooltip="Active means status 'active' with no archive date. A garment awaiting review is neither active nor archived, so the two figures need not add up to the number on record."
                value={
                  <Quantity
                    value={formatCount(summary.active_garments ?? 0)}
                    unit="garments"
                    raw={summary.active_garments ?? 0}
                  />
                }
                hint={`of ${formatCount(summary.total_garments ?? 0)} garment(s) on record \u00b7 ${formatCount(summary.archived_garments ?? 0)} archived`}
                {...(summary.total_garments
                  ? {
                      bar: {
                        fraction: (summary.active_garments ?? 0) / summary.total_garments,
                        label: `Active garments as a share of the whole wardrobe: ${summary.active_garments ?? 0} of ${summary.total_garments}`,
                      },
                    }
                  : {})}
              />
              <StatTile
                label="Attributed"
                tooltip="Counted over every garment on record, archived ones included - not over the active garments above. A garment is attributed when its attribute document is not empty; the document may still be missing any individual attribute below."
                value={
                  <Quantity
                    value={formatCount(summary.attributed_garments ?? 0)}
                    unit="garments"
                    raw={summary.attributed_garments ?? 0}
                  />
                }
                hint={`of ${formatCount(summary.total_garments ?? 0)} garment(s) on record`}
                {...(summary.total_garments
                  ? {
                      bar: {
                        fraction: (summary.attributed_garments ?? 0) / summary.total_garments,
                        label: `Described garments as a share of the whole wardrobe: ${summary.attributed_garments ?? 0} of ${summary.total_garments}`,
                      },
                    }
                  : {})}
              />
              {/* The one headline figure the garment read can reproduce, so
                  the one that earns a filter alongside the charts. */}
              <button
                type="button"
                aria-pressed={filters['worn'] === 'never'}
                onClick={() => onSelectFacet('worn', filters['worn'] === 'never' ? null : 'never')}
                title="Show only the garments this member has never worn."
                className={cx(
                  'min-w-0 cursor-pointer rounded-xl text-left outline-brand focus-visible:outline-2',
                  filters['worn'] === 'never' && 'ring-1 ring-brand',
                )}
              >
                <StatTile
                  label="Never worn"
                  tooltip="Counted over every garment on record, archived ones included, which is why it can exceed the active count. A garment is never worn when its wear count is zero."
                  value={
                    <Quantity
                      value={formatCount(summary.never_worn ?? 0)}
                      unit="garments"
                      raw={summary.never_worn ?? 0}
                    />
                  }
                  hint={`of ${formatCount(summary.total_garments ?? 0)} garment(s) on record \u00b7 ${formatCount(summary.total_wears ?? 0)} wear(s) recorded`}
                />
              </button>
              <StatTile
                label="Last worn"
                tooltip="The most recent wear across every garment on record, archived ones included."
                value={summary.last_worn_at ? when(summary.last_worn_at) : 'Never'}
                hint={`First garment photographed ${when(summary.first_digitized_at, 'at an unrecorded time')}.`}
              />
            </StatRow>
            {facets.length ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {facets.map((facet) => (
                  <FacetBreakdown
                    key={facet.key}
                    facet={facet}
                    population={onRecord}
                    selected={filters[facet.key]}
                    onSelect={(value) => onSelectFacet(facet.key, value)}
                  />
                ))}
              </div>
            ) : (
              <Absent title="No composition breakdown is available.">
                The summary carries no facets, so nothing about colour, category or formality has
                been derived for this wardrobe yet.
              </Absent>
            )}
          </div>
        );
      }}
    </InsightState>
  );
}

type GarmentStatus = 'active' | 'archived' | 'pending_review';
type GarmentView = 'gallery' | 'table';

function garmentAlt(garment: AdminMemberGarment) {
  return garment.title?.trim() || words(garment.category);
}

function garmentTitle(title: string | null | undefined) {
  return title?.trim() || 'Untitled garment';
}

const GARMENT_IN_PROGRESS_STATUSES = new Set(['processing']);
const GARMENT_ENHANCEMENT_IN_PROGRESS_STATUSES = new Set(['pending', 'processing']);

export function garmentInProgress(garment: AdminMemberGarment) {
  return (
    GARMENT_IN_PROGRESS_STATUSES.has(garment.status) ||
    GARMENT_ENHANCEMENT_IN_PROGRESS_STATUSES.has(garment.enhancement_status)
  );
}

function garmentFailureReason(garment: AdminMemberGarment) {
  if (garment.status !== 'failed' && garment.enhancement_status !== 'failed') return undefined;
  if (garment.failure_reason?.trim()) return garment.failure_reason;
  const upload = garment.attributes?.['upload_failure'];
  if (typeof upload === 'string' && upload.trim()) return upload;
  return 'The garment job failed without a stored reason.';
}

function GarmentGenerationState({ garment }: { garment: AdminMemberGarment }) {
  const failure = garmentFailureReason(garment);
  if (failure) return <FailureText raw={failure} />;
  if (!garmentInProgress(garment)) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-live="polite">
      <Status value={garment.status === 'processing' ? 'processing' : garment.enhancement_status} />
      <span className="text-xs text-tertiary">
        {garment.status === 'processing'
          ? 'Garment ingestion is running.'
          : garment.enhancement_status === 'pending'
            ? 'Enhancement is queued.'
            : 'Enhancement is running.'}
      </span>
    </div>
  );
}

/**
 * The attributes the detail dialog shows, each with the keys it is read from
 * in order. The ingestion pipeline and tag edits write `primary_colour`,
 * `pattern_family`, `primary_material`, `size_label` and `occasion_tags`; older
 * garments may still carry the earlier names, read as a fallback, as the
 * backend does (`wardrobe/garments/repository.py`).
 */
const GARMENT_ATTRIBUTE_KEYS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['Colour', ['primary_colour', 'colour_primary_name', 'colour']],
  ['Formality', ['formality']],
  ['Season', ['season']],
  ['Occasion', ['occasion_tags', 'occasions', 'occasion']],
  ['Pattern', ['pattern_family', 'pattern']],
  ['Fabric', ['primary_material', 'fabric']],
  ['Brand', ['brand']],
  ['Size', ['size_label', 'size']],
];

function garmentAttributeValue(value: unknown) {
  const scalar = (item: unknown) => {
    if (typeof item === 'string') return facetValue(item).trim();
    if (typeof item === 'number' || typeof item === 'boolean') return String(item);
    return '';
  };
  const values = Array.isArray(value)
    ? value.map(scalar).filter(Boolean)
    : [scalar(value)].filter(Boolean);
  return values.length ? values.join(', ') : undefined;
}

/** The first of `keys` that holds a displayable value. */
function garmentAttribute(garment: AdminMemberGarment, keys: readonly string[]) {
  for (const key of keys) {
    const value = garmentAttributeValue(garment.attributes?.[key]);
    if (value) return value;
  }
  return undefined;
}

function garmentAttributeItems(garment: AdminMemberGarment): Array<[ReactNode, ReactNode]> {
  return GARMENT_ATTRIBUTE_KEYS.flatMap(([label, keys]) => {
    const value = garmentAttribute(garment, keys);
    return value ? [[label, value]] : [];
  });
}

/**
 * Starting values for the edit-tags form, read with the same key precedence
 * as the display above. Only the fields an operator changes are sent (see
 * `garmentTagChanges`), so a value seeded here and left alone is never
 * written back under a different key.
 */
function prefillGarmentTags(garment: AdminMemberGarment) {
  const attrs = garment.attributes ?? {};
  const str = (...keys: string[]) => {
    for (const key of keys) {
      const value = attrs[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
    return '';
  };
  return {
    ...EMPTY_GARMENT_TAGS,
    category: garment.category ?? '',
    sub_category: garment.subcategory ?? '',
    brand: str('brand'),
    size: str('size_label', 'size'),
    colour_primary_hex: str('colour_primary_hex'),
    colour_primary_name: str('primary_colour', 'colour_primary_name', 'colour'),
    pattern: str('pattern_family', 'pattern'),
    fabric: str('primary_material', 'fabric'),
    formality: str('formality'),
    season: str('season'),
  };
}

type GarmentTags = typeof EMPTY_GARMENT_TAGS;

/**
 * The tag-edit body: only the fields the operator changed. The route rejects
 * a supplied null outright, so a field emptied by the operator is sent as ''
 * (which the wardrobe repository treats as a clear) and an untouched field is
 * left out, which leaves it as it was. The colour hex must match `#rrggbb`,
 * so an emptied hex cannot be sent and is left out too.
 */
export function garmentTagChanges(draft: GarmentTags, baseline: GarmentTags) {
  const body: Partial<Record<keyof GarmentTags, string>> = {};
  for (const key of Object.keys(draft) as Array<keyof GarmentTags>) {
    const value = draft[key].trim();
    if (value === baseline[key].trim()) continue;
    if (!value && key === 'colour_primary_hex') continue;
    body[key] = value;
  }
  return body;
}

/**
 * The wardrobe grid and its tile, shared with the import item page so an
 * import's items read exactly like the wardrobe they are becoming.
 */
export const GARMENT_GRID_CLASS = 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5';
export const GARMENT_TILE_CLASS =
  'flex w-full min-w-0 cursor-pointer flex-col gap-2 rounded-xl border border-secondary bg-primary p-3 text-left outline-brand hover:border-brand focus-visible:outline-2';

function GarmentCard({
  garment,
  onOpen,
  selection,
}: {
  garment: AdminMemberGarment;
  onOpen: () => void;
  selection?: { checked: boolean; onToggle: () => void };
}) {
  const alt = garmentAlt(garment);
  return (
    <li className="flex min-w-0 flex-col gap-1.5">
      {/* Outside the tile's button: a checkbox cannot live inside one. */}
      {selection ? (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
          <input
            type="checkbox"
            aria-label={`Select “${alt}”`}
            checked={selection.checked}
            onChange={selection.onToggle}
          />
          Select
        </label>
      ) : null}
      {/* The whole tile is the control, so the picture is what an operator
          clicks. The image therefore carries no button of its own here; the
          section's own "Refresh links" is what mints fresh signatures. */}
      <button type="button" aria-haspopup="dialog" onClick={onOpen} className={GARMENT_TILE_CLASS}>
        <GarmentImage
          url={garment.image_url}
          refPresent={garment.image_ref}
          originalUrl={garment.original_image_url}
          pending={garmentInProgress(garment)}
          alt={alt}
        />
        <span className="truncate text-sm font-medium text-primary" title={alt}>
          {alt}
        </span>
        <span className="truncate text-xs text-tertiary">
          {words(garment.category)}
          {garment.subcategory ? ` · ${words(garment.subcategory)}` : ''}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <Status value={garment.status} />
          <Status value={garment.enhancement_status} />
        </span>
        <GarmentGenerationState garment={garment} />
        <span className="text-xs text-quaternary">
          Worn {formatCount(garment.wear_count ?? 0)} time
          {(garment.wear_count ?? 0) === 1 ? '' : 's'} · last worn{' '}
          {when(garment.last_worn, 'never')}
        </span>
      </button>
    </li>
  );
}

/** How many pairings the dialog asks for first, and how many more each "Show more" adds. */
export const PAIRINGS_PAGE = 12;
/** The most pairings one read may return. */
const PAIRINGS_MAX = 50;

const PAIRING_SOURCE: Record<AdminGarmentPairing['source'], string> = {
  generated: 'AI suggested',
  stylist: 'Stylist',
  consumer: 'Member',
};

type PairingsState =
  | { status: 'loading'; data?: AdminGarmentPairingPage }
  | { status: 'ready'; data: AdminGarmentPairingPage }
  | { status: 'forbidden' }
  | { status: 'failed'; detail: string };

function PairingTile({
  pairing,
  memberId,
  onOpenGarment,
}: {
  pairing: AdminGarmentPairing;
  memberId: string;
  onOpenGarment?: ((garmentId: string) => void) | undefined;
}) {
  const title = pairing.title?.trim() || 'Untitled';
  const kind = [pairing.category, pairing.subcategory]
    .filter((value): value is string => Boolean(value))
    .map(words)
    .join(' · ');
  const face = (
    <>
      <GarmentImage url={pairing.thumbnail_url} refPresent={pairing.garment_id} alt={title} />
      <span className="truncate text-sm font-medium text-primary" title={title}>
        {title}
      </span>
      {kind ? <span className="truncate text-xs text-tertiary">{kind}</span> : null}
    </>
  );
  const href = refHref({ kind: 'garment', id: pairing.garment_id, memberId });
  const control = 'flex min-w-0 flex-col gap-2 text-left outline-brand focus-visible:outline-2';
  return (
    <li
      aria-label={title}
      className={cx(
        'flex min-w-0 flex-col gap-2 rounded-xl border border-secondary bg-primary p-3',
        !pairing.available && 'opacity-60',
      )}
    >
      {onOpenGarment ? (
        <button
          type="button"
          aria-label={`Open ${title}`}
          onClick={() => onOpenGarment(pairing.garment_id)}
          className={cx(control, 'cursor-pointer')}
        >
          {face}
        </button>
      ) : href ? (
        <Link to={href} aria-label={`Open ${title}`} className={control}>
          {face}
        </Link>
      ) : (
        <div className={control}>{face}</div>
      )}
      <span className="flex flex-wrap items-center gap-1">
        {pairing.shown_to_member ? (
          <Badge type="pill-color" size="sm" color="brand">
            Shown to member
          </Badge>
        ) : null}
        {typeof pairing.confidence === 'number' ? (
          <Badge type="pill-color" size="sm" color="gray">
            {`${Math.round(pairing.confidence * 100)}% match`}
          </Badge>
        ) : null}
        <Badge type="pill-color" size="sm" color="blue">
          {PAIRING_SOURCE[pairing.source] ?? words(pairing.source)}
        </Badge>
        {pairing.decision === 'confirmed' ? (
          <Badge type="pill-color" size="sm" color="success">
            Confirmed
          </Badge>
        ) : null}
        {!pairing.available ? (
          <Badge type="pill-color" size="sm" color="warning">
            Unavailable
          </Badge>
        ) : null}
      </span>
      {pairing.decided_by_name ? (
        <span className="truncate text-xs text-tertiary">By {pairing.decided_by_name}</span>
      ) : null}
      {pairing.rationale?.trim() ? (
        <details className="text-xs">
          <summary className="cursor-pointer font-medium text-brand-secondary">Why</summary>
          <p className="mt-1 text-secondary">{pairing.rationale.trim()}</p>
        </details>
      ) : null}
    </li>
  );
}

/**
 * The garments this one is paired with, read only once its dialog is open.
 * The member's own "Pairs Best With" picks come first and are marked as shown
 * to them; rejected pairings never reach this read.
 */
export function GarmentPairings({
  memberId,
  garmentId,
  onOpenGarment,
}: {
  memberId: string;
  garmentId: string;
  onOpenGarment?: ((garmentId: string) => void) | undefined;
}) {
  const [limit, setLimit] = useState(PAIRINGS_PAGE);
  const [state, setState] = useState<PairingsState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    // "Show more" keeps what is already on screen while the longer page loads.
    setState((previous) =>
      previous.status === 'ready' && previous.data.garment_id === garmentId
        ? { status: 'loading', data: previous.data }
        : { status: 'loading' },
    );
    void (async () => {
      try {
        configureAdminApi();
        const result =
          await memberGarmentPairingsApiV1InternalUsersIdWardrobeGarmentsGarmentIdPairingsGet({
            path: { id: memberId, garment_id: garmentId },
            query: { limit },
          });
        if (!current) return;
        handleUnauthorized(result.response?.status);
        if (result.response?.status === 403) setState({ status: 'forbidden' });
        else if (result.error || !result.data)
          setState({ status: 'failed', detail: failureDetail(result.error) });
        else setState({ status: 'ready', data: result.data });
      } catch (cause) {
        if (current) setState({ status: 'failed', detail: failureDetail(cause) });
      }
    })();
    return () => {
      current = false;
    };
  }, [memberId, garmentId, limit, attempt]);

  const data = state.status === 'ready' || state.status === 'loading' ? state.data : undefined;
  let body: ReactNode;
  if (state.status === 'forbidden')
    body = (
      <Callout tone="warning" title="Your role cannot see this garment’s pairings.">
        Ask an admin to widen your access if you need to see what it is paired with.
      </Callout>
    );
  else if (state.status === 'failed')
    body = (
      <Callout
        tone="error"
        title="The best pairs could not be loaded."
        actions={
          <Button size="sm" color="secondary" onClick={() => setAttempt((count) => count + 1)}>
            Try again
          </Button>
        }
      >
        <FailureText raw={state.detail} />
      </Callout>
    );
  else if (!data)
    body = (
      <ul aria-busy="true" aria-label="Loading best pairs" className={GARMENT_GRID_CLASS}>
        {[0, 1, 2, 3, 4].map((index) => (
          <li
            key={index}
            className="flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-3"
          >
            <span className="aspect-square w-full animate-pulse rounded-lg bg-quaternary/40" />
            <span className="h-3 w-3/4 animate-pulse rounded bg-quaternary/40" />
          </li>
        ))}
      </ul>
    );
  else if (!data.generated)
    body = (
      <p className="text-sm text-tertiary">Pairings haven’t been generated for this garment yet.</p>
    );
  else if (!data.items.length)
    body = <p className="text-sm text-tertiary">No good pairs found yet.</p>;
  else
    body = (
      <>
        <ul aria-label="Best pairs" className={GARMENT_GRID_CLASS}>
          {data.items.map((pairing) => (
            <PairingTile
              key={pairing.pairing_id}
              pairing={pairing}
              memberId={memberId}
              onOpenGarment={onOpenGarment}
            />
          ))}
        </ul>
        {data.items.length < data.total && limit < PAIRINGS_MAX ? (
          <Actions>
            <Button
              size="sm"
              color="secondary"
              isDisabled={state.status === 'loading'}
              onClick={() =>
                setLimit((current) => Math.min(current + PAIRINGS_PAGE, data.total, PAIRINGS_MAX))
              }
            >
              {state.status === 'loading' ? 'Loading…' : 'Show more'}
            </Button>
          </Actions>
        ) : null}
      </>
    );

  return (
    <section aria-label="Best pairs with" className="flex flex-col gap-2">
      <span className="text-sm font-medium text-secondary">
        Best pairs with
        {data?.generated && data.total ? (
          <span className="ml-1 font-normal text-tertiary">
            ({formatCount(data.items.length)} of {formatCount(data.total)})
          </span>
        ) : null}
      </span>
      {body}
    </section>
  );
}

export function GarmentDetail({
  memberId,
  garment,
  onClose,
  onRefresh,
  onOpenGarment,
}: {
  memberId: string;
  garment: GarmentWithProducingJob;
  onClose: () => void;
  onRefresh: () => void | Promise<void>;
  /** Opens another of the member's garments in place; without it a pair links to the wardrobe. */
  onOpenGarment?: ((garmentId: string) => void) | undefined;
}) {
  const alt = garmentAlt(garment);
  const worn = garment.wear_count ?? 0;
  const attributeItems = garmentAttributeItems(garment);
  const [editingTags, setEditingTags] = useState(false);
  const [tagDraft, setTagDraft] = useState(EMPTY_GARMENT_TAGS);
  // What the form started from, so only the fields the operator changed are sent.
  const [tagBaseline, setTagBaseline] = useState(EMPTY_GARMENT_TAGS);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagFailure, setTagFailure] = useState<string>();

  const beginEditTags = () => {
    const prefill = prefillGarmentTags(garment);
    setTagBaseline(prefill);
    setTagDraft(prefill);
    setTagFailure(undefined);
    setEditingTags(true);
  };

  const saveTags = async () => {
    setTagBusy(true);
    setTagFailure(undefined);
    configureAdminApi();
    const result = await editMemberGarmentTagsApiV1InternalUsersIdWardrobeGarmentsGarmentIdTagsPost(
      {
        path: { id: memberId, garment_id: garment.id },
        body: {
          reason: GARMENT_REASON['edit-tags'],
          ...garmentTagChanges(tagDraft, tagBaseline),
        },
      },
    );
    handleUnauthorized(result.response.status);
    setTagBusy(false);
    if (result.response.status === 403) {
      setTagFailure('Your role cannot edit a member’s garment attributes.');
      return;
    }
    if (result.error || !result.data) {
      setTagFailure(errorDetail(result.error, 'Those garment attributes could not be saved.'));
      return;
    }
    setEditingTags(false);
    void Promise.resolve(onRefresh()).catch(() => undefined);
  };

  return (
    <ActionDialog
      title={alt}
      description="Review this garment’s details and take operator actions on the member’s behalf."
      width="lg"
      onClose={onClose}
      footer={
        <Button size="sm" color="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
        <GarmentImage
          url={garment.image_url}
          refPresent={garment.image_ref}
          originalUrl={garment.original_image_url}
          pending={garmentInProgress(garment)}
          alt={alt}
          onRefresh={onRefresh}
        />
        <DetailList
          items={[
            ['Title', garment.title?.trim() || 'Not titled'],
            ['Category', words(garment.category)],
            ['Subcategory', garment.subcategory ? words(garment.subcategory) : 'Not recorded'],
            ['Status', <Status value={garment.status} />],
            ['Enhancement status', <Status value={garment.enhancement_status} />],
            [
              'Times worn',
              <Quantity
                value={formatCount(worn)}
                unit={worn === 1 ? 'wear' : 'wears'}
                raw={worn}
              />,
            ],
            ['Last worn', when(garment.last_worn, 'Never worn')],
            ['Digitised', when(garment.created_at)],
            ['Archived', when(garment.archived_at, 'Not archived')],
          ]}
        />
      </div>
      <GarmentGenerationState garment={garment} />
      {garment.producing_job_id ? (
        <Actions>
          <WorkflowJobButton jobId={garment.producing_job_id} source />
        </Actions>
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-secondary">Attributes</span>
          <button
            type="button"
            aria-label={editingTags ? 'Save attributes' : 'Edit attributes'}
            title={editingTags ? 'Save attributes' : 'Edit attributes'}
            disabled={editingTags && tagBusy}
            onClick={() => (editingTags ? void saveTags() : beginEditTags())}
            className="flex size-6 items-center justify-center rounded-md text-fg-quaternary outline-brand hover:bg-secondary hover:text-fg-secondary focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {editingTags ? <Check className="size-4" /> : <Pencil01 className="size-4" />}
          </button>
        </div>
        {editingTags ? (
          <div className="flex flex-col gap-3">
            {tagFailure ? <FailureText raw={tagFailure} /> : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {Object.keys(EMPTY_GARMENT_TAGS).map((key) => (
                <TextField
                  key={key}
                  label={words(key)}
                  value={tagDraft[key as keyof typeof tagDraft]}
                  onChange={(event) =>
                    setTagDraft((current) => ({ ...current, [key]: event.target.value }))
                  }
                />
              ))}
            </div>
            <Actions>
              <Button
                size="sm"
                color="secondary"
                isDisabled={tagBusy}
                onClick={() => setEditingTags(false)}
              >
                Cancel
              </Button>
            </Actions>
          </div>
        ) : attributeItems.length ? (
          <DetailList items={attributeItems} />
        ) : (
          <span className="text-xs text-quaternary">
            Nothing recorded yet — press the pencil to add attributes.
          </span>
        )}
      </div>
      <GarmentPairings memberId={memberId} garmentId={garment.id} onOpenGarment={onOpenGarment} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <IdentifierCopy label="Garment reference" value={garment.id} />
        {garment.image_ref ? (
          <IdentifierCopy label="Enhanced image storage key" value={garment.image_ref} />
        ) : (
          <IdentifierValue label="Enhanced image storage key" value="None on record" />
        )}
      </div>
      <GarmentOperatorActions
        memberId={memberId}
        garment={garment}
        onDone={() => void Promise.resolve(onRefresh()).catch(() => undefined)}
        onDeleted={() => {
          onClose();
          void Promise.resolve(onRefresh()).catch(() => undefined);
        }}
        excludeActions={EDIT_TAGS_EXCLUDED}
      />
      <Callout tone="neutral" title="Attributes may be empty while enrichment is still running.">
        Attribute values appear above when they are present on the garment. The picture is served by
        a <Term term="signed url">signed URL</Term> that lasts fifteen minutes; refresh it here if
        it has aged out.
      </Callout>
    </ActionDialog>
  );
}

/** Ticks every item on screen, or unticks them once they are all ticked. */
export function SelectAllToggle({
  ids,
  selected,
  onChange,
  noun,
}: {
  ids: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  noun: string;
}) {
  if (!ids.length) return null;
  const all = ids.every((id) => selected.includes(id));
  return (
    <Button
      size="sm"
      color="secondary"
      onClick={() =>
        onChange(
          all
            ? selected.filter((id) => !ids.includes(id))
            : [...selected, ...ids.filter((id) => !selected.includes(id))],
        )
      }
    >
      {all ? 'Unselect all' : `Select all ${formatCount(ids.length)} ${noun}`}
    </Button>
  );
}

function GarmentGallery({
  garments,
  onOpen,
  selection,
}: {
  garments: readonly AdminMemberGarment[];
  onOpen: (id: string) => void;
  selection?: { selected: readonly string[]; onChange: (next: string[]) => void };
}) {
  return (
    <ul className={GARMENT_GRID_CLASS}>
      {garments.map((garment) => (
        <GarmentCard
          key={garment.id}
          garment={garment}
          onOpen={() => onOpen(garment.id)}
          selection={
            selection
              ? {
                  checked: selection.selected.includes(garment.id),
                  onToggle: () =>
                    selection.onChange(
                      selection.selected.includes(garment.id)
                        ? selection.selected.filter((value) => value !== garment.id)
                        : [...selection.selected, garment.id],
                    ),
                }
              : undefined
          }
        />
      ))}
    </ul>
  );
}

/**
 * What an operator can do to one member's garment, and what it costs.
 *
 * Offered per garment rather than as a bulk control because each of these is
 * a decision about a specific thing the member owns. The verbs on offer
 * follow the garment's own state: you cannot archive what is already
 * archived, and showing a button that the API will refuse teaches an operator
 * to ignore refusals.
 *
 * Archive and restore carry the version the list read. The wardrobe service
 * refuses a stale one rather than overwriting a change somebody else made in
 * the meantime, and that refusal surfaces here as a real answer rather than
 * being retried silently.
 */
const GARMENT_CONSEQUENCE: Record<GarmentAction, string> = {
  archive:
    'The member stops seeing this garment in their wardrobe and it leaves their outfits. Reversible.',
  restore: 'The garment returns to the member’s active wardrobe and can be used in outfits again.',
  reprocess: 'Re-runs ingestion on the original photograph. This spends money on the image models.',
  // The route behind Remove archives the garment rather than deleting it.
  remove:
    'Takes the garment out of the member’s wardrobe by archiving it, the same as Archive. Nothing is deleted, and Restore brings it back.',
  requeue:
    'Puts the stored photograph back through the whole live ingestion pipeline. This spends money on every stage of it, and the answer here is a queued job rather than a finished garment.',
  'edit-tags':
    'Saves the attributes you change. Emptying a field clears it, except category and colour hex, which can only be replaced. The garment and its photograph stay in place.',
};

const GARMENT_REASON: Record<GarmentAction, string> = {
  archive: 'Archived the garment from the admin console.',
  restore: 'Restored the garment from the admin console.',
  reprocess: 'Reprocessed the garment from the admin console.',
  remove: 'Removed the garment from the member’s wardrobe from the admin console.',
  requeue: 'Requeued garment ingestion from the admin console.',
  'edit-tags': 'Edited the garment’s tags from the admin console.',
};

type GarmentAction = 'archive' | 'restore' | 'reprocess' | 'remove' | 'requeue' | 'edit-tags';

const GARMENT_ACTION_LABEL: Record<GarmentAction, string> = {
  archive: 'Archive',
  restore: 'Restore',
  reprocess: 'Reprocess',
  remove: 'Remove',
  requeue: 'Requeue',
  'edit-tags': 'Edit tags',
};

/** Shown only while this operator's own request for that verb is in flight. */
const GARMENT_ACTION_BUSY: Record<GarmentAction, string> = {
  archive: 'Archiving…',
  restore: 'Restoring…',
  reprocess: 'Reprocessing…',
  remove: 'Removing…',
  requeue: 'Requeueing…',
  'edit-tags': 'Saving…',
};

/**
 * The verbs a bulk selection can be given, and the ones it cannot.
 *
 * `requeue` is missing on purpose rather than by omission: it is the only verb
 * whose route demands an `Idempotency-Key`, and one key covering forty
 * garments would make a second press collapse the whole batch into the first
 * garment's job. There is no bulk route for it either, so it stays per garment.
 */
type BulkGarmentAction = Exclude<GarmentAction, 'requeue' | 'edit-tags'>;

const BULK_ACTIONS: readonly BulkGarmentAction[] = ['archive', 'restore', 'reprocess', 'remove'];

/** The gallery detail dialog edits tags inline, so its action row excludes it. */
const EDIT_TAGS_EXCLUDED: readonly GarmentAction[] = ['edit-tags'];

/**
 * The refusal codes the bulk route reports per garment, as what to do next.
 *
 * The backend keeps these distinct deliberately - `no_source` needs another
 * photograph from the member and `limited` needs a minute - so this map keeps
 * them distinct too rather than collapsing them into “failed”.
 */
const BULK_FAILURE_REASON: Record<string, string> = {
  not_found: 'No longer in this member’s wardrobe.',
  removed: 'Already removed from this wardrobe.',
  no_source: 'No stored photograph to work from; the member has to upload it again.',
  limited: 'Ingestion is already running for this garment. Try it again in a minute.',
  version_conflict: 'Somebody changed this garment while the batch ran, so it was left alone.',
  invalid_state: 'Already in the state this verb would have put it in.',
  refused: 'Refused by the wardrobe service without naming a reason.',
};

const BULK_PAST: Record<string, string> = {
  archive: 'archived',
  restore: 'restored',
  reprocess: 'reprocessed',
  remove: 'removed',
};

const BULK_GARMENT_REASON: Record<BulkGarmentAction, string> = {
  archive: 'Archived the selected garments from the admin console.',
  restore: 'Restored the selected garments from the admin console.',
  reprocess: 'Reprocessed the selected garments from the admin console.',
  remove: 'Removed the selected garments from the member’s wardrobe from the admin console.',
};

function actionsFor(status: string, enhancementStatus?: string): GarmentAction[] {
  if (status === 'archived')
    return enhancementStatus === 'failed'
      ? ['restore', 'reprocess', 'requeue', 'remove']
      : ['restore', 'remove'];
  if (status === 'active') return ['edit-tags', 'archive', 'reprocess', 'requeue', 'remove'];
  return ['edit-tags', 'reprocess', 'requeue', 'remove'];
}

const EMPTY_GARMENT_TAGS = {
  category: '',
  sub_category: '',
  brand: '',
  size: '',
  colour_primary_hex: '',
  colour_primary_name: '',
  pattern: '',
  fabric: '',
  formality: '',
  season: '',
};

/**
 * The one paid retry used by both the member wardrobe and the fleet failure
 * queue. The key is minted by the dialog that owns the operator decision and
 * held for that dialog's lifetime, so a repeated confirmation carries the
 * same request identity.
 */
async function reprocessGarment(
  memberId: string,
  garmentId: string,
  reason: string,
  idempotencyKey: string,
) {
  configureAdminApi();
  const response =
    await reprocessMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdReprocessPost({
      path: { id: memberId, garment_id: garmentId },
      headers: { 'Idempotency-Key': idempotencyKey },
      body: { reason },
    });
  handleUnauthorized(response.response.status);
  return response;
}

function GarmentOperatorActions({
  memberId,
  garment,
  onDone,
  onDeleted,
  excludeActions,
}: {
  memberId: string;
  garment: AdminMemberGarment;
  onDone: () => void;
  /** Called once the garment is permanently deleted; without it no delete is offered. */
  onDeleted?: () => void;
  /** Verbs to leave off the row - e.g. the gallery detail dialog edits tags
   *  inline next to the attributes it already shows, so it excludes
   *  `edit-tags` here rather than offer the same edit two ways. */
  excludeActions?: readonly GarmentAction[];
}) {
  const [pending, setPending] = useState<GarmentAction>();
  const [busy, setBusy] = useState(false);
  // The verb this operator actually pressed and is waiting on. The in-progress
  // label belongs to that request alone, never to the garment's own state.
  const [inFlight, setInFlight] = useState<GarmentAction>();
  const [failure, setFailure] = useState<string>();
  const [reprocessResult, setReprocessResult] = useState<AdminOnBehalfReprocessResult>();
  const [tags, setTags] = useState(EMPTY_GARMENT_TAGS);
  const [tagBaseline, setTagBaseline] = useState(EMPTY_GARMENT_TAGS);
  const [requeued, setRequeued] = useState<AdminOnBehalfIngestionRequeued>();
  // Minted when the dialog opens and held for as long as it stays open, so an
  // operator who presses Confirm twice pays the pipeline once. A key minted
  // per press would make the second press a second paid run.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const run = async (action: GarmentAction, requestKey = idempotencyKey) => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const path = { id: memberId, garment_id: garment.id };
    if (action === 'edit-tags') {
      const result =
        await editMemberGarmentTagsApiV1InternalUsersIdWardrobeGarmentsGarmentIdTagsPost({
          path,
          body: {
            reason: GARMENT_REASON['edit-tags'],
            ...garmentTagChanges(tags, tagBaseline),
          },
        });
      handleUnauthorized(result.response.status);
      setBusy(false);
      if (result.response.status === 403) {
        setFailure('Your role cannot edit a member’s garment attributes.');
        return;
      }
      if (result.error || !result.data) {
        setFailure(errorDetail(result.error, 'Those garment attributes could not be saved.'));
        return;
      }
      setPending(undefined);
      setTags(EMPTY_GARMENT_TAGS);
      onDone();
      return;
    }
    if (action === 'requeue') {
      const queued =
        await requeueMemberGarmentIngestionApiV1InternalUsersIdWardrobeGarmentsGarmentIdRequeueIngestionPost(
          {
            path,
            body: { reason: GARMENT_REASON.requeue },
            headers: { 'Idempotency-Key': requestKey },
          },
        );
      handleUnauthorized(queued.response.status);
      setBusy(false);
      if (queued.response.status === 403) {
        setFailure('Your role cannot re-run ingestion on a member’s garment.');
        return;
      }
      if (queued.response.status === 400) {
        setFailure(
          'This garment has no stored photo to re-send, so there is nothing to re-ingest. The member has to upload the garment again.',
        );
        return;
      }
      if (queued.error || !queued.data) {
        setFailure(errorDetail(queued.error, 'That garment could not be requeued.'));
        return;
      }
      setRequeued(queued.data);
      setPending(undefined);
      onDone();
      return;
    }
    // The server COALESCEs this to 1, so it is always sent; the client type
    // only calls it optional because the field carries a default. Falling
    // back to 1 is safe rather than a hole in the guard: a version that
    // does not match is refused with a 409, never applied over.
    if (action === 'reprocess') {
      const response = await reprocessGarment(
        memberId,
        garment.id,
        GARMENT_REASON.reprocess,
        requestKey,
      );
      setBusy(false);
      if (response.response.status === 403) {
        setFailure('Your role cannot take that action on a member’s garment.');
        return;
      }
      if (response.error || !response.data) {
        setFailure(
          errorDetail(response.error, 'That garment could not be queued for reprocessing.'),
        );
        return;
      }
      setReprocessResult(response.data);
      setPending(undefined);
      onDone();
      return;
    }
    const version = {
      reason: GARMENT_REASON[action],
      expected_version: garment.archive_version ?? 1,
    };
    const result =
      action === 'archive'
        ? await archiveMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdArchivePost({
            path,
            body: version,
          })
        : action === 'restore'
          ? await restoreMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdRestorePost({
              path,
              body: version,
            })
          : await removeMemberGarmentApiV1InternalUsersIdWardrobeGarmentsGarmentIdDelete({
              path,
              body: { reason: GARMENT_REASON.remove },
            });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot take that action on a member’s garment.');
      return;
    }
    if (result.response.status === 409) {
      setFailure(
        'This garment changed since the page was read, so the change was refused rather than applied over somebody else’s. Refresh and look again.',
      );
      return;
    }
    if (result.error) {
      setFailure(errorDetail(result.error, 'That action could not be completed.'));
      return;
    }
    setPending(undefined);
    onDone();
  };

  const start = (action: GarmentAction) => {
    const requestKey = crypto.randomUUID();
    setIdempotencyKey(requestKey);
    setRequeued(undefined);
    setReprocessResult(undefined);
    setFailure(undefined);
    const prefill = prefillGarmentTags(garment);
    setTagBaseline(prefill);
    setTags(prefill);
    if (action === 'restore' || action === 'reprocess' || action === 'requeue') {
      setInFlight(action);
      void run(action, requestKey).finally(() => setInFlight(undefined));
      return;
    }
    setPending(action);
  };
  // Remove only archives, so once permanent deletion exists it would be a
  // second Archive button next to a real delete.
  const canDelete = Boolean(garmentDeletionOperations().single);
  const offered = actionsFor(garment.status, garment.enhancement_status).filter(
    (action) => !excludeActions?.includes(action) && !(canDelete && action === 'remove'),
  );
  const generationInProgress = garmentInProgress(garment);
  // Reprocess goes through the intake service, which refuses a garment whose
  // ingestion is already running. Requeue does not: it enqueues a fresh
  // ingestion run, which is exactly the heal for one stuck in processing, so
  // it stays available while the garment is processing.
  const blockedWhileRunning = (action: GarmentAction) =>
    generationInProgress && action === 'reprocess';
  return (
    <>
      <Actions>
        {offered.map((action) => {
          const blocked = blockedWhileRunning(action);
          return (
            <span
              key={action}
              className="inline-flex"
              title={blocked ? 'Ingestion is running' : undefined}
            >
              <Button
                size="sm"
                color="secondary"
                isDisabled={blocked || inFlight !== undefined}
                onClick={() => start(action)}
              >
                {inFlight === action ? GARMENT_ACTION_BUSY[action] : GARMENT_ACTION_LABEL[action]}
              </Button>
            </span>
          );
        })}
        {onDeleted ? (
          <DeleteGarmentAction
            memberId={memberId}
            garmentId={garment.id}
            title={garmentTitle(garment.title)}
            onDeleted={onDeleted}
          />
        ) : null}
      </Actions>
      {generationInProgress && offered.includes('reprocess') ? (
        <span className="text-xs text-tertiary">
          Ingestion is running, so Reprocess is unavailable until it finishes. Requeue starts a
          fresh ingestion run if this one looks stuck.
        </span>
      ) : null}
      <GarmentGenerationState garment={garment} />
      {pending ? (
        <ActionDialog
          title={`${pending === 'edit-tags' ? 'Edit tags for' : words(pending)} “${garmentTitle(garment.title)}”`}
          description={GARMENT_CONSEQUENCE[pending]}
          onClose={() => setPending(undefined)}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            {pending === 'edit-tags' ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {Object.keys(EMPTY_GARMENT_TAGS).map((key) => (
                    <TextField
                      key={key}
                      label={words(key)}
                      value={tags[key as keyof typeof tags]}
                      onChange={(event) =>
                        setTags((current) => ({ ...current, [key]: event.target.value }))
                      }
                    />
                  ))}
                </div>
                <Actions>
                  <Button size="sm" color="secondary" onClick={() => setPending(undefined)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    isDisabled={busy}
                    onClick={() => void run(pending)}
                  >
                    {busy ? 'Working…' : 'Save tags'}
                  </Button>
                </Actions>
              </>
            ) : (
              <ConfirmStep
                title={`Confirm garment ${pending}.`}
                body={GARMENT_CONSEQUENCE[pending]}
                confirmLabel={`Confirm ${pending}`}
                busy={busy}
                onCancel={() => setPending(undefined)}
                onConfirm={() => void run(pending)}
              />
            )}
          </div>
        </ActionDialog>
      ) : null}
      {!pending && failure ? <FailureText raw={failure} /> : null}
      {!pending && requeued ? (
        <Callout tone="success" title="Ingestion was queued for this garment.">
          <div className="flex flex-col gap-2">
            <span className="flex flex-col gap-0.5">
              <span className="text-xs text-tertiary">Workflow job</span>
              <EntityRef kind="workflow_job" id={requeued.job_id} label="Garment processing" />
            </span>
            <IdentifierCopy label="Pipeline run" value={requeued.run_id} />
            <IdentifierCopy label="Source image storage key" value={requeued.source_image_ref} />
            <span className="text-xs text-tertiary">
              Queued, not finished. Nothing about this garment has changed yet; the workflow job
              above is where its progress shows.
            </span>
          </div>
        </Callout>
      ) : null}
      {!pending && reprocessResult ? (
        <Callout tone="success" title="Reprocessing was queued for this garment.">
          The retry was accepted and queued; it has not finished yet. The current garment state will
          remain visible until the pipeline writes a later result.
        </Callout>
      ) : null}
    </>
  );
}

/**
 * One garment verb, applied to everything the operator ticked.
 *
 * The report is the point of this control. The route runs each garment in its
 * own savepoint and answers with the ids that worked and the ones that did
 * not, each with the refusal that applied, so this renders that list rather
 * than a count: an operator told “three failed” out of forty has to find
 * the three by hand, and the reason is what decides whether to ask the member
 * for a photograph or simply wait a minute.
 *
 * The list behind it is only refreshed when the report is dismissed. Refreshing on
 * success would unmount this component the moment a bulk remove emptied the
 * selection, taking the report with it.
 */
function GarmentBulkActions({
  memberId,
  garmentIds,
  titles,
  onDone,
  onClear,
  onSetMade,
}: {
  memberId: string;
  garmentIds: readonly string[];
  /** Garment titles by id, from the page, so the report names what it refused. */
  titles?: ReadonlyMap<string, string>;
  onDone: () => void;
  onClear: () => void;
  onSetMade: () => void;
}) {
  const [pending, setPending] = useState<BulkGarmentAction>();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [report, setReport] = useState<AdminOnBehalfBulkResult>();

  const run = async (action: BulkGarmentAction) => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await bulkMemberGarmentsApiV1InternalUsersIdWardrobeGarmentsBulkPost({
      path: { id: memberId },
      body: { action, reason: BULK_GARMENT_REASON[action], garment_ids: [...garmentIds] },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setPending(undefined);
    if (result.response.status === 403) {
      setFailure(
        action === 'remove'
          ? 'Removing a member’s garments needs a destructive-actions grant, which your role does not hold. The other three verbs are still open to you.'
          : 'Your role cannot take that action on a member’s garments.',
      );
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That batch could not be applied.'));
      return;
    }
    setReport(result.data);
  };

  const clear = () => {
    setPending(undefined);
    if (report) {
      setReport(undefined);
      onClear();
      onDone();
      return;
    }
    onClear();
  };

  return (
    <Callout
      tone="neutral"
      title={`${formatCount(garmentIds.length)} garment(s) selected.`}
      actions={
        <Button size="sm" color="secondary" onClick={clear}>
          Clear selection
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <span>
          One verb is applied to every garment ticked. Each one is attempted on its own, so a
          garment that refuses is reported against itself rather than abandoning the rest of the
          batch. One audit row covers the whole batch, recorded against your operator account with
          this member named as the subject.
        </span>
        {failure ? <FailureText raw={failure} /> : null}
        {report ? (
          <Callout
            tone={report.failed.length ? 'warning' : 'success'}
            title={`${formatCount(report.succeeded.length)} of ${formatCount(report.requested)} garment(s) ${BULK_PAST[report.action] ?? report.action}.`}
            actions={
              <Button size="sm" color="secondary" onClick={clear}>
                Dismiss results
              </Button>
            }
          >
            {report.failed.length ? (
              <div className="flex flex-col gap-3">
                <span>
                  These {formatCount(report.failed.length)} garment(s) were not touched, and the
                  reasons are not interchangeable:
                </span>
                <ul className="flex flex-col gap-2">
                  {report.failed.map((item) => (
                    <li key={item.garment_id} className="flex flex-col gap-1">
                      <EntityRef
                        kind="garment"
                        id={item.garment_id}
                        memberId={memberId}
                        label={titles?.get(item.garment_id)}
                      />
                      <span className="text-xs">
                        {BULK_FAILURE_REASON[item.error_code] ?? item.error_code}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <span>Every garment in the selection was accepted.</span>
            )}
          </Callout>
        ) : null}
        {pending ? (
          <Actions>
            <span className="self-center text-sm text-secondary">
              {GARMENT_CONSEQUENCE[pending]}
            </span>
            <Button
              size="sm"
              color="secondary"
              isDisabled={busy}
              onClick={() => setPending(undefined)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color={pending === 'remove' ? 'primary-destructive' : 'primary'}
              isDisabled={busy}
              onClick={() => void run(pending)}
            >
              {busy ? 'Working…' : `Confirm ${pending} of ${formatCount(garmentIds.length)}`}
            </Button>
          </Actions>
        ) : (
          <Actions>
            {BULK_ACTIONS.filter(
              (action) => !(action === 'remove' && garmentDeletionOperations().bulk),
            ).map((action) => (
              <Button
                key={action}
                size="sm"
                color="secondary"
                isDisabled={busy}
                onClick={() =>
                  action === 'archive' || action === 'remove'
                    ? setPending(action)
                    : void run(action)
                }
              >
                {action} selection
              </Button>
            ))}
            <MakeGarmentSet memberId={memberId} garmentIds={garmentIds} onMade={onSetMade} />
          </Actions>
        )}
      </div>
    </Callout>
  );
}

function GarmentResults({
  memberId,
  memberName,
  page,
  receivedAt,
  view,
  status,
  filters,
  offset,
  openId,
  onRefresh,
  onPage,
  onOpen,
  onClearFilters,
  onSetMade,
}: {
  memberId: string;
  memberName?: string | null | undefined;
  page: AdminMemberGarmentPage;
  receivedAt: number;
  view: GarmentView;
  status: '' | GarmentStatus;
  filters: FacetFilters;
  /** Where the filtered view is paged to; the unfiltered view is paged by the server. */
  offset: number;
  openId: string | undefined;
  onRefresh: () => void | Promise<void>;
  onPage: (offset: number) => void;
  onOpen: (id: string | undefined) => void;
  onClearFilters: () => void;
  onSetMade: () => void;
}) {
  const [ticked, setTicked] = useState<string[]>([]);
  const filtering = Object.keys(filters).length > 0;
  const matched = filtering
    ? page.items.filter((garment) => garmentMatches(garment, filters))
    : page.items;
  const shown = filtering ? matched.slice(offset, offset + PAGE) : matched;
  // The dialog reads the garment out of the live page rather than holding its
  // own copy, so refreshing the signatures updates the open dialog in place.
  const open = page.items.find((garment) => garment.id === openId);
  // Narrowed to what is on screen rather than remembered across pages and
  // filters: a batch applied to garments the operator can no longer see is a
  // decision they cannot check before taking it.
  const selected = ticked.filter((id) => shown.some((garment) => garment.id === id));
  const unscanned = filtering ? Math.max(0, page.total - page.items.length) : 0;
  const titles = new Map(page.items.map((garment) => [garment.id, garmentAlt(garment)]));
  const refresh = () => void Promise.resolve(onRefresh()).catch(() => undefined);
  const emptyHint = status
    ? `This member has no garments with status “${words(status)}”. Choose “All statuses” to see the whole wardrobe.`
    : 'Nothing has been digitised into this wardrobe yet.';
  return (
    <SignatureExpiryBoundary signedAt={receivedAt} onRefresh={onRefresh}>
      {unscanned ? (
        <Callout
          tone="warning"
          title={`These filters looked at the ${formatCount(page.items.length)} most recent garments, not all ${formatCount(page.total)} on record.`}
        >
          The garment read returns at most {FILTER_SCAN} garments in one request, so{' '}
          {formatCount(unscanned)} older garment(s) were never examined against these filters.
          Narrowing Status brings more of them into range.
        </Callout>
      ) : null}
      {filtering && page.items.length ? (
        <p className="text-sm text-tertiary">
          <Quantity value={formatCount(matched.length)} raw={matched.length} /> of the{' '}
          <Quantity value={formatCount(page.items.length)} raw={page.items.length} /> garment(s)
          read match these filters. The composition charts count active garments only, so a chart
          figure and this one agree only when Status is set to Active.
        </p>
      ) : null}
      {/* Outside the list below so a delete that empties it keeps its report on screen. */}
      <div className="flex flex-wrap items-center gap-2">
        <SelectAllToggle
          ids={shown.map((garment) => garment.id)}
          selected={selected}
          onChange={setTicked}
          noun={shown.length === 1 ? 'garment' : 'garments'}
        />
        <BulkGarmentDeletion
          memberId={memberId}
          memberName={memberName}
          selectedIds={selected}
          // With a status filter the total counts only that status, while
          // “delete all” removes every garment, so it is offered unfiltered only.
          total={status ? 0 : page.total}
          titles={titles}
          onClearSelection={() => setTicked([])}
          onDeleted={refresh}
        />
      </div>
      {!page.items.length ? (
        <Absent title="No garments recorded yet.">{emptyHint}</Absent>
      ) : filtering && !matched.length ? (
        // A legitimate answer, and deliberately unlike the failed read above
        // it: no alert, no retry, and a way back out of the narrowing.
        <Callout
          tone="neutral"
          title="No garments match these filters."
          actions={
            <Button size="sm" color="secondary" onClick={onClearFilters}>
              Clear filters
            </Button>
          }
        >
          The read succeeded and returned {formatCount(page.items.length)} garment(s); none of them
          carries every value selected in the charts above.
        </Callout>
      ) : (
        <>
          {selected.length ? (
            <GarmentBulkActions
              memberId={memberId}
              garmentIds={selected}
              titles={titles}
              onClear={() => setTicked([])}
              onDone={refresh}
              onSetMade={onSetMade}
            />
          ) : null}
          {view === 'gallery' ? (
            <GarmentGallery
              garments={shown}
              onOpen={onOpen}
              selection={{ selected, onChange: setTicked }}
            />
          ) : (
            <RecordTable
              noun="garments"
              memberId={memberId}
              rowKind="garment"
              records={shown}
              columns={[
                'title',
                'category',
                'subcategory',
                'status',
                'enhancement_status',
                'wear_count',
                'last_worn',
                'created_at',
              ]}
              emptyHint={emptyHint}
              selection={{
                selected,
                onChange: setTicked,
                idFor: (record) => String(record.id),
                itemNoun: 'garment',
              }}
              rowActions={(record) => (
                <GarmentOperatorActions
                  memberId={memberId}
                  garment={record as unknown as AdminMemberGarment}
                  onDone={refresh}
                  onDeleted={() => {
                    setTicked((current) => current.filter((value) => value !== String(record.id)));
                    refresh();
                  }}
                />
              )}
            />
          )}
        </>
      )}
      {shown.length ? (
        <Pager
          offset={filtering ? offset : page.offset}
          limit={filtering ? PAGE : page.limit}
          total={filtering ? matched.length : page.total}
          onPage={onPage}
        />
      ) : null}
      {open ? (
        <GarmentDetail
          key={open.id}
          memberId={memberId}
          garment={open}
          onClose={() => onOpen(undefined)}
          onRefresh={onRefresh}
          onOpenGarment={onOpen}
        />
      ) : openId ? (
        <LinkedGarment
          key={openId}
          memberId={memberId}
          garmentId={openId}
          onOpen={onOpen}
          onRefreshList={onRefresh}
        />
      ) : null}
    </SignatureExpiryBoundary>
  );
}

/**
 * A linked garment that is not on the loaded page - older, under another
 * status filter, or deleted. It is read on its own by id, which the wardrobe
 * route still scopes to this member.
 */
function LinkedGarment({
  memberId,
  garmentId,
  onOpen,
  onRefreshList,
}: {
  memberId: string;
  garmentId: string;
  onOpen: (id: string | undefined) => void;
  onRefreshList: () => void | Promise<void>;
}) {
  const source = useInsight<AdminMemberGarmentPage>(
    () =>
      memberWardrobeApiV1InternalUsersIdWardrobeGet({
        path: { id: memberId },
        query: { garment_id: garmentId, limit: 1, offset: 0 },
      }),
    [memberId, garmentId],
  );
  return (
    <InsightState source={source} subject="the linked garment">
      {(page) => {
        const garment = page.items.find((item) => item.id === garmentId);
        if (garment)
          return (
            <GarmentDetail
              memberId={memberId}
              garment={garment}
              onClose={() => onOpen(undefined)}
              onRefresh={async () => {
                await source.refresh({ preserve: true });
                await onRefreshList();
              }}
              onOpenGarment={onOpen}
            />
          );
        return (
          <Callout
            tone="neutral"
            title="The linked garment was not found in this member’s wardrobe."
            actions={
              <Button size="sm" color="secondary" onClick={() => onOpen(undefined)}>
                Dismiss
              </Button>
            }
          >
            It may have been deleted, or it belongs to another member.
            <CopyableId value={garmentId} label="garment id" full />
          </Callout>
        );
      }}
    </InsightState>
  );
}

/** The search parameter naming the garment whose detail is open. */
export const GARMENT_PARAM = 'garment';
/** The wardrobe search, kept in the address beside the open garment. */
export const SEARCH_PARAM = 'wq';
/** The search parameter naming the outfit whose detail is open. */
export const OUTFIT_PARAM = 'outfit';

function WardrobeGarments({
  id,
  memberName,
  filters,
  onClearFacet,
  onClearFilters,
  onSetMade,
}: {
  id: string;
  memberName?: string | null | undefined;
  filters: FacetFilters;
  onClearFacet: (key: string) => void;
  onClearFilters: () => void;
  onSetMade: () => void;
}) {
  // Defaults to every status: a wardrobe panel that silently hid archived
  // garments would misreport the member's closet.
  const [status, setStatus] = useState<'' | GarmentStatus>('');
  const [offset, setOffset] = useState(0);
  // The open garment lives in the address, so a link to one garment - from a
  // workflow job, an import, any reference to it - opens it on arrival.
  const [params, setParams] = useSearchParams();
  const openId = params.get(GARMENT_PARAM) ?? undefined;
  const setOpenId = (garmentId: string | undefined) => {
    const next = new URLSearchParams(params);
    if (garmentId) next.set(GARMENT_PARAM, garmentId);
    else next.delete(GARMENT_PARAM);
    setParams(next, { replace: true });
  };
  // The search lives in the address too, so a searched closet can be shared or reloaded.
  const query = (params.get(SEARCH_PARAM) ?? '').trim();
  const [draft, setDraft] = useState(query);
  useEffect(() => setDraft(query), [query]);
  const search = (value: string) => {
    const next = new URLSearchParams(params);
    const words = value.trim().slice(0, 200);
    if (words) next.set(SEARCH_PARAM, words);
    else next.delete(SEARCH_PARAM);
    setParams(next, { replace: true });
    setOffset(0);
  };
  // An operator reviewing a closet is looking at clothes, so pictures are the
  // default; the table stays one click away for the full column set.
  const [view, setView] = useState<GarmentView>('gallery');
  const filtering = Object.keys(filters).length > 0;
  // The server cannot filter on an attribute, so a filtered view reads as much
  // of the wardrobe as one request allows and narrows it here. Paging within
  // that result is local, which is why the read offset is pinned to zero.
  const readLimit = filtering ? FILTER_SCAN : PAGE;
  const readOffset = filtering ? 0 : offset;
  const signature = Object.entries(filters)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  useEffect(() => setOffset(0), [signature]);
  const source = useInsight<AdminMemberGarmentPage>(
    () =>
      memberWardrobeApiV1InternalUsersIdWardrobeGet({
        path: { id },
        query: {
          limit: readLimit,
          offset: readOffset,
          ...(status ? { status } : {}),
          ...(query ? { q: query } : {}),
        },
      }),
    [id, status, readOffset, readLimit, query],
  );
  return (
    <Section
      title="Garments"
      description="Every garment on record for this member. Selecting a bucket in the charts above narrows this grid; the selection is kept in the page address, so a narrowed view can be shared or reloaded."
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2" role="group" aria-label="Garment view">
            {(['gallery', 'table'] as GarmentView[]).map((option) => (
              <Button
                key={option}
                size="sm"
                color={view === option ? 'primary' : 'secondary'}
                aria-pressed={view === option}
                onClick={() => setView(option)}
              >
                {option === 'gallery' ? 'Gallery' : 'Table'}
              </Button>
            ))}
          </div>
          <Button size="sm" color="secondary" onClick={() => void source.refresh()}>
            Refresh links
          </Button>
        </div>
      }
    >
      <Filters>
        <form
          role="search"
          aria-label="Search this wardrobe"
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            search(draft);
          }}
        >
          <TextField
            label="Search"
            type="search"
            placeholder="navy linen shirt, gym, party…"
            maxLength={200}
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <Button type="submit" size="sm" color="secondary">
            Search
          </Button>
          {query ? (
            <Button size="sm" color="link-gray" onClick={() => search('')}>
              Clear search
            </Button>
          ) : null}
        </form>
        <SelectField
          label="Status"
          value={status}
          onChange={(event) => {
            setStatus(event.currentTarget.value as '' | GarmentStatus);
            setOffset(0);
          }}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' },
            { value: 'pending_review', label: 'Pending review' },
          ]}
        />
        {filtering ? (
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Chart filters"
          >
            {Object.entries(filters).map(([key, value]) => (
              <button
                key={key}
                type="button"
                onClick={() => onClearFacet(key)}
                aria-label={`Remove the ${facetLabel(key)} filter “${filterLabel(key, value)}”`}
                className="flex cursor-pointer items-center gap-1.5 rounded-full border border-brand bg-brand-primary px-2.5 py-1 text-xs text-brand-secondary outline-brand focus-visible:outline-2"
              >
                <span className="capitalize">
                  {facetLabel(key)}: {filterLabel(key, value)}
                </span>
                <XClose className="size-3.5" />
              </button>
            ))}
            <Button size="sm" color="link-gray" onClick={onClearFilters}>
              Clear filters
            </Button>
          </div>
        ) : null}
      </Filters>
      <InsightState source={source} subject="this member's garments">
        {(page, receivedAt) => (
          <>
            <WardrobeSearchNote search={page.search} />
            <GarmentResults
              memberId={id}
              memberName={memberName}
              page={page}
              receivedAt={receivedAt}
              view={view}
              status={status}
              filters={filters}
              offset={offset}
              openId={openId}
              onRefresh={() => source.refresh({ preserve: true })}
              onPage={setOffset}
              onOpen={setOpenId}
              onClearFilters={onClearFilters}
              onSetMade={onSetMade}
            />
          </>
        )}
      </InsightState>
    </Section>
  );
}

/** How a wardrobe search was answered: the member's own search, or a match on the words. */
function WardrobeSearchNote({ search }: { search?: AdminMemberGarmentPage['search'] }) {
  if (!search) return null;
  return (
    <div
      aria-label="How the search was answered"
      className="mb-3 flex flex-col gap-1 text-sm text-secondary"
    >
      {search.mode === 'member_search' ? (
        <p>
          Results for &ldquo;{search.query}&rdquo; as the member&rsquo;s own Closet search finds
          them, best match first. It covers active garments; pick Archived or Pending review to
          match those by name instead.
        </p>
      ) : (
        <p>
          Garments whose name, type or description contain every word of &ldquo;{search.query}
          &rdquo;.
        </p>
      )}
      {search.reading?.length ? (
        <p className="text-xs text-tertiary">Read as: {search.reading.join(' · ')}</p>
      ) : null}
    </div>
  );
}

/** The media service requires the digest before it will issue a signed upload URL. */
async function sha256(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

type GarmentUploadFailure = 'create' | 'transfer' | 'complete' | 'queue';

const GARMENT_UPLOAD_FAILURE: Record<GarmentUploadFailure, string> = {
  create: 'The upload could not be created.',
  transfer: 'The file could not be transferred.',
  complete: 'The upload could not be completed.',
  queue: 'The garment could not be queued.',
};

/**
 * One explicit attempt through the media-upload handshake and into garment
 * ingestion. No step retries: after any refusal the operator decides whether
 * a new attempt is appropriate.
 */
function UploadGarment({ memberId }: { memberId: string }) {
  const [file, setFile] = useState<File>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{
    step: GarmentUploadFailure;
    detail?: string;
  }>();
  const [queued, setQueued] = useState<OperatorGarmentQueued>();
  const ready = Boolean(file && reason.trim().length >= 3);

  const fail = (step: GarmentUploadFailure, detail?: unknown) => {
    setFailure({
      step,
      ...(detail === undefined ? {} : { detail: errorDetail(detail, '') || undefined }),
    });
    setBusy(false);
  };

  const upload = async () => {
    const trimmedReason = reason.trim();
    if (!file || trimmedReason.length < 3) return;
    setBusy(true);
    setFailure(undefined);
    setQueued(undefined);
    configureAdminApi();

    let checksum: string;
    try {
      checksum = await sha256(file);
    } catch (error) {
      fail('create', error);
      return;
    }

    let created: Awaited<ReturnType<typeof createMediaUploadApiV1MediaUploadsPost>>;
    try {
      created = await createMediaUploadApiV1MediaUploadsPost({
        body: {
          purpose: 'wardrobe_garment',
          subject_user_id: memberId,
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          checksum_sha256: checksum,
          idempotency_key: `admin-garment-upload:${crypto.randomUUID()}`,
        },
      });
    } catch (error) {
      fail('create', error);
      return;
    }
    handleUnauthorized(created.response.status);
    if (!created.data?.upload_url) {
      fail('create', 'error' in created ? created.error : undefined);
      return;
    }

    let transferred: Response;
    try {
      transferred = await fetch(created.data.upload_url, {
        method: 'PUT',
        headers: created.data.upload_headers,
        body: file,
      });
    } catch (error) {
      fail('transfer', error);
      return;
    }
    if (!transferred.ok) {
      fail('transfer', `Storage returned HTTP ${transferred.status}.`);
      return;
    }

    let completed: Awaited<ReturnType<typeof completeMediaUploadApiV1MediaUploadsIdCompletePost>>;
    try {
      completed = await completeMediaUploadApiV1MediaUploadsIdCompletePost({
        path: { id: created.data.id },
        body: { etag: transferred.headers.get('etag') },
      });
    } catch (error) {
      fail('complete', error);
      return;
    }
    handleUnauthorized(completed.response.status);
    if (!completed.data) {
      fail('complete', 'error' in completed ? completed.error : undefined);
      return;
    }

    let garment: Awaited<
      ReturnType<typeof createMemberGarmentApiV1InternalWardrobeMembersMemberIdGarmentsPost>
    >;
    try {
      garment = await createMemberGarmentApiV1InternalWardrobeMembersMemberIdGarmentsPost({
        path: { member_id: memberId },
        body: { media_upload_id: created.data.id, reason: trimmedReason },
      });
    } catch (error) {
      fail('queue', error);
      return;
    }
    handleUnauthorized(garment.response.status);
    if (!garment.data) {
      fail('queue', 'error' in garment ? garment.error : undefined);
      return;
    }
    setQueued(garment.data);
    setBusy(false);
  };

  return (
    <Section
      title="Upload garment"
      description="Upload one garment photograph for this member. A successful request queues ingestion; the garment will not appear in the wardrobe until processing finishes."
    >
      <div className="flex flex-col gap-4">
        {failure ? (
          <FailureText
            raw={`${GARMENT_UPLOAD_FAILURE[failure.step]}${failure.detail ? ` ${failure.detail}` : ''}`}
          />
        ) : null}
        {queued ? (
          <Callout tone="success" title="The garment was queued for ingestion.">
            It is not in the member’s wardrobe yet. Processing must finish first.
            <div className="flex flex-col gap-2 pt-2">
              <span className="flex flex-col gap-0.5">
                <span className="text-xs text-tertiary">Garment</span>
                <EntityRef
                  kind="garment"
                  id={queued.garment_id}
                  memberId={memberId}
                  label="New garment (processing)"
                />
              </span>
              <IdentifierValue label="Pipeline stage" value={words(queued.pipeline_stage)} />
            </div>
          </Callout>
        ) : null}
        <TextField
          label="Garment photo"
          type="file"
          accept="image/*"
          required
          disabled={busy}
          onChange={(event) => setFile(event.target.files?.[0])}
        />
        <TextAreaField
          label="Reason for uploading the garment"
          required
          minLength={3}
          maxLength={1000}
          disabled={busy}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Required. This explanation is recorded with the operator action."
        />
        <Actions>
          <Button
            size="sm"
            color="primary"
            isDisabled={!ready || busy}
            onClick={() => void upload()}
          >
            {busy ? 'Uploading…' : 'Upload garment'}
          </Button>
        </Actions>
      </div>
    </Section>
  );
}

function WardrobePanel({ id, memberName }: { id: string; memberName?: string | null }) {
  // The selection lives in the URL so a narrowed wardrobe is a link. Every
  // other parameter on the page - the tab, above all - is carried through.
  const [params, setParams] = useSearchParams();
  const filters = readFacetFilters(params);
  const write = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };
  const selectFacet = (key: string, value: string | null) =>
    write((next) => {
      next.delete(`${FACET_PARAM}${key}`);
      if (value !== null) next.set(`${FACET_PARAM}${key}`, value);
    });
  const clearFilters = () =>
    write((next) => {
      for (const key of Object.keys(filters)) next.delete(`${FACET_PARAM}${key}`);
    });
  const [setsRevision, setSetsRevision] = useState(0);
  return (
    <div className="flex flex-col gap-6">
      <UploadGarment memberId={id} />
      <Section
        title="Composition"
        description="Aggregated server-side over the stored garment attribute document. The four figures count every garment on record, archived ones included; the attribute breakdowns beneath them count only the active garments, so the two sets of totals are not meant to agree. Selecting a bucket filters the garments below to it."
      >
        <WardrobeSummary id={id} filters={filters} onSelectFacet={selectFacet} />
      </Section>
      <WardrobeGarments
        id={id}
        memberName={memberName}
        filters={filters}
        onClearFacet={(key) => selectFacet(key, null)}
        onClearFilters={clearFilters}
        onSetMade={() => setSetsRevision((current) => current + 1)}
      />
      <MemberGarmentSets memberId={id} revision={setsRevision} />
    </div>
  );
}

/* -------------------------------------------------------------- Drive import */

/** An import stops changing once it reaches one of these. */
const TERMINAL_IMPORT_STATUSES = new Set(['completed', 'partial', 'failed']);

export function importIsTerminal(status: string | undefined): boolean {
  return status !== undefined && TERMINAL_IMPORT_STATUSES.has(status);
}

/** Where one import's items and their processing stages are shown. */
export function wardrobeImportHref(importId: string): string {
  return `/wardrobe-imports/${encodeURIComponent(importId)}`;
}

const RECENT_IMPORTS = 10;

function DriveImportPanel({ id }: { id: string }) {
  const [manifest, setManifest] = useState('');
  const [label, setLabel] = useState('');
  const [folderLink, setFolderLink] = useState('');
  const [foldersFromSubfolders, setFoldersFromSubfolders] = useState(false);
  /** Whether Drive folder names are sent to the model as garment-type hints. */
  const [categoryFromFolderNames, setCategoryFromFolderNames] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailure, setSubmitFailure] = useState<string>();
  const [accepted, setAccepted] = useState<WardrobeBulkImportCreated>();
  const [importId, setImportId] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusFailure, setStatusFailure] = useState<string>();
  const [importStatus, setImportStatus] = useState<WardrobeBulkImportStatus>();
  const [recent, setRecent] = useState<WardrobeBulkImportSummary[]>();
  const [recentTotal, setRecentTotal] = useState<number>();
  const [recentFailure, setRecentFailure] = useState<string>();

  const selection = buildSelection({
    folderLink,
    manifest,
    foldersFromSubfolders,
    categoryFromFolderNames,
  });
  // Shown once a submit was tried, and kept current as the operator fixes them.
  const problems = attempted ? selection.problems : [];

  const loadStatus = useCallback(async (requestedId: string, preserve = false) => {
    if (!requestedId) return;
    if (!preserve) {
      setLoadingStatus(true);
      setStatusFailure(undefined);
    }
    configureAdminApi();
    const result = await getWardrobeBulkImportApiV1InternalWardrobeBulkImportsImportIdGet({
      path: { import_id: requestedId },
    });
    handleUnauthorized(result.response.status);
    if (!preserve) setLoadingStatus(false);
    if (result.error || !result.data) {
      if (!preserve) {
        setImportStatus(undefined);
        setStatusFailure(errorDetail(result.error, 'That wardrobe import could not be loaded.'));
      }
      return;
    }
    setImportStatus(result.data);
  }, []);

  // The member's own imports, newest first. The API filters by member, so an
  // import queued in an earlier visit - or by somebody else - is found here
  // rather than lost with the id nobody wrote down.
  const loadRecent = useCallback(async () => {
    configureAdminApi();
    const result = await listWardrobeBulkImportsApiV1InternalWardrobeBulkImportsGet({
      query: { member_id: id, limit: RECENT_IMPORTS },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setRecentFailure(errorDetail(result.error, 'Recent imports could not be loaded.'));
      return undefined;
    }
    setRecentFailure(undefined);
    setRecent(result.data.items);
    setRecentTotal(result.data.total);
    return result.data.items;
  }, [id]);

  // On opening the tab: list the member's imports and follow the latest one.
  useEffect(() => {
    let live = true;
    void loadRecent().then((items) => {
      const latest = items?.[0];
      if (!live || !latest) return;
      setImportId((current) => current || latest.import_id);
      void loadStatus(latest.import_id);
    });
    return () => {
      live = false;
    };
  }, [loadRecent, loadStatus]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);
    setSubmitFailure(undefined);
    setAccepted(undefined);
    if (selection.problems.length) return;
    setSubmitting(true);
    configureAdminApi();
    const { drive_folder_link, ...chosen } = selection.body;
    const result = await createWardrobeBulkImportApiV1InternalWardrobeBulkImportsPost({
      body: {
        ...(drive_folder_link ? { drive_folder_link } : {}),
        members: [
          {
            member_id: id,
            ...chosen,
            ...(label.trim() ? { label: label.trim() } : {}),
          },
        ],
      },
    });
    handleUnauthorized(result.response.status);
    setSubmitting(false);
    if (result.error || !result.data) {
      setSubmitFailure(rejectionDetail(result.error, 'The wardrobe import could not be queued.'));
      return;
    }
    setAttempted(false);
    setAccepted(result.data);
    setImportId(result.data.import_id);
    setImportStatus(undefined);
    setStatusFailure(undefined);
    // Straight away, not on a click: the status is what the operator queued
    // it to see, and it is live-refreshed from here until it is terminal.
    void loadStatus(result.data.import_id);
    void loadRecent();
  };

  const refreshStatus = () => loadStatus(importId.trim());
  const following = importStatus && importStatus.import_id === importId.trim();
  const inProgress = Boolean(following && !importIsTerminal(importStatus?.status));
  const recentInProgress = Boolean(recent?.some((item) => !importIsTerminal(item.status)));
  useLiveRefresh(
    async () => {
      await Promise.all([
        inProgress ? loadStatus(importId.trim(), true) : undefined,
        recentInProgress ? loadRecent() : undefined,
      ]);
    },
    // Polling stops once everything on screen is terminal: a finished
    // import does not change, so re-reading it is only load.
    submitting || loadingStatus || !(inProgress || recentInProgress),
  );

  return (
    <Section
      title="Drive import"
      description="Queue a Drive folder for this member’s wardrobe and follow its progress. The member’s recent imports are listed below; any import can also be loaded by id."
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-sm font-semibold text-primary">
              Import wardrobe from Google Drive
            </h3>
            <p className="text-sm text-tertiary">
              Without a manifest, every image in the folder and its nested subfolders is ingested.
            </p>
          </div>
          <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
            {submitFailure ? (
              <Callout tone="error" title="The import was not queued.">
                {submitFailure}
              </Callout>
            ) : null}
            {problems.length ? (
              <Callout tone="error" title="Fix these before queueing the import.">
                <ul className="flex list-disc flex-col gap-1 pl-5">
                  {problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Callout>
            ) : null}
            {accepted && !importStatus ? (
              <Callout tone="success" title="The wardrobe import is queued, not yet imported.">
                <div className="flex flex-col gap-2">
                  <p>
                    A durable worker will walk the Drive folder and start garment ingestion. Its
                    status is shown below and follows the import until it finishes.
                  </p>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-xs text-tertiary">Wardrobe import</span>
                    <EntityRef
                      kind="wardrobe_import"
                      id={accepted.import_id}
                      label="Drive import (queued)"
                    />
                  </span>
                  <Actions>
                    <Button
                      size="sm"
                      color="secondary"
                      href={wardrobeImportHref(accepted.import_id)}
                    >
                      Open import items
                    </Button>
                  </Actions>
                </div>
              </Callout>
            ) : null}
            <TextField
              label="Drive folder link"
              hint="Needed unless you list Drive file links or ids only."
              value={folderLink}
              onChange={(event) => setFolderLink(event.target.value)}
            />
            <ManifestField value={manifest} onChange={setManifest} selection={selection} />
            <SubfolderFoldersField
              checked={foldersFromSubfolders}
              onChange={setFoldersFromSubfolders}
            />
            <CategoryFromFolderNamesField
              checked={categoryFromFolderNames}
              onChange={setCategoryFromFolderNames}
            />
            <TextField
              label="Label (optional)"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
            <Actions>
              <Button type="submit" size="md" isDisabled={submitting}>
                {submitting ? 'Queueing…' : 'Queue import'}
              </Button>
            </Actions>
          </form>
        </div>
        <div className="flex flex-col gap-4 border-t border-secondary pt-6">
          <RecentDriveImports
            imports={recent}
            total={recentTotal}
            failure={recentFailure}
            selected={importId.trim()}
            onSelect={(selectedId) => {
              setImportId(selectedId);
              setImportStatus(undefined);
              void loadStatus(selectedId);
            }}
          />
          <div>
            <h3 className="text-sm font-semibold text-primary">Import status</h3>
            <p className="text-sm text-tertiary">
              The latest import is followed automatically until it finishes. Load any other import
              by id, including one for another member.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-end gap-3 sm:flex-row">
              <div className="w-full flex-1">
                <TextField
                  label="Import id"
                  value={importId}
                  onChange={(event) => {
                    setImportId(event.target.value);
                    setImportStatus(undefined);
                  }}
                />
              </div>
              <Button
                size="md"
                color="secondary"
                isDisabled={loadingStatus || !importId.trim()}
                onClick={() => void refreshStatus()}
              >
                {loadingStatus ? 'Loading…' : importStatus ? 'Refresh status' : 'Load status'}
              </Button>
            </div>
            {statusFailure ? (
              <Callout tone="error" title="Import status could not be loaded.">
                <FailureText raw={statusFailure} />
              </Callout>
            ) : null}
            {importStatus ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="flex flex-col gap-0.5">
                    <span className="text-xs text-tertiary">Wardrobe import</span>
                    <EntityRef
                      kind="wardrobe_import"
                      id={importStatus.import_id}
                      label={`Drive import of ${when(importStatus.created_at)}`}
                    />
                  </span>
                  <Button
                    size="sm"
                    color="secondary"
                    href={wardrobeImportHref(importStatus.import_id)}
                  >
                    Open import items
                  </Button>
                </div>
                <DetailList
                  items={[
                    ['Status', <Status value={importStatus.status} />],
                    ['Created', when(importStatus.created_at)],
                    ...(importStatus.completed_at ||
                    ['completed', 'partial', 'failed'].includes(importStatus.status)
                      ? ([['Completed', when(importStatus.completed_at, 'Not recorded')]] as Array<
                          [ReactNode, ReactNode]
                        >)
                      : []),
                    ['Drive folder', importStatus.drive_folder_link ?? 'Not provided'],
                  ]}
                />
                {importStatus.members.length ? (
                  importStatus.members.map((member) => (
                    <div
                      key={member.member_id}
                      className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-4"
                    >
                      <span className="flex flex-col gap-0.5">
                        <span className="text-xs text-tertiary">Member</span>
                        <EntityRef
                          kind="member"
                          id={member.member_id}
                          {...(member.member_id === id ? { label: 'This member' } : {})}
                        />
                      </span>
                      {member.failure_reason ? (
                        <MemberFailure reason={member.failure_reason} />
                      ) : null}
                      <DetailList
                        items={[
                          ['Label', member.label ?? 'Not provided'],
                          ['Status', <Status value={member.status} />],
                          [
                            'Images found',
                            <Quantity
                              value={
                                member.images_discovered == null
                                  ? '—'
                                  : formatCount(member.images_discovered)
                              }
                              raw={member.images_discovered ?? undefined}
                            />,
                          ],
                          [
                            'New garments',
                            <Quantity
                              value={
                                member.images_new === undefined
                                  ? '—'
                                  : formatCount(member.images_new)
                              }
                              raw={member.images_new}
                            />,
                          ],
                          [
                            'Already in wardrobe',
                            <Quantity
                              value={
                                member.images_duplicate === undefined
                                  ? '—'
                                  : formatCount(member.images_duplicate)
                              }
                              raw={member.images_duplicate}
                            />,
                          ],
                          [
                            'Images skipped',
                            <Quantity
                              value={
                                member.images_skipped == null
                                  ? '—'
                                  : formatCount(member.images_skipped)
                              }
                              raw={member.images_skipped ?? undefined}
                            />,
                          ],
                          [
                            'Images failed',
                            <Quantity
                              value={formatCount(member.images_failed)}
                              raw={member.images_failed}
                            />,
                          ],
                          ...(member.images_queued !== 0 ||
                          (member.status !== 'completed' && member.status !== 'failed')
                            ? ([
                                [
                                  'Images queued',
                                  <Quantity
                                    value={
                                      member.images_queued === undefined
                                        ? '—'
                                        : formatCount(member.images_queued)
                                    }
                                    raw={member.images_queued}
                                  />,
                                ],
                              ] as Array<[ReactNode, ReactNode]>)
                            : []),
                          [
                            'Pipelines started',
                            <Quantity
                              value={formatCount(member.pipelines_started)}
                              raw={member.pipelines_started}
                            />,
                          ],
                        ]}
                      />
                      <MemberSelectionOutcome
                        member={member}
                        foldersHref={`/consumers/${encodeURIComponent(member.member_id)}?tab=folders`}
                      />
                    </div>
                  ))
                ) : (
                  <Absent title="No member status rows are recorded.">
                    The import exists, but its response does not contain a member progress row.
                  </Absent>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Section>
  );
}

/** The member's recent Drive imports, newest first, each opening its own page. */
function RecentDriveImports({
  imports,
  total,
  failure,
  selected,
  onSelect,
}: {
  imports: readonly WardrobeBulkImportSummary[] | undefined;
  /** Every import on record for this member, of which `imports` is the newest page. */
  total: number | undefined;
  failure: string | undefined;
  selected: string;
  onSelect: (importId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-primary">Recent imports</h3>
        <p className="text-sm text-tertiary">
          {imports && total !== undefined && total > imports.length
            ? `Showing the newest ${formatCount(imports.length)} of ${formatCount(total)} Drive imports for this member.`
            : 'This member’s Drive imports, newest first.'}
        </p>
      </div>
      {failure ? (
        <Callout tone="error" title="Recent imports could not be loaded.">
          <FailureText raw={failure} />
        </Callout>
      ) : imports === undefined ? (
        <span className="text-sm text-tertiary">Loading recent imports…</span>
      ) : imports.length === 0 ? (
        <Absent title="No Drive imports yet.">
          Nothing has been imported from Drive for this member.
        </Absent>
      ) : (
        <ul className="flex flex-col divide-y divide-secondary rounded-xl border border-secondary">
          {imports.map((item) => (
            <li
              key={item.import_id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              data-selected={item.import_id === selected ? 'true' : undefined}
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  <Status value={item.status} />
                  <span className="text-sm text-secondary">{when(item.created_at)}</span>
                </span>
                <span className="text-xs text-tertiary">
                  {formatCount(item.images_imported)} imported · {formatCount(item.images_failed)}{' '}
                  failed
                  {item.member_count > 1 ? ` across ${formatCount(item.member_count)} members` : ''}
                </span>
              </div>
              <Actions>
                <Button
                  size="sm"
                  color="tertiary"
                  isDisabled={item.import_id === selected}
                  onClick={() => onSelect(item.import_id)}
                >
                  {item.import_id === selected ? 'Shown below' : 'Show status'}
                </Button>
                <Button size="sm" color="secondary" href={wardrobeImportHref(item.import_id)}>
                  Open import
                </Button>
              </Actions>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- outfits */

type OutfitAction =
  | 'rename'
  | 'edit-tags'
  | 'bookmark'
  | 'remove-bookmark'
  | 'mark-worn'
  | 'mark-not-worn'
  | 'promote'
  | 'set-garments'
  | 'regenerate';

const OUTFIT_CONSEQUENCE: Record<OutfitAction, string> = {
  rename: 'Changes the title the member sees. The current title revision is shown for reference.',
  'edit-tags': 'Changes the fit, formality and colour tags used to describe this outfit.',
  bookmark: 'Saves this outfit to the member’s bookmarked outfits.',
  'remove-bookmark': 'Removes this outfit from the member’s bookmarks without deleting it.',
  'mark-worn': 'Records that the member actually wore this outfit.',
  'mark-not-worn': 'Clears the actually-worn mark for this outfit.',
  promote: 'Promotes this outfit into the member’s durable outfit collection.',
  'set-garments': 'Replaces the garments in this outfit with the selected wardrobe garments.',
  regenerate: 'Queues a fresh generation for this outfit without spending the member’s allowance.',
};

const OUTFIT_ACTION_LABEL: Record<OutfitAction, string> = {
  rename: 'Rename',
  'edit-tags': 'Edit tags',
  bookmark: 'Bookmark',
  'remove-bookmark': 'Remove bookmark',
  'mark-worn': 'Mark worn',
  'mark-not-worn': 'Mark not worn',
  promote: 'Promote',
  'set-garments': 'Edit garments',
  regenerate: 'Regenerate',
};

const OUTFIT_REASON: Record<OutfitAction, string> = {
  rename: 'Renamed the outfit from the admin console.',
  'edit-tags': 'Edited the outfit’s tags from the admin console.',
  bookmark: 'Bookmarked the outfit from the admin console.',
  'remove-bookmark': 'Removed the outfit bookmark from the admin console.',
  'mark-worn': 'Marked the outfit as worn from the admin console.',
  'mark-not-worn': 'Cleared the outfit’s worn status from the admin console.',
  promote: 'Promoted the outfit from the admin console.',
  'set-garments': 'Changed the outfit’s garments from the admin console.',
  regenerate: 'Regenerated the outfit from the admin console.',
};

type SelectionGarment = {
  id: string;
  title: string;
  category?: string | null;
  image_url?: string | null;
  image_ref?: string | null;
};

function GarmentSelectionTile({
  garment,
  checked,
  label,
  onChange,
}: {
  garment: SelectionGarment;
  checked: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-secondary p-2 text-sm text-secondary">
      <input type="checkbox" aria-label={label} checked={checked} onChange={onChange} />
      <div className="w-14 shrink-0">
        <GarmentImage url={garment.image_url} refPresent={garment.image_ref} alt={garment.title} />
      </div>
      <span className="min-w-0">
        <span className="block truncate">{garment.title}</span>
        <span className="block truncate text-xs text-quaternary">
          {garment.category ? words(garment.category) : 'Category not recorded'}
        </span>
      </span>
    </label>
  );
}

function GarmentSelectionPicker({
  memberId,
  selected,
  existingItems = [],
  onChange,
  legend = 'Active wardrobe garments',
  selectionName = 'outfit',
}: {
  memberId: string;
  selected: readonly string[];
  existingItems?: readonly SelectionGarment[];
  onChange: (ids: string[]) => void;
  legend?: string;
  selectionName?: string;
}) {
  const [offset, setOffset] = useState(0);
  const source = useInsight<AdminMemberGarmentPage>(
    () =>
      memberWardrobeApiV1InternalUsersIdWardrobeGet({
        path: { id: memberId },
        query: { limit: PAGE, offset, status: 'active' },
      }),
    [memberId, offset],
  );
  return (
    <InsightState source={source} subject="this member's wardrobe">
      {(page) => (
        <div className="flex flex-col gap-3">
          <span className="text-sm text-secondary">
            {formatCount(selected.length)} garment(s) selected. Browse every wardrobe page to add
            garments that are not currently in the {selectionName}.
          </span>
          {existingItems.length ? (
            <fieldset className="flex flex-col gap-2 rounded-lg border border-secondary p-3">
              <legend className="px-1 text-sm font-medium text-secondary">Current selection</legend>
              <div className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
                {existingItems.map((garment) => {
                  const checked = selected.includes(garment.id);
                  return (
                    <GarmentSelectionTile
                      key={garment.id}
                      garment={garment}
                      checked={checked}
                      label={`Include ${garment.title}`}
                      onChange={() =>
                        onChange(
                          checked
                            ? selected.filter((value) => value !== garment.id)
                            : [...selected, garment.id],
                        )
                      }
                    />
                  );
                })}
              </div>
            </fieldset>
          ) : null}
          {page.items.length ? (
            <fieldset className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
              <legend className="sr-only">{legend}</legend>
              {page.items
                .filter((garment) => !existingItems.some((item) => item.id === garment.id))
                .map((garment) => {
                  const checked = selected.includes(garment.id);
                  const title = garment.title?.trim() || 'Untitled garment';
                  return (
                    <GarmentSelectionTile
                      key={garment.id}
                      garment={{ ...garment, title }}
                      checked={checked}
                      label={`Select ${title}`}
                      onChange={() =>
                        onChange(
                          checked
                            ? selected.filter((value) => value !== garment.id)
                            : [...selected, garment.id],
                        )
                      }
                    />
                  );
                })}
            </fieldset>
          ) : (
            <Callout tone="warning" title="There are no active garments on this page.">
              Browse another page or add active garments to the member’s wardrobe.
            </Callout>
          )}
          <Pager offset={page.offset} limit={page.limit} total={page.total} onPage={setOffset} />
        </div>
      )}
    </InsightState>
  );
}

function OutfitActions({
  memberId,
  outfit,
  onDone,
  renderAcceptancePending = false,
  onGenerationAccepted,
  showRenderState = true,
  onDeleted,
}: {
  memberId: string;
  outfit: OutfitWithRenderJobs;
  onDone: () => void | Promise<void>;
  renderAcceptancePending?: boolean;
  onGenerationAccepted?: () => void;
  showRenderState?: boolean;
  /** Offered only when the client carries the delete operation. */
  onDeleted?: () => void;
}) {
  const [pending, setPending] = useState<OutfitAction>();
  const [title, setTitle] = useState(outfit.display_title);
  const [fit, setFit] = useState('');
  const [formality, setFormality] = useState('');
  const [colours, setColours] = useState('');
  const [garmentIds, setGarmentIds] = useState(outfit.garments.map((item) => item.id));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const activeRenderJob = activeOutfitJob(outfit);
  const endedRenderJob = latestOutfitJob(outfit);

  const primaryActions: OutfitAction[] = [
    outfit.bookmarked ? 'remove-bookmark' : 'bookmark',
    outfit.actually_worn ? 'mark-not-worn' : 'mark-worn',
    'set-garments',
    'regenerate',
  ];
  const secondaryActions: OutfitAction[] = ['rename', 'edit-tags', 'promote'];

  const start = (action: OutfitAction) => {
    setTitle(outfit.display_title);
    setFit('');
    setFormality('');
    setColours('');
    setGarmentIds(outfit.garments.map((item) => item.id));
    setFailure(undefined);
    setSuccess(undefined);
    const requestKey = action === 'regenerate' ? crypto.randomUUID() : idempotencyKey;
    if (action === 'regenerate') setIdempotencyKey(requestKey);
    if (action === 'rename' || action === 'edit-tags' || action === 'set-garments') {
      setPending(action);
      return;
    }
    void run(action, requestKey);
  };

  const run = async (action: OutfitAction, requestKey = idempotencyKey) => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const collectionPath = { id: memberId };
    const itemPath = { id: memberId, outfit_id: outfit.outfit_id };
    let response;
    if (action === 'rename') {
      response = await renameMemberOutfitApiV1InternalUsersIdOutfitsOutfitIdTitlePatch({
        path: itemPath,
        body: { title: title.trim(), reason: OUTFIT_REASON.rename },
      });
    } else if (action === 'edit-tags') {
      response = await editMemberOutfitTagsApiV1InternalUsersIdOutfitsOutfitIdTagsPatch({
        path: itemPath,
        body: {
          reason: OUTFIT_REASON['edit-tags'],
          fit: fit.trim() || null,
          formality: formality.trim() || null,
          colours: colours
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
        },
      });
    } else if (action === 'bookmark' || action === 'remove-bookmark') {
      response = await bookmarkMemberOutfitApiV1InternalUsersIdOutfitsBookmarkPost({
        path: collectionPath,
        body: {
          outfit_id: outfit.outfit_id,
          saved: action === 'bookmark',
          reason: OUTFIT_REASON[action],
        },
      });
    } else if (action === 'mark-worn' || action === 'mark-not-worn') {
      response = await markMemberOutfitWornApiV1InternalUsersIdOutfitsWearPost({
        path: collectionPath,
        body: {
          outfit_id: outfit.outfit_id,
          actually_worn: action === 'mark-worn',
          reason: OUTFIT_REASON[action],
        },
      });
    } else if (action === 'set-garments') {
      response = await setMemberOutfitGarmentsApiV1InternalUsersIdOutfitsOutfitIdGarmentsPost({
        path: itemPath,
        body: { garment_ids: garmentIds, reason: OUTFIT_REASON['set-garments'], mode: 'update' },
      });
    } else if (action === 'promote') {
      response = await promoteMemberOutfitApiV1InternalUsersIdOutfitsOutfitIdPromotePost({
        path: itemPath,
        body: { reason: OUTFIT_REASON.promote },
      });
    } else {
      response = await regenerateMemberOutfitApiV1InternalUsersIdOutfitsRegeneratePost({
        path: collectionPath,
        headers: { 'Idempotency-Key': requestKey },
        body: { outfit_id: outfit.outfit_id, reason: OUTFIT_REASON.regenerate },
      });
    }
    handleUnauthorized(response.response.status);
    setBusy(false);
    if (response.response.status === 403) {
      setFailure(
        'Your role lacks the members-manage capability required to edit a member’s outfit.',
      );
      return;
    }
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'That outfit action could not be completed.'));
      return;
    }
    setPending(undefined);
    setSuccess(
      action === 'regenerate'
        ? 'Regeneration was queued. The outfit has not been regenerated yet.'
        : 'The outfit change was recorded.',
    );
    if (action === 'regenerate' && onGenerationAccepted) onGenerationAccepted();
    else void Promise.resolve(onDone()).catch(() => undefined);
  };

  const ready = (action: OutfitAction) =>
    (action !== 'rename' || title.trim().length > 0) &&
    (action !== 'set-garments' || garmentIds.length >= 2);

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
          Edit this outfit
        </span>
        <Actions>
          {primaryActions.map((action) => (
            <Button
              key={action}
              size="sm"
              color="secondary"
              isDisabled={
                busy ||
                (action === 'regenerate' && Boolean(activeRenderJob || renderAcceptancePending))
              }
              onClick={() => start(action)}
            >
              {action === 'regenerate' && (activeRenderJob?.regenerating || renderAcceptancePending)
                ? 'Regenerating…'
                : OUTFIT_ACTION_LABEL[action]}
            </Button>
          ))}
          <details className="relative">
            <summary className="cursor-pointer list-none rounded-lg border border-secondary px-3 py-2 text-sm font-semibold text-secondary shadow-xs hover:bg-primary_hover">
              More actions
            </summary>
            <div className="absolute right-0 z-10 mt-1 flex min-w-40 flex-col gap-1 rounded-lg border border-secondary bg-primary p-1 shadow-lg">
              {secondaryActions.map((action) => (
                <Button key={action} size="sm" color="secondary" onClick={() => start(action)}>
                  {OUTFIT_ACTION_LABEL[action]}
                </Button>
              ))}
            </div>
          </details>
          {onDeleted ? (
            <DeleteOutfitAction
              memberId={memberId}
              outfitId={outfit.outfit_id}
              title={outfit.display_title}
              onDeleted={onDeleted}
            />
          ) : null}
        </Actions>
        {!pending && failure ? <FailureText raw={failure} /> : null}
        {!pending && showRenderState && activeRenderJob ? (
          <ActiveJobState {...activeRenderJob} />
        ) : !pending && showRenderState && renderAcceptancePending ? (
          <div className="flex flex-wrap items-center gap-1.5" aria-live="polite">
            <Status value="requested" />
            <span className="text-xs text-tertiary">Starting regeneration…</span>
          </div>
        ) : null}
        {!pending &&
        showRenderState &&
        !outfit.render_image_ref &&
        !outfit.render_image_url &&
        endedRenderJob &&
        ['dead_lettered', 'cancelled'].includes(endedRenderJob.status) ? (
          <p className="text-xs text-error-primary">
            The last render {endedRenderJob.status === 'cancelled' ? 'was cancelled' : 'failed'}
            {endedRenderJob.error_code ? ` (${endedRenderJob.error_code})` : ''}. You can try again.
          </p>
        ) : null}
        {!pending && success ? <Callout tone="success" title={success} /> : null}
      </div>
      {pending ? (
        <ActionDialog
          title={`${OUTFIT_ACTION_LABEL[pending]} “${outfit.display_title}”`}
          description={`${OUTFIT_CONSEQUENCE[pending]} The action and reason are recorded against your operator account.`}
          width="lg"
          onClose={() => {
            setPending(undefined);
          }}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            {success ? <Callout tone="success" title={success} /> : null}
            {pending === 'rename' ? (
              <TextField
                label="New outfit title"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
            ) : null}
            {pending === 'edit-tags' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <TextField
                  label="Fit"
                  value={fit}
                  onChange={(event) => {
                    setFit(event.target.value);
                  }}
                />
                <TextField
                  label="Formality"
                  value={formality}
                  onChange={(event) => {
                    setFormality(event.target.value);
                  }}
                />
                <TextField
                  label="Colours"
                  hint="Comma-separated"
                  value={colours}
                  onChange={(event) => {
                    setColours(event.target.value);
                  }}
                />
              </div>
            ) : null}
            {pending === 'set-garments' ? (
              <GarmentSelectionPicker
                memberId={memberId}
                selected={garmentIds}
                existingItems={outfit.garments.map((garment) => ({
                  id: garment.id,
                  title: garment.title?.trim() || 'Untitled garment',
                  category: garment.category,
                  image_url: garment.image_url,
                  image_ref: garment.image_ref,
                }))}
                legend="Garments in this outfit"
                onChange={(ids) => {
                  setGarmentIds(ids);
                }}
              />
            ) : null}
            <Actions>
              <Button
                size="sm"
                color="secondary"
                isDisabled={busy}
                onClick={() => setPending(undefined)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary"
                isDisabled={!ready(pending) || busy}
                onClick={() => void run(pending)}
              >
                {busy ? 'Working…' : OUTFIT_ACTION_LABEL[pending]}
              </Button>
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

function OutfitDetail({
  memberId,
  outfitId,
  onClose,
  onDone,
  onDeleted,
}: {
  memberId: string;
  outfitId: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  onDeleted: () => void;
}) {
  const source = useInsight<OutfitWithRenderJobs>(
    () =>
      memberOutfitApiV1InternalUsersIdOutfitsOutfitIdGet({
        path: { id: memberId, outfit_id: outfitId },
      }),
    [memberId, outfitId],
  );
  const renderAcceptance = useRenderAcceptance(
    source.state.status === 'ready' ? source.state.data : undefined,
  );
  const generationAccepted = () => {
    renderAcceptance.accept();
    void Promise.all([source.refresh({ preserve: true }), Promise.resolve(onDone())]).catch(
      () => undefined,
    );
  };
  return (
    <ActionDialog
      title="Outfit detail"
      description="The stored outfit and the operator actions available for it."
      width="lg"
      onClose={onClose}
      footer={
        <Button size="sm" color="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <InsightState source={source} subject="this outfit">
        {(outfit) => (
          <div className="flex flex-col gap-5">
            <OutfitActions
              memberId={memberId}
              outfit={outfit}
              onDone={() => source.refresh({ preserve: true })}
              renderAcceptancePending={renderAcceptance.pending}
              onGenerationAccepted={generationAccepted}
              onDeleted={onDeleted}
            />
            {outfit.producing_job_id ? (
              <Actions>
                <WorkflowJobButton jobId={outfit.producing_job_id} source />
              </Actions>
            ) : null}
            <DetailList
              items={[
                ['Title', outfit.display_title],
                ['Title revision', formatCount(outfit.title_revision)],
                ['Source', words(outfit.source)],
                ['Occasion', outfit.occasion_tag ? words(outfit.occasion_tag) : 'Not recorded'],
                ['Created', when(outfit.created_at)],
                ['Bookmarked', outfit.bookmarked ? when(outfit.bookmarked_at, 'Yes') : 'No'],
                ['Actually worn', outfit.actually_worn ? when(outfit.last_worn, 'Yes') : 'No'],
                ['Feedback', outfit.feedback_thumbs ? words(outfit.feedback_thumbs) : 'None'],
              ]}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <IdentifierCopy label="Outfit reference" value={outfit.outfit_id} />
              {outfit.render_image_ref ? (
                <IdentifierCopy label="Render image storage key" value={outfit.render_image_ref} />
              ) : (
                <IdentifierValue label="Render image storage key" value="None on record" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {outfit.garments.map((garment) => (
                <CollectionGarmentTile
                  key={garment.id}
                  garment={{
                    id: garment.id,
                    title: garment.title ?? null,
                    category: garment.category ?? null,
                    imageUrl: garment.image_url ?? null,
                    imageRef: garment.image_ref,
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </InsightState>
    </ActionDialog>
  );
}

function CreateOutfit({ memberId, onDone }: { memberId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [result, setResult] = useState<AdminOnBehalfActionResult>();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const response = await createMemberOutfitApiV1InternalUsersIdOutfitsPost({
      path: { id: memberId },
      headers: { 'Idempotency-Key': idempotencyKey },
      body: {
        garment_ids: selected,
        reason: 'Created the outfit from the admin console.',
      },
    });
    handleUnauthorized(response.response.status);
    setBusy(false);
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'The outfit could not be created.'));
      return;
    }
    setResult(response.data);
    onDone();
  };
  const ready = selected.length >= 2;
  return (
    <>
      <Button
        size="sm"
        color="primary"
        onClick={() => {
          setIdempotencyKey(crypto.randomUUID());
          setFailure(undefined);
          setResult(undefined);
          setOpen(true);
        }}
      >
        Create outfit
      </Button>
      {open ? (
        <ActionDialog
          title="Create an outfit"
          description="Choose at least two wardrobe garments. One idempotency key is held for this dialog so a retry cannot create a duplicate."
          width="lg"
          onClose={() => setOpen(false)}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            {result ? (
              <Callout tone="success" title="The outfit was created.">
                {result.resource_id ? (
                  <EntityRef
                    kind="outfit"
                    id={result.resource_id}
                    memberId={memberId}
                    label="The new outfit"
                  />
                ) : null}
              </Callout>
            ) : null}
            <GarmentSelectionPicker
              memberId={memberId}
              selected={selected}
              onChange={setSelected}
              legend="Garments (choose at least two)"
              selectionName="new outfit"
            />
            <Actions>
              <Button size="sm" color="secondary" isDisabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary"
                isDisabled={!ready || busy}
                onClick={() => void run()}
              >
                {busy ? 'Working…' : 'Create outfit'}
              </Button>
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

function ConfirmStep({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Back',
  busy,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Callout tone="warning" title={title}>
      <div className="pt-1">{body}</div>
      <span className="block pt-1">This is recorded against your operator account.</span>
      <Actions>
        <Button size="sm" color="secondary" isDisabled={busy} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button size="sm" color="primary-destructive" isDisabled={busy} onClick={onConfirm}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </Actions>
    </Callout>
  );
}

function CreateFolder({ memberId, onDone }: { memberId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [failure, setFailure] = useState<string>();
  const [result, setResult] = useState<WardrobeFolderSummary>();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const response = await createMemberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersPost({
      path: { id: memberId },
      body: { name: name.trim(), reason: 'Created the wardrobe folder from the admin console.' },
    });
    handleUnauthorized(response.response.status);
    setBusy(false);
    if (response.response.status === 403) {
      setFailure(
        'Your role lacks the members-manage capability required to edit a member’s wardrobe folders.',
      );
      return;
    }
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'That folder could not be created.'));
      return;
    }
    setResult(response.data);
    onDone();
  };

  const ready = name.trim().length > 0;
  return (
    <>
      <Button
        size="sm"
        color="secondary"
        onClick={() => {
          setFailure(undefined);
          setResult(undefined);
          setName('');
          setOpen(true);
        }}
      >
        Create folder
      </Button>
      {open ? (
        <ActionDialog
          title="Create a folder"
          description="Create an empty folder for this member. Garments and saved outfits can be filed after it is created, and the change is recorded against your operator account."
          onClose={() => setOpen(false)}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            {result ? (
              <Callout tone="success" title="The folder was created.">
                <IdentifierCopy label="Folder reference" value={result.id} />
              </Callout>
            ) : null}
            <TextField
              label="Folder name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Actions>
              <Button size="sm" color="secondary" isDisabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary"
                isDisabled={!ready || busy}
                onClick={() => void run()}
              >
                {busy ? 'Working…' : 'Confirm create folder'}
              </Button>
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

type FolderGarmentAction = 'rename' | 'delete' | 'edit-garments';
type FolderAction = FolderGarmentAction | 'file-outfits' | 'remove-outfits';

const FOLDER_REASON: Record<FolderAction | 'add-garment' | 'remove-garment', string> = {
  rename: 'Renamed the wardrobe folder from the admin console.',
  delete: 'Deleted the wardrobe folder from the admin console.',
  'edit-garments': 'Edited the wardrobe folder garments from the admin console.',
  'file-outfits': 'Filed outfits in the folder from the admin console.',
  'remove-outfits': 'Removed outfits from the folder from the admin console.',
  'add-garment': 'Added a garment to the wardrobe folder from the admin console.',
  'remove-garment': 'Removed a garment from the wardrobe folder from the admin console.',
};

function FolderOperatorActions({
  memberId,
  folder,
  garments,
  outfits,
  outfitCandidates,
  onDone,
}: {
  memberId: string;
  folder: WardrobeFolderSummary;
  garments: readonly WardrobeFolderGarmentItem[];
  outfits: readonly WardrobeFolderOutfit[];
  outfitCandidates: readonly WardrobeFolderOutfit[];
  onDone: () => void;
}) {
  const [pending, setPending] = useState<FolderAction>();
  const [name, setName] = useState(folder.name);
  const [garmentIds, setGarmentIds] = useState(garments.map((garment) => garment.garment_id));
  const [outfitIds, setOutfitIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [success, setSuccess] = useState<string>();

  const start = (action: FolderAction) => {
    setFailure(undefined);
    setSuccess(undefined);
    setName(folder.name);
    setGarmentIds(garments.map((garment) => garment.garment_id));
    setOutfitIds([]);
    setPending(action);
  };

  const run = async (action: FolderGarmentAction) => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const path = { id: memberId, folder_id: folder.id };
    let responses;
    if (action === 'rename') {
      responses = [
        await updateMemberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersFolderIdPatch({
          path,
          body: {
            expected_version: folder.version,
            name: name.trim(),
            reason: FOLDER_REASON.rename,
          },
        }),
      ];
    } else if (action === 'delete') {
      responses = [
        await deleteMemberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersFolderIdDelete({
          path,
          body: { reason: FOLDER_REASON.delete },
        }),
      ];
    } else {
      const existing = new Set(garments.map((garment) => garment.garment_id));
      const selected = new Set(garmentIds);
      responses = await Promise.all([
        ...garmentIds
          .filter((garmentId) => !existing.has(garmentId))
          .map((garmentId) =>
            putMemberWardrobeFolderGarmentApiV1InternalUsersIdWardrobeFoldersFolderIdGarmentsGarmentIdPut(
              {
                path: { ...path, garment_id: garmentId },
                body: { reason: FOLDER_REASON['add-garment'] },
              },
            ),
          ),
        ...garments
          .filter((garment) => !selected.has(garment.garment_id))
          .map((garment) =>
            removeMemberWardrobeFolderGarmentApiV1InternalUsersIdWardrobeFoldersFolderIdGarmentsGarmentIdDelete(
              {
                path: { ...path, garment_id: garment.garment_id },
                body: { reason: FOLDER_REASON['remove-garment'] },
              },
            ),
          ),
      ]);
    }
    responses.forEach((response) => handleUnauthorized(response.response.status));
    setBusy(false);
    if (responses.some((response) => response.response.status === 403)) {
      setFailure(
        'Your role lacks the members-manage capability required to edit a member’s wardrobe folders.',
      );
      return;
    }
    if (
      responses.some(
        (response) =>
          response.error || response.response.status < 200 || response.response.status >= 300,
      )
    ) {
      setFailure(
        errorDetail(
          responses.find((response) => response.error)?.error,
          'That folder action could not be completed.',
        ),
      );
      return;
    }
    setPending(undefined);
    onDone();
  };

  const runOutfits = async (added: boolean) => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const response =
      await mutateMemberWardrobeFolderOutfitsApiV1InternalUsersIdWardrobeFoldersFolderIdOutfitsPost(
        {
          path: { id: memberId, folder_id: folder.id },
          body: {
            outfit_ids: outfitIds,
            added,
            reason: FOLDER_REASON[added ? 'file-outfits' : 'remove-outfits'],
          },
        },
      );
    handleUnauthorized(response.response.status);
    setBusy(false);
    if (response.response.status === 403) {
      setFailure("Your role cannot manage a member's folders.");
      return;
    }
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'That folder action could not be completed.'));
      return;
    }
    setPending(undefined);
    if ('addedCount' in response.data) {
      setSuccess(
        `Filed ${formatCount(response.data.addedCount)} ${response.data.addedCount === 1 ? 'outfit' : 'outfits'}; ${formatCount(response.data.alreadyPresentCount)} already in the folder. Their pieces were filed too.`,
      );
    } else {
      setSuccess(
        `Removed ${formatCount(response.data.removedCount)} ${response.data.removedCount === 1 ? 'outfit' : 'outfits'}. Their pieces stay in the folder.`,
      );
    }
    onDone();
  };

  const title = folder.name.trim() || 'Untitled folder';
  const ready =
    (pending !== 'rename' || name.trim().length > 0) &&
    (pending !== 'file-outfits' || outfitIds.length > 0) &&
    (pending !== 'remove-outfits' || outfitIds.length > 0);
  return (
    <>
      {success ? <Callout tone="success" title={success} /> : null}
      <Actions>
        <Button size="sm" color="secondary" onClick={() => start('rename')}>
          Rename
        </Button>
        <Button size="sm" color="secondary" onClick={() => start('edit-garments')}>
          Edit garments
        </Button>
        <Button size="sm" color="secondary" onClick={() => start('file-outfits')}>
          File outfits
        </Button>
        <Button size="sm" color="secondary" onClick={() => start('remove-outfits')}>
          Remove outfits
        </Button>
        <Button size="sm" color="secondary-destructive" onClick={() => start('delete')}>
          Delete folder
        </Button>
      </Actions>
      {pending ? (
        <ActionDialog
          title={
            pending === 'rename'
              ? `Rename “${title}”`
              : pending === 'edit-garments'
                ? `Edit garments in “${title}”`
                : pending === 'file-outfits'
                  ? `File outfits in “${title}”`
                  : pending === 'remove-outfits'
                    ? `Remove outfits from “${title}”`
                    : `Delete “${title}”?`
          }
          description={
            pending === 'delete'
              ? 'This deletes the folder. Its garments and saved outfits remain available to the member.'
              : 'This change is made on behalf of the member and recorded against your operator account.'
          }
          width={
            pending === 'edit-garments' ||
            pending === 'file-outfits' ||
            pending === 'remove-outfits'
              ? 'lg'
              : undefined
          }
          onClose={() => {
            setPending(undefined);
          }}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            {pending === 'rename' ? (
              <TextField
                label="New folder name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            ) : null}
            {pending === 'edit-garments' ? (
              <GarmentSelectionPicker
                memberId={memberId}
                selected={garmentIds}
                existingItems={garments.map((garment) => ({
                  id: garment.garment_id,
                  title: garment.label.trim() || 'Untitled garment',
                  category: garment.category,
                  image_url: garment.image_url,
                }))}
                legend="Garments in this folder"
                selectionName="folder"
                onChange={setGarmentIds}
              />
            ) : null}
            {pending === 'file-outfits' ? (
              <FolderOutfitPicker
                outfits={outfitCandidates}
                selected={outfitIds}
                empty="No outfits are available to file in this folder."
                onChange={setOutfitIds}
              />
            ) : null}
            {pending === 'remove-outfits' ? (
              <FolderOutfitPicker
                outfits={outfits}
                selected={outfitIds}
                empty="No outfits are filed in this folder."
                onChange={setOutfitIds}
              />
            ) : null}
            {pending === 'delete' ? (
              <ConfirmStep
                title="Confirm folder deletion."
                body="The folder will be deleted, but its garments and saved outfits will stay with the member."
                confirmLabel="Confirm delete folder"
                busy={busy}
                onCancel={() => setPending(undefined)}
                onConfirm={() => void run('delete')}
              />
            ) : (
              <Actions>
                <Button
                  size="sm"
                  color="secondary"
                  isDisabled={busy}
                  onClick={() => setPending(undefined)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  isDisabled={!ready || busy}
                  onClick={() =>
                    pending === 'file-outfits' || pending === 'remove-outfits'
                      ? void runOutfits(pending === 'file-outfits')
                      : void run(pending)
                  }
                >
                  {busy
                    ? 'Working…'
                    : pending === 'rename'
                      ? 'Confirm rename'
                      : pending === 'edit-garments'
                        ? 'Confirm garment changes'
                        : pending === 'file-outfits'
                          ? 'Confirm file outfits'
                          : pending === 'remove-outfits'
                            ? 'Confirm remove outfits'
                            : 'Delete folder'}
                </Button>
              </Actions>
            )}
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

type CollectionGarment = {
  id: string;
  title: string | null;
  category: string | null;
  imageUrl: string | null;
  imageRef?: string | null;
};

function CollectionCard({ children }: { children: ReactNode }) {
  return (
    <li className="flex min-w-0 flex-col gap-4 rounded-xl border border-secondary bg-primary p-4">
      {children}
    </li>
  );
}

function CollectionGarmentTile({
  garment,
  children,
  remainingCount,
}: {
  garment: CollectionGarment;
  children?: ReactNode;
  remainingCount?: number;
}) {
  const title = garmentTitle(garment.title);
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-secondary p-2">
      <div className="relative overflow-hidden rounded-lg">
        <GarmentImage url={garment.imageUrl} refPresent={garment.imageRef} alt={title} />
        {remainingCount ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-lg font-semibold text-white">
            +{formatCount(remainingCount)}
          </div>
        ) : null}
      </div>
      <span className="truncate text-xs text-secondary">{title}</span>
      <span className="truncate text-xs text-quaternary">
        {garment.category ? words(garment.category) : 'Category not recorded'}
      </span>
      {children}
    </div>
  );
}

/** Every garment, for an expanded view where a "+N" would hide what was asked for. */
function CollectionGarmentGrid({ garments }: { garments: readonly CollectionGarment[] }) {
  if (!garments.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {garments.map((garment) => (
        <CollectionGarmentTile key={garment.id} garment={garment} />
      ))}
    </div>
  );
}

function FolderOutfitPicker({
  outfits,
  selected,
  empty,
  onChange,
}: {
  outfits: readonly WardrobeFolderOutfit[];
  selected: readonly string[];
  empty: string;
  onChange: (ids: string[]) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-sm font-medium text-secondary">Outfits</legend>
      <span className="text-sm text-secondary">
        {formatCount(selected.length)} {selected.length === 1 ? 'outfit' : 'outfits'} selected.
      </span>
      {outfits.length ? (
        <div className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
          {outfits.map((outfit) => {
            const checked = selected.includes(outfit.outfit_id);
            const name = outfit.name.trim() || 'Untitled outfit';
            return (
              <label
                key={outfit.outfit_id}
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-secondary p-2 text-sm text-secondary"
              >
                <input
                  type="checkbox"
                  aria-label={`Select ${name}`}
                  checked={checked}
                  onChange={() =>
                    onChange(
                      checked
                        ? selected.filter((value) => value !== outfit.outfit_id)
                        : [...selected, outfit.outfit_id],
                    )
                  }
                />
                <div className="w-14 shrink-0">
                  <GarmentImage
                    url={outfit.image_url}
                    alt={name}
                    absentDetail="No image is recorded for this outfit."
                  />
                </div>
                <span className="min-w-0">
                  <span className="block truncate">{name}</span>
                  <span className="block truncate text-xs text-quaternary">
                    {formatCount(outfit.garments.length)}{' '}
                    {outfit.garments.length === 1 ? 'garment' : 'garments'}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      ) : (
        <span className="text-sm text-tertiary">{empty}</span>
      )}
    </fieldset>
  );
}

function FolderOutfitGrid({ outfits }: { outfits: readonly WardrobeFolderOutfit[] }) {
  if (!outfits.length) {
    return <span className="text-sm text-tertiary">No outfits are filed in this folder.</span>;
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {outfits.map((outfit) => {
        const name = outfit.name.trim() || 'Untitled outfit';
        return (
          <CollectionCard key={outfit.outfit_id}>
            <div className="w-28 shrink-0">
              <GarmentImage
                url={outfit.image_url}
                alt={name}
                className="aspect-[4/5]"
                absentDetail="No image is recorded for this outfit."
              />
            </div>
            <DetailList
              items={[
                ['Name', name],
                [
                  'Garments',
                  <Quantity
                    value={formatCount(outfit.garments.length)}
                    unit={outfit.garments.length === 1 ? 'garment' : 'garments'}
                    raw={outfit.garments.length}
                  />,
                ],
              ]}
            />
            <IdentifierCopy label="Outfit reference" value={outfit.outfit_id} />
          </CollectionCard>
        );
      })}
    </ul>
  );
}

function FolderCard({
  memberId,
  folder,
  onDone,
}: {
  memberId: string;
  folder: WardrobeFolderSummary;
  onDone: () => void;
}) {
  // Contents load only when opened: a folder detail is heavy to build, and fetching every card's
  // detail at once took production down on 2026-10-01 for a member with 25 large folders.
  const [open, setOpen] = useState(false);
  return (
    <CollectionCard>
      <DetailList
        items={[
          ['Name', folder.name.trim() || 'Untitled folder'],
          ['Created', when(folder.created_at)],
          ['Position', folder.position],
          [
            'Garments',
            <Quantity
              value={formatCount(folder.garment_count)}
              unit={folder.garment_count === 1 ? 'garment' : 'garments'}
              raw={folder.garment_count}
            />,
          ],
        ]}
      />
      <IdentifierCopy label="Folder reference" value={folder.id} />
      <div>
        <Button
          size="sm"
          color="secondary"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Hide contents' : 'Show contents'}
        </Button>
      </div>
      {open ? <FolderContents memberId={memberId} folder={folder} onDone={onDone} /> : null}
    </CollectionCard>
  );
}

function FolderContents({
  memberId,
  folder,
  onDone,
}: {
  memberId: string;
  folder: WardrobeFolderSummary;
  onDone: () => void;
}) {
  const detail = useInsight<AdminMemberWardrobeFolderDetail>(
    () =>
      memberWardrobeFolderApiV1InternalUsersIdWardrobeFoldersFolderIdGet({
        path: { id: memberId, folder_id: folder.id },
      }),
    [memberId, folder.id],
  );
  const refresh = () => {
    onDone();
    void detail.refresh();
  };
  return (
    <InsightState source={detail} subject={`the contents of “${folder.name}”`}>
      {(folderDetail) => (
        <>
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-primary">Filed outfits</h3>
            <FolderOutfitGrid outfits={folderDetail.outfits} />
          </div>
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-primary">Garments</h3>
            {folderDetail.items.length ? (
              <CollectionGarmentGrid
                garments={folderDetail.items.map((garment) => ({
                  id: garment.garment_id,
                  title: garment.label,
                  category: garment.category,
                  imageUrl: garment.image_url,
                }))}
              />
            ) : (
              <span className="text-sm text-tertiary">
                No garments are returned for this folder.
              </span>
            )}
          </div>
          <FolderOperatorActions
            memberId={memberId}
            folder={folderDetail.folder}
            garments={folderDetail.items}
            outfits={folderDetail.outfits}
            outfitCandidates={folderDetail.outfit_candidates}
            onDone={refresh}
          />
        </>
      )}
    </InsightState>
  );
}

function FoldersPanel({ id }: { id: string }) {
  const [offset, setOffset] = useState(0);
  const folders = useInsight<AdminMemberWardrobeFolderPage>(
    () =>
      memberWardrobeFoldersApiV1InternalUsersIdWardrobeFoldersGet({
        path: { id },
        query: { limit: PAGE, offset },
      }),
    [id, offset],
  );

  return (
    <div className="flex flex-col gap-6">
      <Section
        title="Folders"
        description="A folder holds garments and saved outfits. Filing an outfit also files its active pieces."
        actions={<CreateFolder memberId={id} onDone={() => void folders.refresh()} />}
      >
        <InsightState source={folders} subject="this member's saved folders">
          {(page) =>
            page.items.length ? (
              <>
                <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {page.items.map((folder) => (
                    <FolderCard
                      key={folder.id}
                      memberId={id}
                      folder={folder}
                      onDone={() => void folders.refresh()}
                    />
                  ))}
                </ul>
                <Pager
                  offset={page.offset}
                  limit={page.limit}
                  total={page.total}
                  onPage={setOffset}
                />
              </>
            ) : (
              <Absent title="This member has no saved folders.">
                This is an ordinary empty state. Create a folder to file garments and saved outfits.
              </Absent>
            )
          }
        </InsightState>
      </Section>
    </div>
  );
}

function OutfitRender({
  memberId,
  outfit,
  onDone,
  renderAcceptancePending,
  onGenerationAccepted,
}: {
  memberId: string;
  outfit: OutfitWithRenderJobs;
  onDone: () => void | Promise<void>;
  renderAcceptancePending: boolean;
  onGenerationAccepted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const activeRenderJob = activeOutfitJob(outfit);
  const endedJob = latestOutfitJob(outfit);

  if (outfit.render_image_url) {
    return (
      <div className="flex flex-col gap-2">
        <GarmentImage
          url={outfit.render_image_url}
          refPresent={outfit.render_image_ref}
          alt={`Rendered outfit “${outfit.display_title}”`}
          className="aspect-[4/5]"
        />
        {activeRenderJob ? <ActiveJobState {...activeRenderJob} /> : null}
      </div>
    );
  }
  if (outfit.render_image_ref) {
    return (
      <div className="flex flex-col gap-2">
        <GarmentImage
          url={null}
          refPresent={outfit.render_image_ref}
          alt={`Rendered outfit “${outfit.display_title}”`}
          className="aspect-[4/5]"
          onRefresh={onDone}
        />
        {activeRenderJob ? <ActiveJobState {...activeRenderJob} /> : null}
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const response = await regenerateMemberOutfitApiV1InternalUsersIdOutfitsRegeneratePost({
      path: { id: memberId },
      headers: { 'Idempotency-Key': crypto.randomUUID() },
      body: {
        outfit_id: outfit.outfit_id,
        reason: OUTFIT_REASON.regenerate,
      },
    });
    handleUnauthorized(response.response.status);
    setBusy(false);
    if (response.response.status === 403) {
      setFailure(
        'Your role lacks the members-manage capability required to render a member’s outfit.',
      );
      return;
    }
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'The outfit render could not be queued.'));
      return;
    }
    onGenerationAccepted();
  };

  return (
    <div className="flex aspect-[4/5] flex-col items-center justify-center gap-3 rounded-lg border border-secondary bg-secondary p-4 text-center">
      <Image01 className="size-6 text-fg-quaternary" />
      <span className="text-sm text-tertiary">No rendered image is available.</span>
      <Button
        size="sm"
        color="secondary"
        isDisabled={busy || Boolean(activeRenderJob) || renderAcceptancePending}
        onClick={() => void generate()}
      >
        {busy ? 'Generating…' : 'Generate'}
      </Button>
      {activeRenderJob ? (
        <ActiveJobState {...activeRenderJob} />
      ) : renderAcceptancePending ? (
        <div className="flex flex-wrap items-center justify-center gap-1.5" aria-live="polite">
          <Status value="requested" />
          <span className="text-xs text-tertiary">Starting render…</span>
        </div>
      ) : null}
      {endedJob && ['dead_lettered', 'cancelled'].includes(endedJob.status) ? (
        <p className="text-xs text-error-primary">
          The last render {endedJob.status === 'cancelled' ? 'was cancelled' : 'failed'}
          {endedJob.error_code ? ` (${endedJob.error_code})` : ''}. You can try again.
        </p>
      ) : null}
      {failure ? <FailureText raw={failure} /> : null}
    </div>
  );
}

/** Ticking a card, offered only when the client can delete a selection. */
type OutfitSelection = { checked: boolean; onToggle: () => void };

function OutfitSelectBox({
  outfit,
  selection,
}: {
  outfit: OutfitWithRenderJobs;
  selection: OutfitSelection | undefined;
}) {
  if (!selection) return null;
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
      <input
        type="checkbox"
        aria-label={`Select “${outfit.display_title}”`}
        checked={selection.checked}
        onChange={selection.onToggle}
      />
      Select
    </label>
  );
}

/** An outfit's garments as the spread draws them. */
function spreadGarments(garments: readonly CollectionGarment[]): SpreadGarment[] {
  return garments.map((garment) => ({
    id: garment.id,
    imageUrl: garment.imageUrl,
    label: garment.title,
  }));
}

function outfitCollectionGarments(outfit: OutfitWithRenderJobs): CollectionGarment[] {
  return outfit.garments.map((garment) => ({
    id: garment.id,
    title: garment.title ?? null,
    category: garment.category ?? null,
    imageUrl: garment.image_url ?? null,
    imageRef: garment.image_ref,
  }));
}

function OutfitCard({
  memberId,
  outfit,
  onOpen,
  onDone,
  onDeleted,
  selection,
}: {
  memberId: string;
  outfit: OutfitWithRenderJobs;
  onOpen: () => void;
  onDone: () => void | Promise<void>;
  onDeleted?: () => void;
  selection?: OutfitSelection;
}) {
  const renderAcceptance = useRenderAcceptance(outfit);
  const generationAccepted = () => {
    renderAcceptance.accept();
    void Promise.resolve(onDone()).catch(() => undefined);
  };
  return (
    <CollectionCard>
      <OutfitSelectBox outfit={outfit} selection={selection} />
      <OutfitRender
        memberId={memberId}
        outfit={outfit}
        onDone={onDone}
        renderAcceptancePending={renderAcceptance.pending}
        onGenerationAccepted={generationAccepted}
      />
      <DetailList
        items={[
          ['Title', outfit.display_title],
          ['Source', words(outfit.source)],
          ['Created', when(outfit.created_at)],
          [
            'Garments',
            <Quantity
              value={formatCount(outfit.garments.length)}
              unit={outfit.garments.length === 1 ? 'garment' : 'garments'}
              raw={outfit.garments.length}
            />,
          ],
        ]}
      />
      <div className="flex flex-wrap gap-1">
        <Status value={outfit.bookmarked ? 'bookmarked' : 'not_bookmarked'} />
        <Status value={outfit.actually_worn ? 'worn' : 'not_worn'} />
      </div>
      {outfit.render_image_url || outfit.render_image_ref ? null : outfit.garments.length ? (
        <OutfitSpread
          className="max-w-[16rem]"
          memberId={memberId}
          label={outfit.display_title}
          garments={spreadGarments(outfitCollectionGarments(outfit))}
        />
      ) : (
        <span className="text-sm text-tertiary">No garments are returned for this outfit.</span>
      )}
      <Actions>
        <Button size="sm" color="secondary" onClick={onOpen}>
          View outfit details
        </Button>
      </Actions>
      <OutfitActions
        memberId={memberId}
        outfit={outfit}
        onDone={onDone}
        renderAcceptancePending={renderAcceptance.pending}
        onGenerationAccepted={generationAccepted}
        showRenderState={false}
        onDeleted={onDeleted}
      />
    </CollectionCard>
  );
}

/** The garments view: an outfit laid flat, every garment in it side by side. */
function OutfitGarmentsCard({
  outfit,
  onOpen,
  selection,
}: {
  outfit: OutfitWithRenderJobs;
  onOpen: () => void;
  selection?: OutfitSelection;
}) {
  const garments = outfitCollectionGarments(outfit);
  return (
    <CollectionCard>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-primary">
            {outfit.display_title}
          </span>
          <span className="text-xs text-tertiary">
            {`${formatCount(garments.length)} ${garments.length === 1 ? 'garment' : 'garments'} · ${when(outfit.created_at)}`}
          </span>
        </div>
        <OutfitSelectBox outfit={outfit} selection={selection} />
      </div>
      {garments.length ? (
        <OutfitSpread
          className="max-w-[16rem]"
          label={outfit.display_title}
          garments={spreadGarments(garments)}
        />
      ) : (
        <span className="text-sm text-tertiary">No garments are returned for this outfit.</span>
      )}
      <Actions>
        <Button size="sm" color="secondary" onClick={onOpen}>
          View outfit details
        </Button>
      </Actions>
    </CollectionCard>
  );
}

/** The search parameter keeping the outfits tab on the avatar or the garments view. */
export const OUTFIT_VIEW_PARAM = 'outfit_view';
/** The search parameter keeping how many outfits one page of the tab shows. */
export const OUTFIT_PAGE_SIZE_PARAM = 'outfits_shown';

type OutfitView = 'avatar' | 'garments';
type OutfitPageSize = 25 | 100 | 'all';

const OUTFIT_PAGE_SIZES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '25', label: '25 per page' },
  { value: '100', label: '100 per page' },
  { value: 'all', label: 'All outfits' },
];

function readOutfitPageSize(value: string | null): OutfitPageSize {
  if (value === 'all') return 'all';
  return value === '100' ? 100 : 25;
}

/** The largest page the outfit read serves, used when every outfit is wanted. */
const OUTFIT_READ_MAX = 200;

/**
 * Reads every outfit, one full page after another. The total can move while
 * this runs, so the loop also stops on a short page and drops repeats.
 */
async function readEveryOutfit(
  id: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<Read<AdminMemberOutfitPage>> {
  const items: AdminMemberOutfit[] = [];
  const seen = new Set<string>();
  let total = 0;
  for (let offset = 0; ; offset += OUTFIT_READ_MAX) {
    const result = await memberOutfitsApiV1InternalUsersIdOutfitsGet({
      path: { id },
      query: { limit: OUTFIT_READ_MAX, offset },
    });
    if (result.response.status >= 300 || result.error || !result.data) return result;
    total = result.data.total;
    for (const item of result.data.items) {
      if (seen.has(item.outfit_id)) continue;
      seen.add(item.outfit_id);
      items.push(item);
    }
    onProgress(items.length, total);
    if (result.data.items.length < OUTFIT_READ_MAX || offset + OUTFIT_READ_MAX >= total) break;
  }
  return {
    data: { items, total: Math.max(total, items.length), limit: items.length, offset: 0 },
    error: undefined,
    response: { status: 200 },
  };
}

function OutfitsPanel({ id, memberName }: { id: string; memberName?: string | null }) {
  const [offset, setOffset] = useState(0);
  const [ticked, setTicked] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ loaded: number; total: number }>();
  // Kept in the address so a reference to one outfit opens it on arrival, and
  // so a chosen view and page size survive a reload or a shared link.
  const [params, setParams] = useSearchParams();
  const openId = params.get(OUTFIT_PARAM) ?? undefined;
  const view: OutfitView = params.get(OUTFIT_VIEW_PARAM) === 'garments' ? 'garments' : 'avatar';
  const pageSize = readOutfitPageSize(params.get(OUTFIT_PAGE_SIZE_PARAM));
  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const setOpenId = (outfitId: string | undefined) => setParam(OUTFIT_PARAM, outfitId);
  const outfits = useInsight<AdminMemberOutfitPage>(
    () => {
      if (pageSize !== 'all')
        return memberOutfitsApiV1InternalUsersIdOutfitsGet({
          path: { id },
          query: { limit: pageSize, offset },
        });
      setProgress(undefined);
      return readEveryOutfit(id, (loaded, total) => setProgress({ loaded, total }));
    },
    [id, offset, pageSize],
    // Re-reading every page every few seconds is too much for a long list;
    // the full list refreshes on request instead.
    { live: pageSize !== 'all' },
  );
  const deletion = outfitDeletionOperations();
  const page = outfits.state.status === 'ready' ? outfits.state.data : undefined;
  // Narrowed to what is on screen: a batch applied to outfits the operator
  // can no longer see is a decision they cannot check before taking it.
  const selected = page
    ? ticked.filter((outfitId) => page.items.some((item) => item.outfit_id === outfitId))
    : [];
  const afterDeletion = () => {
    setTicked([]);
    if (offset) setOffset(0);
    else void outfits.refresh();
  };
  const afterSingleDeletion = (outfitId: string) => {
    setTicked((current) => current.filter((value) => value !== outfitId));
    if (openId === outfitId) setOpenId(undefined);
    void outfits.refresh({ preserve: true }).catch(() => void outfits.refresh());
  };
  const selectionFor = (outfitId: string): OutfitSelection | undefined =>
    deletion.bulk
      ? {
          checked: ticked.includes(outfitId),
          onToggle: () =>
            setTicked((current) =>
              current.includes(outfitId)
                ? current.filter((value) => value !== outfitId)
                : [...current, outfitId],
            ),
        }
      : undefined;
  return (
    <div className="flex flex-col gap-6">
      <Section
        title="Outfits"
        description="The member’s saved and generated outfits. Avatar view shows each outfit on the member’s avatar; Garments view lays out the garments in it. Open one for its feedback and operator actions."
        actions={<CreateOutfit memberId={id} onDone={() => void outfits.refresh()} />}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2" role="group" aria-label="Outfit view">
              {(['avatar', 'garments'] as OutfitView[]).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  color={view === option ? 'primary' : 'secondary'}
                  aria-pressed={view === option}
                  onClick={() =>
                    setParam(OUTFIT_VIEW_PARAM, option === 'avatar' ? undefined : option)
                  }
                >
                  {option === 'avatar' ? 'Avatar view' : 'Garments view'}
                </Button>
              ))}
            </div>
            <SelectField
              label="Show"
              value={String(pageSize)}
              options={OUTFIT_PAGE_SIZES}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setOffset(0);
                setParam(OUTFIT_PAGE_SIZE_PARAM, value === '25' ? undefined : value);
              }}
            />
            {pageSize === 'all' ? (
              <Button size="sm" color="secondary" onClick={() => void outfits.refresh()}>
                Refresh
              </Button>
            ) : null}
            {page && deletion.bulk ? (
              <SelectAllToggle
                ids={page.items.map((item) => item.outfit_id)}
                selected={selected}
                onChange={setTicked}
                noun={page.items.length === 1 ? 'outfit' : 'outfits'}
              />
            ) : null}
          </div>
          {page ? (
            <span className="text-sm font-medium text-secondary">
              {`${formatCount(page.total)} ${page.total === 1 ? 'outfit' : 'outfits'}`}
            </span>
          ) : null}
        </div>
        {pageSize === 'all' && outfits.state.status === 'loading' ? (
          <p className="text-sm text-tertiary" aria-live="polite">
            {progress
              ? `Loading every outfit… ${formatCount(progress.loaded)} of ${formatCount(progress.total)} loaded.`
              : 'Loading every outfit…'}
          </p>
        ) : null}
        {page && deletion.bulk ? (
          <BulkOutfitDeletion
            memberId={id}
            memberName={memberName}
            selectedIds={selected}
            total={page.total}
            titles={new Map(page.items.map((item) => [item.outfit_id, item.display_title]))}
            onClearSelection={() => setTicked([])}
            onDeleted={afterDeletion}
          />
        ) : null}
        <InsightState source={outfits} subject="this member's outfits">
          {(data) =>
            data.items.length ? (
              <>
                <ul
                  className={cx(
                    'grid grid-cols-1 gap-3',
                    view === 'avatar' ? 'lg:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-3',
                  )}
                >
                  {data.items.map((outfit) =>
                    view === 'avatar' ? (
                      <OutfitCard
                        key={outfit.outfit_id}
                        memberId={id}
                        outfit={outfit}
                        onOpen={() => setOpenId(outfit.outfit_id)}
                        onDone={() => outfits.refresh({ preserve: true })}
                        onDeleted={
                          deletion.single ? () => afterSingleDeletion(outfit.outfit_id) : undefined
                        }
                        selection={selectionFor(outfit.outfit_id)}
                      />
                    ) : (
                      <OutfitGarmentsCard
                        key={outfit.outfit_id}
                        outfit={outfit}
                        onOpen={() => setOpenId(outfit.outfit_id)}
                        selection={selectionFor(outfit.outfit_id)}
                      />
                    ),
                  )}
                </ul>
                {pageSize === 'all' ? (
                  <p className="border-t border-secondary pt-3 text-sm text-tertiary">
                    {data.items.length < data.total
                      ? `${formatCount(data.items.length)} of ${formatCount(data.total)} outfits loaded. Refresh to read the rest.`
                      : `Showing all ${formatCount(data.items.length)} ${data.items.length === 1 ? 'outfit' : 'outfits'}.`}
                  </p>
                ) : (
                  <Pager
                    offset={data.offset}
                    limit={data.limit}
                    total={data.total}
                    onPage={setOffset}
                  />
                )}
              </>
            ) : (
              <Absent title="This member has no outfits.">
                Create one from at least two active wardrobe garments.
              </Absent>
            )
          }
        </InsightState>
      </Section>
      {openId ? (
        <OutfitDetail
          memberId={id}
          outfitId={openId}
          onClose={() => setOpenId(undefined)}
          onDone={() => outfits.refresh({ preserve: true })}
          onDeleted={() => afterSingleDeletion(openId)}
        />
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------------- chat */

function ChatReply({ turn }: { turn: AdminMemberChatTurn }) {
  if (turn.reply_state === 'stored') {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm whitespace-pre-wrap text-secondary">{turn.reply}</p>
        {turn.error ? <IdentifierValue label="Unexpected reply error" value={turn.error} /> : null}
      </div>
    );
  }
  if (turn.reply_state === 'pending') {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm text-tertiary">The Studio job is still running.</p>
        {turn.reply ? <IdentifierValue label="Unexpected stored reply" value={turn.reply} /> : null}
        {turn.error ? <IdentifierValue label="Unexpected reply error" value={turn.error} /> : null}
      </div>
    );
  }
  if (turn.reply_state === 'failed') {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-error-primary">Reply failed</span>
        <p className="text-sm whitespace-pre-wrap text-secondary">
          {turn.error || 'The Studio job failed without an error message.'}
        </p>
        {turn.reply ? <IdentifierValue label="Unexpected stored reply" value={turn.reply} /> : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-tertiary">Empty reply</span>
      <p className="text-sm text-tertiary">The Studio job succeeded but produced no text.</p>
      {turn.reply ? <IdentifierValue label="Unexpected stored reply" value={turn.reply} /> : null}
      {turn.error ? <IdentifierValue label="Unexpected reply error" value={turn.error} /> : null}
    </div>
  );
}

function ChatGarmentTile({
  id,
  garment,
}: {
  id: string;
  garment: AdminChatReferencedGarment | undefined;
}) {
  if (!garment) {
    return (
      <div className="flex size-24 shrink-0 items-center justify-center rounded-lg border border-secondary bg-secondary px-2 text-center text-xs text-tertiary">
        Item no longer available
      </div>
    );
  }
  const title = garment.title || 'Untitled garment';
  return (
    <div
      className="flex w-24 shrink-0 flex-col gap-1"
      title={[title, garment.category].filter(Boolean).join(' — ')}
    >
      <GarmentImage url={garment.image_url} refPresent={id} alt={title} className="size-24" />
      <span className="truncate text-xs font-medium text-secondary">{title}</span>
      {garment.category ? (
        <span className="truncate text-xs text-tertiary">{garment.category}</span>
      ) : null}
    </div>
  );
}

function ChatGarmentRow({
  ids,
  garments,
}: {
  ids: string[];
  garments: Map<string, AdminChatReferencedGarment>;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {ids.map((id, index) => (
        <ChatGarmentTile key={`${id}-${index}`} id={id} garment={garments.get(id)} />
      ))}
    </div>
  );
}

function ChatEmbedCards({ turn }: { turn: AdminMemberChatTurn }) {
  const unresolvedIds = new Set(turn.unresolved_ids ?? []);
  const garments = new Map(
    (turn.referenced_garments ?? [])
      .filter((garment) => !unresolvedIds.has(garment.id))
      .map((garment) => [garment.id, garment]),
  );
  const outfits = new Map(
    (turn.referenced_outfits ?? [])
      .filter((outfit) => !unresolvedIds.has(outfit.id))
      .map((outfit) => [outfit.id, outfit]),
  );
  const shownGarmentIds = new Set<string>();
  for (const embed of turn.embeds ?? []) {
    if (embed.kind === 'garment-list' || embed.kind === 'outfit') {
      embed.garmentIds.forEach((id) => shownGarmentIds.add(id));
    } else if (embed.kind === 'folder') {
      embed.garmentIds?.forEach((id) => shownGarmentIds.add(id));
    } else if (embed.kind === 'reminder') {
      shownGarmentIds.add(embed.garmentId);
    }
  }
  const inlineIds = (turn.inline_garment_ids ?? []).filter((id) => !shownGarmentIds.has(id));
  const folderActions = {
    listed: 'Listed',
    created: 'Created',
    reused: 'Reused',
    renamed: 'Renamed',
    updated: 'Added to',
    not_created: 'Not created',
  } as const;

  return (
    <div className="flex flex-col gap-2">
      {(turn.embeds ?? []).map((embed, index) => {
        if (embed.kind === 'garment-list') {
          return (
            <div
              key={`garment-list-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3"
            >
              {embed.title ? (
                <span className="text-xs font-semibold text-secondary">{embed.title}</span>
              ) : null}
              <ChatGarmentRow ids={embed.garmentIds} garments={garments} />
            </div>
          );
        }
        if (embed.kind === 'outfit') {
          const outfit =
            embed.action === 'set-for-today' && embed.outfitId
              ? outfits.get(embed.outfitId)
              : undefined;
          const label =
            embed.label ||
            (embed.action === 'set-for-today' ? "Set as today's outfit" : 'Suggested outfit');
          return (
            <div
              key={`outfit-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3"
            >
              <span className="text-xs font-semibold text-secondary">{label}</span>
              {outfit ? (
                <div className="flex items-start gap-2">
                  <div className="w-28 shrink-0">
                    <GarmentImage
                      url={outfit.render_image_url}
                      refPresent={outfit.id}
                      alt={outfit.display_title}
                      className="aspect-[4/5]"
                    />
                  </div>
                  <span className="text-sm font-medium text-secondary">{outfit.display_title}</span>
                </div>
              ) : (
                <OutfitSpread
                  className="max-w-[12rem]"
                  label={label}
                  garments={embed.garmentIds.map((id) => ({
                    id,
                    imageUrl: garments.get(id)?.image_url ?? null,
                    label: garments.get(id)?.title ?? null,
                  }))}
                />
              )}
            </div>
          );
        }
        if (embed.kind === 'folder') {
          const itemCount = embed.itemCount ?? embed.garmentIds?.length ?? embed.outfitIds?.length;
          return (
            <div
              key={`folder-${embed.folderId}-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3"
            >
              <span className="text-xs font-semibold text-secondary">{embed.name}</span>
              <span className="text-xs text-tertiary">
                {folderActions[embed.action]} ·{' '}
                {embed.scope === 'wardrobe' ? 'Wardrobe folder' : 'Looks folder'}
                {itemCount === undefined || itemCount === null
                  ? ''
                  : ` · ${formatCount(itemCount)} ${itemCount === 1 ? 'item' : 'items'}`}
              </span>
              {embed.garmentIds?.length ? (
                <ChatGarmentRow ids={embed.garmentIds} garments={garments} />
              ) : null}
            </div>
          );
        }
        if (embed.kind === 'reminder') {
          return (
            <div
              key={`reminder-${index}`}
              className="flex gap-3 rounded-lg border border-secondary bg-primary p-3"
            >
              <ChatGarmentTile id={embed.garmentId} garment={garments.get(embed.garmentId)} />
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-secondary">
                  Reminder for {when(embed.scheduledFor)}
                </span>
                {embed.reason ? (
                  <span className="text-xs text-tertiary">{embed.reason}</span>
                ) : null}
              </div>
            </div>
          );
        }
        if (embed.kind === 'try-on') {
          return (
            <div
              key={`try-on-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3"
            >
              <span className="text-xs font-semibold text-secondary">Try-on</span>
              <ChatGarmentRow ids={embed.garmentIds} garments={garments} />
            </div>
          );
        }
        if (embed.kind === 'wardrobe-gap') {
          return (
            <div
              key={`wardrobe-gap-${index}`}
              className="flex flex-col gap-1 rounded-lg border border-secondary bg-primary p-3"
            >
              <span className="text-xs font-semibold text-secondary">
                Wardrobe gap: {embed.category}
              </span>
              <span className="text-xs text-tertiary">{embed.reason}</span>
            </div>
          );
        }
        if (embed.kind === 'choice') {
          const questions = embed.questions.length
            ? embed.questions
            : embed.question
              ? [
                  {
                    questionId: 'fallback',
                    question: embed.question,
                    options: embed.options ?? [],
                  },
                ]
              : [];
          return (
            <div
              key={`choice-${embed.choiceId}-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3"
            >
              <span className="text-xs font-semibold text-secondary">Asked the member</span>
              {questions.map((question) => (
                <span key={question.questionId} className="text-xs text-tertiary">
                  {[
                    question.question,
                    ...question.options.map((option) => option.label),
                    ...('allowSkip' in question && question.allowSkip ? ['Skippable'] : []),
                  ].join(' · ')}
                </span>
              ))}
            </div>
          );
        }
        return null;
      })}
      {inlineIds.length ? (
        <div className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3">
          <span className="text-xs font-semibold text-secondary">Mentioned in the reply</span>
          <ChatGarmentRow ids={inlineIds} garments={garments} />
        </div>
      ) : null}
      {turn.unparsed_embed_count ? (
        <span className="text-xs text-tertiary">
          {formatCount(turn.unparsed_embed_count)} embedded{' '}
          {turn.unparsed_embed_count === 1 ? 'item' : 'items'} could not be displayed.
        </span>
      ) : null}
    </div>
  );
}

function toolName(value: string) {
  const spaced = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced ? `${spaced.charAt(0).toUpperCase()}${spaced.slice(1).toLowerCase()}` : 'Tool';
}

function jsonText(value: unknown) {
  if (typeof value === 'string') return value;
  if (value === undefined) return 'Not recorded';
  // JSON.stringify yields undefined only for values JSON cannot carry.
  return JSON.stringify(value, null, 2) ?? 'Not recorded';
}

function ChatJsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-secondary bg-secondary p-2 font-mono text-xs text-secondary">
      {jsonText(value)}
    </pre>
  );
}

function TruncatedNote({ bytes }: { bytes: number | null | undefined }) {
  return (
    <span className="text-xs text-tertiary">
      (truncated{typeof bytes === 'number' ? `, ${formatCount(bytes)} bytes` : ''})
    </span>
  );
}

function ChatToolCall({ call }: { call: AdminChatToolCall }) {
  const args = call.truncated && call.args_preview ? call.args_preview : call.args;
  const result = call.truncated && call.result_preview ? call.result_preview : call.result;
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-secondary bg-primary p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-secondary">{toolName(call.tool_name)}</span>
        <span className="text-xs text-tertiary">{formatCount(call.duration_ms)} ms</span>
      </div>
      <span className="text-xs font-medium text-tertiary">Arguments</span>
      <ChatJsonBlock value={args} />
      {call.truncated && call.args_preview ? <TruncatedNote bytes={call.args_bytes} /> : null}
      <details className="rounded-lg border border-secondary bg-secondary px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-secondary">
          Output
          {call.error ? (
            <span className="ml-2 text-error-primary">Failed: {call.error}</span>
          ) : null}
        </summary>
        <div className="mt-2 flex flex-col gap-1">
          <ChatJsonBlock value={result} />
          {call.truncated && call.result_preview ? (
            <TruncatedNote bytes={call.result_bytes} />
          ) : null}
        </div>
      </details>
    </li>
  );
}

function ChatTools({ toolCalls }: { toolCalls: AdminMemberChatTurn['tool_calls'] }) {
  // Absent (an API without the field) reads the same as null: nothing was recorded.
  if (toolCalls === null || toolCalls === undefined) {
    return (
      <span className="text-xs text-tertiary">Tool calls weren't recorded for this reply.</span>
    );
  }
  if (!toolCalls?.length) return null;
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold text-secondary">Tools used</span>
      <ol className="flex flex-col gap-2">
        {toolCalls.map((call, index) => (
          <ChatToolCall key={`${call.tool_name}-${index}`} call={call} />
        ))}
      </ol>
      {toolCalls.some((call) => call.tool_calls_truncated) ? (
        <span className="text-xs text-tertiary">Only the first 50 tool calls were recorded.</span>
      ) : null}
    </div>
  );
}

function ChatTurn({ turn }: { turn: AdminMemberChatTurn }) {
  return (
    <li className="flex max-w-3xl flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-tertiary">{when(turn.created_at)}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <Status value={turn.status} />
          <Status value={turn.reply_state} />
        </div>
      </div>
      <div className="flex flex-col gap-1 rounded-xl border border-secondary bg-secondary p-3">
        <span className="text-xs font-semibold text-secondary">Member</span>
        {turn.prompt_state === 'stored' ? (
          <p className="text-sm whitespace-pre-wrap text-secondary">{turn.prompt}</p>
        ) : (
          <p className="text-sm text-tertiary italic">
            Prompt not captured. The member&apos;s message was not saved for this turn.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1 rounded-xl border border-brand-secondary bg-brand-primary p-3">
        <span className="text-xs font-semibold text-secondary">Studio</span>
        <ChatReply turn={turn} />
        <ChatEmbedCards turn={turn} />
        <ChatTools toolCalls={turn.tool_calls} />
      </div>
      <IdentifierCopy label="Reply job reference" value={turn.job_id} />
    </li>
  );
}

function ChatPanel({ id }: { id: string }) {
  const [offset, setOffset] = useState(0);
  const source = useInsight<AdminMemberChatPage>(
    () =>
      memberChatApiV1InternalUsersIdChatGet({
        path: { id },
        query: { limit: PAGE, offset },
      }),
    [id, offset],
  );
  return (
    <Section
      title="Studio chat"
      description="The member's actual Studio conversation, newest first. This view is read-only; there is no way to reply from here."
    >
      <InsightState source={source} subject="this member's Studio chat">
        {(page) => {
          const turns = page.items;
          return (
            <div className="flex flex-col gap-3">
              {turns.length ? (
                <ol className="flex flex-col gap-3" aria-label="Studio conversation">
                  {turns.map((turn) => (
                    <ChatTurn key={turn.job_id} turn={turn} />
                  ))}
                </ol>
              ) : (
                <Absent title="This member has no chat messages.">
                  No Studio conversation turns are available to show on this page.
                </Absent>
              )}
              {page.total ? (
                <Pager
                  offset={page.offset}
                  limit={page.limit}
                  total={page.total}
                  onPage={setOffset}
                />
              ) : null}
            </div>
          );
        }}
      </InsightState>
    </Section>
  );
}

/* --------------------------------------------------------------- searches */

export function MemberSearchesPanel({ id }: { id: string }) {
  const [foundNothing, setFoundNothing] = useState(false);
  const [offset, setOffset] = useState(0);
  const source = useInsight<AdminMemberSearchPage>(
    () =>
      memberSearchesApiV1InternalUsersIdSearchesGet({
        path: { id },
        query: {
          limit: PAGE,
          offset,
          surface: SEARCH_SURFACE,
          ...(foundNothing ? { found_nothing: true } : {}),
        },
      }),
    [id, foundNothing, offset],
  );
  return (
    <Section
      title="Searches"
      description="The settled Wardrobe searches this member made, newest first. Replayed keystrokes are folded into the query they settled on; this panel is read-only."
    >
      <SearchHistoryFilters
        foundNothing={foundNothing}
        onFoundNothingChange={(next) => {
          setFoundNothing(next);
          setOffset(0);
        }}
      />
      <InsightState source={source} subject="this member's searches">
        {(page) =>
          page.items.length ? (
            <div className="flex flex-col gap-3">
              <SearchHistoryList searches={page.items} memberId={id} />
              <Pager
                offset={page.offset}
                limit={page.limit}
                total={page.total}
                onPage={setOffset}
              />
            </div>
          ) : (
            <Absent title="This member has no searches matching these filters.">
              The read succeeded and returned no settled searches to show.
            </Absent>
          )
        }
      </InsightState>
    </Section>
  );
}

/* ------------------------------------------------------------- daily looks */

/** The look laid flat, as the member's app draws it, with each piece named under it. */
function DailyLookGarmentList({ garments }: { garments: AdminMemberDailyLookGarment[] }) {
  if (!garments.length) return null;
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-xs font-medium text-tertiary">Garments in this look</h4>
      <OutfitSpread
        className="max-w-[14rem]"
        label="Daily look"
        garments={garments.map((garment) => ({
          id: garment.id,
          imageUrl: garment.image_url ?? null,
          label: garment.title?.trim() || words(garment.category),
        }))}
      />
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs">
        {garments.map((garment) => (
          <li key={garment.id} className="truncate text-secondary">
            {garment.title?.trim() || words(garment.category)}
            <span className="text-quaternary"> · {words(garment.category)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Garment ids that name nothing this member owns: there is nothing to link to. */
function UnresolvedGarmentIds({ ids }: { ids: readonly string[] }) {
  return (
    <span className="flex flex-col gap-0.5 pt-1">
      <span className="text-xs">Unresolved garment references</span>
      <span className="flex flex-wrap gap-1">
        {ids.map((garmentId) => (
          <CopyableId key={garmentId} value={garmentId} label="garment id" />
        ))}
      </span>
    </span>
  );
}

function DailyLookGarments({ look }: { look: AdminMemberDailyLook }) {
  if (look.garment_state === 'none_selected')
    return (
      <Callout tone="neutral" title="No garments selected">
        This slot was created with no garments selected.
      </Callout>
    );
  if (look.garment_state === 'unresolved')
    return (
      <Callout tone="warning" title="Selected garments unresolved">
        None of the {formatCount(look.unresolved_garment_ids.length)} selected garment{' '}
        {look.unresolved_garment_ids.length === 1 ? 'ID resolves' : 'IDs resolve'} to a garment
        owned by this member.
        {look.unresolved_garment_ids.length ? (
          <UnresolvedGarmentIds ids={look.unresolved_garment_ids} />
        ) : null}
      </Callout>
    );

  return (
    <div className="flex flex-col gap-2">
      {look.garment_state === 'partially_unresolved' ? (
        <Callout tone="warning" title="Some selected garments unresolved">
          {formatCount(look.unresolved_garment_ids.length)} selected garment{' '}
          {look.unresolved_garment_ids.length === 1 ? 'ID did' : 'IDs did'} not resolve to a garment
          owned by this member.
          {look.unresolved_garment_ids.length ? (
            <UnresolvedGarmentIds ids={look.unresolved_garment_ids} />
          ) : null}
        </Callout>
      ) : null}
      <DailyLookGarmentList garments={look.garments} />
    </div>
  );
}

function DailyLookRender({ look }: { look: DailyLookWithRenderJob }) {
  const renderJob = activeJob(look.render_job);
  const pendingLabel = ['queued', 'running', 'regenerating'].includes(look.render_status)
    ? `Render ${words(look.render_status)}`
    : 'Render pending';
  if (look.render_image_url)
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <div className="relative w-24 shrink-0" data-render-state={look.render_state}>
          <GarmentImage
            url={look.render_image_url}
            // The daily-look read exposes no raw render ref, so the object path of
            // the signed URL stands in for it: it changes exactly when the image
            // does and stays stable when only the signature is re-issued.
            refPresent={look.render_image_url.split('?')[0]}
            alt={`Daily look for ${look.for_date}`}
            className="aspect-[4/5]"
          />
          {look.render_state === 'ready' ? null : (
            <span className="absolute inset-x-1 bottom-1 flex justify-center rounded-md bg-primary/90 p-1">
              <Status value={look.render_state === 'pending' ? pendingLabel : look.render_state} />
            </span>
          )}
        </div>
        {renderJob ? <ActiveJobState job={renderJob} /> : null}
      </div>
    );
  const title =
    look.render_state === 'not_started'
      ? 'Render not started'
      : look.render_state === 'pending'
        ? pendingLabel
        : look.render_state === 'failed'
          ? 'Render failed'
          : 'Rendered image unavailable';
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        className={cx(
          'flex aspect-[4/5] w-24 shrink-0 items-center justify-center rounded-lg border px-2 text-center text-xs font-medium',
          look.render_state === 'failed' || look.render_state === 'ready'
            ? 'border-error-secondary bg-error-primary text-error-primary'
            : 'border-secondary bg-secondary text-tertiary',
        )}
        data-render-state={look.render_state}
      >
        {title}
      </div>
      {renderJob ? <ActiveJobState job={renderJob} /> : null}
    </div>
  );
}

function DailyLookCard({ look, memberId }: { look: DailyLookWithRenderJob; memberId: string }) {
  const reviewed = Boolean(
    look.delivery === 'stylist_reviewed' ||
    look.reviewed_by ||
    look.reviewed_at ||
    look.stylist_comment,
  );
  const lookIndex = look.look_index ?? 1;
  const retired = look.delivery === 'rejected' || look.delivery === 'unfilled';
  return (
    <li
      aria-label={`Daily look ${lookIndex} for ${look.for_date}`}
      data-look-index={lookIndex}
      className={cx(
        'flex min-w-0 flex-col gap-3 rounded-xl border border-secondary p-3',
        retired ? 'bg-secondary' : 'bg-primary',
      )}
    >
      <div className="flex min-w-0 gap-3">
        <DailyLookRender look={look} />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="text-sm font-semibold text-primary">
            {look.for_date} · Look {lookIndex}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            <Status value={words(look.delivery)} />
            <Status value={words(look.garment_state)} />
            <Status value={words(look.render_state)} />
            {look.render_state === 'pending' ? <Status value={words(look.render_status)} /> : null}
            <Status value={words(look.review_status)} />
          </div>
        </div>
      </div>
      {look.delivery === 'rejected' ? (
        <Callout tone="neutral" title="Rejected by the stylist">
          {look.rejected_at ? `Rejected ${when(look.rejected_at)}. ` : ''}
          {look.replaced_by_slot_id
            ? 'A replacement look was generated in its place.'
            : 'No replacement look is recorded yet.'}
          {look.replaced_by_slot_id ? (
            <span className="mt-1 block">
              <IdentifierCopy label="Replacement slot" value={look.replaced_by_slot_id} />
            </span>
          ) : null}
        </Callout>
      ) : null}
      {look.delivery === 'unfilled' ? (
        <Callout tone="warning" title="No look could be generated">
          {look.fill_error?.trim() || 'Generation stopped without a stored reason.'}
        </Callout>
      ) : null}
      {look.producing_job_id ? (
        <Actions>
          <WorkflowJobButton jobId={look.producing_job_id} source />
        </Actions>
      ) : null}
      {look.render_error ? <FailureText raw={look.render_error} /> : null}
      <details className="group border-t border-secondary pt-2">
        <summary className="cursor-pointer text-xs font-medium text-brand-secondary">
          Details
        </summary>
        <div className="mt-3 flex flex-col gap-3">
          {look.render_state === 'failed' && !look.render_error ? (
            <FailureText raw="The render failed without a stored reason." />
          ) : look.render_state === 'ready' && !look.render_image_url ? (
            <p className="text-xs text-error-primary">
              The slot is ready, but this read returned no render image URL.
            </p>
          ) : null}
          <DetailList
            items={[
              ['Look', `${formatCount(lookIndex)} of up to 3 for this date`],
              ['Delivery', words(look.delivery)],
              ['Generation attempts', formatCount(look.generation_attempts ?? 1)],
              ['Garment state', words(look.garment_state)],
              ['Review status', look.review_status],
              ['Render state', words(look.render_state)],
              ['Render status', look.render_status],
              ['Render attempts', formatCount(look.render_attempts)],
              ...(look.render_completed_at || ['ready', 'failed'].includes(look.render_state)
                ? ([['Render completed', when(look.render_completed_at, 'Not recorded')]] as Array<
                    [ReactNode, ReactNode]
                  >)
                : []),
              ['Location', look.location.trim() || 'Not recorded'],
              ['Created', when(look.created_at)],
              ...(reviewed
                ? ([
                    [
                      'Reviewed by',
                      looksLikeUuid(look.reviewed_by) ? (
                        <EntityRef kind="staff" id={look.reviewed_by} />
                      ) : (
                        (look.reviewed_by ?? 'Not recorded')
                      ),
                    ],
                    ['Reviewed at', when(look.reviewed_at)],
                    ['Stylist comment', look.stylist_comment?.trim() || 'No stylist comment'],
                  ] as Array<[ReactNode, ReactNode]>)
                : []),
            ]}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <IdentifierCopy label="Slot reference" value={look.slot_id} />
            {look.outfit_id ? (
              <span className="flex flex-col gap-0.5">
                <span className="text-xs text-tertiary">Outfit</span>
                <EntityRef kind="outfit" id={look.outfit_id} memberId={memberId} />
              </span>
            ) : (
              <IdentifierValue label="Outfit" value="None" />
            )}
          </div>
          <DailyLookGarments look={look} />
        </div>
      </details>
    </li>
  );
}

export function DailyLooksPanel({ id }: { id: string }) {
  const [offset, setOffset] = useState(0);
  const source = useInsight<AdminMemberDailyLookPage>(
    () =>
      memberDailyLooksApiV1InternalUsersIdDailyLooksGet({
        path: { id },
        query: { limit: PAGE, offset },
      }),
    [id, offset],
  );
  return (
    <Section
      title="Daily looks"
      description="The member's Outfit-of-the-Day looks, newest date first: up to three a day, in look order. A look appears as an image only after its render is ready. Stylist-reviewed, auto-shipped, rejected and unfilled looks are all included; a rejected look is listed beside the look that replaced it."
    >
      <InsightState source={source} subject="this member's daily looks">
        {(page, receivedAt) =>
          page.items.length ? (
            <SignatureExpiryBoundary
              signedAt={receivedAt}
              onRefresh={() => source.refresh({ preserve: true })}
            >
              <div className="flex flex-col gap-3">
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {[...page.items]
                    .sort(
                      (left, right) =>
                        right.for_date.localeCompare(left.for_date) ||
                        (left.look_index ?? 1) - (right.look_index ?? 1),
                    )
                    .map((look) => (
                      <DailyLookCard key={look.slot_id} look={look} memberId={id} />
                    ))}
                </ul>
                <Pager
                  offset={page.offset}
                  limit={page.limit}
                  total={page.total}
                  onPage={setOffset}
                />
              </div>
            </SignatureExpiryBoundary>
          ) : (
            <Absent title="This member has no daily looks.">
              This read returned no slots. It does not establish why none exist.
            </Absent>
          )
        }
      </InsightState>
    </Section>
  );
}

/* ------------------------------------------------------- enrichment failures */

function FailedGarmentReprocess({
  garment,
  onQueued,
}: {
  garment: AdminFailedGarment;
  onQueued: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [queued, setQueued] = useState<AdminOnBehalfReprocessResult>();

  return (
    <div className="flex flex-col gap-2">
      <Button
        size="sm"
        color="secondary"
        isDisabled={busy}
        onClick={() => {
          setFailure(undefined);
          setQueued(undefined);
          const key = crypto.randomUUID();
          void runWithKey(key);
        }}
      >
        {busy ? 'Queueing…' : 'Reprocess'}
      </Button>
      {failure ? <FailureText raw={failure} /> : null}
      {queued ? <span className="text-xs text-success-primary">Reprocessing queued.</span> : null}
    </div>
  );

  async function runWithKey(key: string) {
    setBusy(true);
    setFailure(undefined);
    const response = await reprocessGarment(
      garment.member_id,
      garment.garment_id,
      GARMENT_REASON.reprocess,
      key,
    );
    setBusy(false);
    if (response.response.status === 403) {
      setFailure('Your role cannot reprocess a member’s garment.');
      return;
    }
    if (response.error || !response.data) {
      setFailure(errorDetail(response.error, 'That garment could not be queued for reprocessing.'));
      return;
    }
    setQueued(response.data);
    onQueued();
  }
}

export function EnrichmentFailures() {
  const [offset, setOffset] = useState(0);
  const source = useInsight<AdminFailedGarmentPage>(
    () => failedGarmentsApiV1InternalGarmentsFailedGet({ query: { limit: PAGE, offset } }),
    [offset],
  );
  return (
    <Page
      title="Enrichment failures"
      description="Garments whose ingestion or enhancement state is failed, newest first. Open the member to inspect the wardrobe, or queue the same reprocess action available on that member’s garment."
    >
      <Section
        title="Failed garments"
        description="A garment appears once even when both processing and the clean photo failed."
      >
        <InsightState source={source} subject="failed garments across the fleet">
          {(page) =>
            page.items.length ? (
              <>
                <RecordTable
                  noun="failed garments"
                  records={page.items}
                  columns={[
                    'garment_id',
                    'member_id',
                    'status',
                    'enhancement_status',
                    'failure_reason',
                    'enhancement_attempts',
                    'pipeline_version',
                    'created_at',
                  ]}
                  columnLabel={(column) =>
                    column === 'failure_reason' ? 'Why it failed' : undefined
                  }
                  renderCell={(column, record) =>
                    column === 'failure_reason' ? (
                      typeof record['failure_reason'] === 'string' &&
                      record['failure_reason'].trim() ? (
                        <FailureText raw={record['failure_reason']} />
                      ) : (
                        <span className="text-tertiary">No reason recorded</span>
                      )
                    ) : undefined
                  }
                  searchable={false}
                  rowActions={(record) => (
                    <FailedGarmentReprocess
                      garment={record as AdminFailedGarment}
                      onQueued={() => void source.refresh()}
                    />
                  )}
                />
                <Pager
                  offset={page.offset}
                  limit={page.limit}
                  total={page.total}
                  onPage={setOffset}
                />
              </>
            ) : (
              <Absent title="There are no failed garments.">
                Neither the ingestion status nor the enhancement status is failed anywhere in the
                wardrobe fleet.
              </Absent>
            )
          }
        </InsightState>
      </Section>
    </Page>
  );
}

/* ---------------------------------------------------------------------- tabs */

export function MemberInsights({
  id,
  tab,
  onTabChange,
  overview,
  memberName,
  member,
}: {
  id: string;
  /** The member record, for the consents they currently hold. */
  member?: ConsumerDetail | undefined;
  tab: InsightTabId;
  onTabChange: (next: InsightTabId) => void;
  /** The member's name, which confirming a delete-all may ask to be typed. */
  memberName?: string | null;
  /** The member record itself, already read by the route for the page title. */
  overview: ReactNode;
}) {
  return (
    <Tabs
      className="gap-5"
      selectedKey={tab}
      onSelectionChange={(key) => onTabChange(resolveInsightTab(String(key)))}
    >
      <Tabs.List type="underline" size="md" className="border-b border-secondary">
        {INSIGHT_TABS.map((item) => (
          <Tabs.Item key={item.id} id={item.id} label={item.label} />
        ))}
      </Tabs.List>
      <Tabs.Panel id="overview">{overview}</Tabs.Panel>
      <Tabs.Panel id="onboarding">
        <OnboardingPanel id={id} member={member} />
      </Tabs.Panel>
      <Tabs.Panel id="wardrobe">
        <WardrobePanel id={id} memberName={memberName} />
      </Tabs.Panel>
      <Tabs.Panel id="drive-import">
        <DriveImportPanel id={id} />
      </Tabs.Panel>
      <Tabs.Panel id="outfits">
        <OutfitsPanel id={id} memberName={memberName} />
      </Tabs.Panel>
      <Tabs.Panel id="folders">
        <FoldersPanel id={id} />
      </Tabs.Panel>
      <Tabs.Panel id="chat">
        <ChatPanel id={id} />
      </Tabs.Panel>
      <Tabs.Panel id="searches">
        <MemberSearchesPanel id={id} />
      </Tabs.Panel>
      <Tabs.Panel id="daily-looks">
        <DailyLooksPanel id={id} />
      </Tabs.Panel>
      <Tabs.Panel id="workflow-runs">
        <MemberWorkflowRunsPanel id={id} />
      </Tabs.Panel>
    </Tabs>
  );
}
